import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import isVideoFile from '../../utils/isVideoFile';
import getAddressFromCoordinates from '../../utils/getAddressFromCoordinates';
import getCoordinatesFromAddress from '../../utils/getCoordinatesFromAddress';
import getDistanceFromLatLonInMeters from '../../utils/getDistanceFromLatLonInMeters';
import { createMediaUploader } from '../../utils/mediaUpload';
import axiosConn from '../../axiosConn';
import useMobileView from '../../hooks/useMobileView';
import { formatCoarseLocationLabel } from '../../utils/localOpsLocation';
import { normalizeMediaList } from '../../utils/postMediaUtils';
import { resolveLocalOpsPlaceCandidates } from '../../utils/localOpsPlaceResolver';
import {
  COLLISION_SPECIALIST_RIDE_TEMPLATE,
  getCollisionSpecialistRideSuggestedPrice,
} from '../../utils/localOpsCollisionSpecialistRide';
import {
  getLocalOpsConfig,
  formatPercent,
  formatUsdJgkPairFromJgk,
} from '../../utils/localOpsConfig';
import {
  LOCALOPS_TASK_PAYMENT_MODES,
  canCreateCommunityTask,
  normalizeLocalOpsRegionSlug,
} from '../../utils/localOpsCommunityTasks';
import {
  LOCALOPS_FUNNEL_EVENTS,
  recordLocalOpsCreateSheetJourneyEvent,
  recordLocalOpsFunnelEvent,
} from '../../utils/localOpsFunnelTelemetry';
import LocalOpsSheet from './LocalOpsSheet';
import LocalOpsMediaPicker from './LocalOpsMediaPicker';
import LocalOpsPayWithBalance from './LocalOpsPayWithBalance';
import LocalOpsCompletionFlowHint from './LocalOpsCompletionFlowHint';
import { buildRideHelpStarterTaskPreset } from './LocalOpsStarterTaskIdeas';

const TYPE_OPTIONS = [
  {
    id: 'offering_product',
    label: 'Sell a local item',
    mode: 'offering',
    offeringType: 'product',
    group: 'offerings',
    description: 'Eggs, produce, firewood, local goods.',
    tooltip:
      'Great for local goods ready now: eggs, produce, firewood, handmade items.',
    Icon: LocalMallOutlinedIcon,
  },
  {
    id: 'offering_service',
    label: 'Offer a local service',
    mode: 'offering',
    offeringType: 'service',
    group: 'offerings',
    description: 'Handyman, lawn care, lessons, services.',
    tooltip:
      'Great for services you do well: handyman, lawn care, lessons, services.',
    Icon: DesignServicesOutlinedIcon,
  },
  {
    id: 'task_delivery',
    label: 'Delivery task',
    mode: 'task',
    taskType: 'delivery',
    group: 'tasks',
    description: 'Get an item delivered by a nearby helper.',
    tooltip: 'Ask a nearby helper to deliver an item for you.',
    Icon: LocalShippingOutlinedIcon,
  },
  {
    id: 'task_errand',
    label: 'Errand task',
    mode: 'task',
    taskType: 'errand',
    group: 'tasks',
    description: 'Ask someone local to run a quick task.',
    tooltip: 'Request a quick local errand or favor.',
    Icon: TaskAltOutlinedIcon,
  },
  {
    id: 'task_home_help',
    label: 'Home help task',
    mode: 'task',
    taskType: 'home_help',
    group: 'tasks',
    description: 'Cleaning, lifting, yard, setup, or home help.',
    tooltip:
      'Request paid help around the home, yard, setup, cleanup, lifting, or small local chores.',
    Icon: TaskAltOutlinedIcon,
  },
  {
    id: 'task_assistance',
    label: 'Ride / transport help',
    mode: 'task',
    taskType: 'assisted_transport',
    group: 'tasks',
    description: 'Request trusted help with a ride (tracked for safety).',
    tooltip: 'Trusted transport help with live tracking for safety.',
    Icon: AltRouteOutlinedIcon,
  },
];

const INFO_COPY =
  'See what locals can do and what locals need right now. Post what you offer or request help nearby.';
const GENERIC_SUPPLY_PROOF = {
  eligibleNearbyCopy: 'Nearby eligible helpers can be notified after funding.',
  verifiedLocalPriorityCopy: 'Verified Local helpers get priority.',
};

const getCreateJourneyStepName = (step) => {
  if (step <= 0) return 'type';
  if (step === 1) return 'details';
  if (step === 2) return 'review';
  return 'details';
};

const normalizeCreateJourneyFieldName = (field) => {
  const raw = String(field || '').trim();
  if (raw === 'serviceArea') return 'service_area';
  if (raw === 'deliveryAddress') return 'dropoff';
  return raw
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
};

const buildCreateJourneyBlockerSignature = ({
  currentStep,
  firstBlockingField,
  reasonCodes,
} = {}) =>
  [
    currentStep || 'unknown',
    firstBlockingField || 'unknown',
    (reasonCodes || []).slice().sort().join('+') || 'unknown',
  ].join('|');

const STEP_TWO_GUIDANCE = {
  ride_help: {
    title: 'Ride Help task',
    copy:
      'Add pickup, destination, timing, and amount. Nearby Ride Ready helpers can review and accept when available.',
    items: ['Pickup', 'Confirmed destination', 'Timing optional', 'Amount'],
    chips: ['Pickup', 'Destination', 'Time', 'Rider details', 'Amount'],
  },
  food_pickup: {
    title: 'Food pickup task',
    copy: 'Make the order easy for a nearby helper to pick up.',
    items: ['Restaurant', 'Order name', 'Pickup time', 'Drop-off notes'],
    chips: ['Restaurant', 'Order name', 'Pickup time', 'Drop-off notes'],
  },
  grocery_pickup: {
    title: 'Grocery pickup task',
    copy: 'Add the store/order details and any substitution notes.',
    items: ['Store', 'Order name', 'Substitutions', 'Drop-off notes'],
    chips: ['Store', 'Order name', 'Substitutions', 'Drop-off notes'],
  },
  small_errand: {
    title: 'Small errand task',
    copy: 'Describe the local action and how completion should be proven.',
    items: ['What needs done', 'Where', 'Best time', 'Proof needed'],
    chips: ['What needs done', 'Where', 'Best time', 'Proof needed'],
  },
  yard_help: {
    title: 'Yard help task',
    copy: 'Set clear expectations for outdoor help.',
    items: ['What needs done', 'Tools/materials', 'Best time', 'Photo helpful'],
    chips: ['What needs done', 'Tools', 'Best time', 'Photo'],
  },
  cleaning_help: {
    title: 'Cleaning task',
    copy: 'Tell helpers what area, supplies, and timing to expect.',
    items: ['Area to clean', 'Estimated time', 'Supplies provided', 'Best time'],
    chips: ['Area', 'Supplies', 'Estimated time', 'Best time'],
  },
  home_help: {
    title: 'Home Help task',
    copy: 'Add the job, access notes, and any safety details.',
    items: ['What needs done', 'Tools/materials', 'Best time', 'Safety/access notes'],
    chips: ['Job', 'Tools', 'Access notes', 'Safety notes'],
  },
  posting_help: {
    title: 'Posting help task',
    copy: 'Be specific about what should be posted and what proof you need.',
    items: ['What needs posted', 'Where to post', 'Screenshot/proof', 'Completion proof'],
    chips: ['What to post', 'Where to post', 'Screenshot/proof', 'Completion proof'],
  },
  task_delivery: {
    title: 'Delivery task',
    copy: 'Add pickup and delivery details so a nearby helper can move the item.',
    items: ['Pickup', 'Delivery notes', 'Timing', 'Amount'],
    chips: ['Pickup', 'Delivery notes', 'Timing', 'Amount'],
  },
  task_errand: {
    title: 'Errand task',
    copy: 'Add what needs done, where, and how the helper should confirm it.',
    items: ['What needs done', 'Where', 'Best time', 'Proof needed'],
    chips: ['What needs done', 'Where', 'Best time', 'Proof needed'],
  },
  task_home_help: {
    title: 'Home help task',
    copy: 'Make the home task concrete and easy to accept.',
    items: ['What needs done', 'Tools/materials', 'Best time', 'Access notes'],
    chips: ['Job', 'Tools', 'Best time', 'Access notes'],
  },
  task_assistance: {
    title: 'Ride / transport help task',
    copy: 'Add private pickup and destination details for trusted ride help.',
    items: ['Pickup', 'Destination', 'Trusted player', 'Amount'],
    chips: ['Pickup', 'Destination', 'Trusted player', 'Amount'],
  },
  offering_product: {
    title: 'Product offering',
    copy: 'List what is available, what it costs, and how buyers receive it.',
    items: ['What is offered', 'Price', 'Quantity', 'Photos', 'Fulfillment'],
    chips: ['What is offered', 'Price', 'Quantity', 'Photos', 'Fulfillment'],
  },
  offering_service: {
    title: 'Service offering',
    copy: 'Show what service is available, where it is available, and what is included.',
    items: ['Service', 'Service area', 'Starting price', 'Availability', "What's included"],
    chips: ['Service', 'Service area', 'Starting price', 'Availability', "What's included"],
  },
};

const makeCoreTaskFlowBlueprint = (id, config) =>
  Object.freeze({
    id,
    taskFlow: true,
    titleMode: 'guided',
    categoryMode: 'system',
    descriptionMode: 'generated_with_notes',
    priceMode: 'manual',
    paymentCopyMode: 'jgk_balance',
    visibilityDefault: 'nearby',
    trustedVisibilityMode: 'primary',
    sectionOrder: [
      'primary',
      'location',
      'timing',
      'amount',
      'notes',
      'media',
      'preview',
      'advanced',
    ],
    stepTwoGuidance: STEP_TWO_GUIDANCE[id],
    previewHeading: 'Public preview (exact address hidden)',
    previewHelper:
      'Exact task addresses are shared only after a helper accepts.',
    previewLocation: 'Exact task address shared after accept',
    privateDetailsCopy:
      'Exact task address is shared after a helper accepts.',
    mediaMode: 'advanced',
    mediaHelper: 'Optional. Add media only if it helps explain the task.',
    ...config,
  });

const makeOfferingFlowBlueprint = (id, config) =>
  Object.freeze({
    id,
    offeringFlow: true,
    titleMode: 'editable',
    categoryMode: 'editable',
    descriptionMode: 'manual',
    priceMode: 'manual',
    paymentCopyMode: 'offering_reservation',
    visibilityDefault: 'nearby',
    trustedVisibilityMode: 'primary',
    sectionOrder: [
      'primary',
      'price',
      'availability',
      'fulfillment',
      'details',
      'media',
      'preview',
      'advanced',
    ],
    stepTwoGuidance: STEP_TWO_GUIDANCE[id],
    previewHeading: 'Public preview (buyer view)',
    previewHelper:
      'Exact pickup, address, and fulfillment details are shared after a buyer reserves.',
    previewLocation: 'Pickup/fulfillment details shared after reservation',
    mediaMode: 'inline',
    mediaHelper: 'Optional, but recommended for offerings.',
    ...config,
  });

const CREATE_FLOW_BLUEPRINTS = Object.freeze({
  ride_help: Object.freeze({
    id: 'ride_help',
    label: 'Ride Help',
    systemTitle: 'Local ride help',
    category: 'Transport',
    sectionOrder: [
      'pickup',
      'destination',
      'timing',
      'amount',
      'notes',
      'media',
      'preview',
      'advanced',
    ],
    titleMode: 'system',
    categoryMode: 'hidden',
    descriptionMode: 'generated_with_notes',
    priceMode: 'route_suggested',
    paymentCopyMode: 'task_credit',
    visibilityDefault: 'nearby',
    trustedVisibilityMode: 'advanced',
    stepTwoGuidance: STEP_TWO_GUIDANCE.ride_help,
  }),
  offering_product: makeOfferingFlowBlueprint('offering_product', {
    label: 'Product offering',
    headerTitle: 'Create Product Offering',
    systemTitle: 'Local product offering',
    primaryHeading: 'What are you offering?',
    titleLabel: 'What are you offering?',
    titlePlaceholder: 'Fresh farm eggs, used table saw, homemade dessert box...',
    titleHelper:
      'Name the item people can reserve or buy locally. Details can stay optional.',
    categoryLabel: 'Category (required)',
    categoryHelper: 'Keeps the offering discoverable for nearby buyers.',
    priceHeading: 'Price',
    priceLabel: 'Price ($)',
    priceHelper: 'Set the price buyers see before they reserve.',
    quantityLabel: 'Quantity available',
    quantityHelper: 'Set how many units you can fulfill right now.',
    mediaHelper:
      'Photos help buyers decide. Add a photo so people know what they are reserving.',
    availabilityHeading: 'Availability',
    availabilityHelper:
      'Defaults to available now and stays ongoing. Change only if timing matters.',
    availabilityStartLabel: 'Available starting (optional)',
    availabilityEndLabel: 'Keep offering available',
    fulfillmentHeading: 'Pickup / fulfillment',
    pickupLabel: 'Pickup or fulfillment location (private, required)',
    pickupPlaceholder: 'Street, city, state',
    pickupHelper:
      'Where is this available? Type an address or place, then select a local match. Exact details are shared after reservation.',
    deliveryHeading: 'Delivery',
    deliveryLabel: 'Delivery supported',
    deliveryHelper:
      'Buyers can request delivery or coordinate with a local helper. You can turn this off.',
    detailsHeading: 'Details for buyers (optional)',
    detailsLabel: 'What should buyers know? (optional)',
    detailsPlaceholder:
      'Condition, size, pickup instructions, what is included...',
    sectionOrder: [
      'primary',
      'price',
      'media',
      'availability',
      'fulfillment',
      'details',
      'preview',
      'advanced',
    ],
  }),
  offering_service: makeOfferingFlowBlueprint('offering_service', {
    label: 'Service offering',
    headerTitle: 'Create Service Offering',
    systemTitle: 'Local service offering',
    primaryHeading: 'What service do you offer?',
    titleLabel: 'What service do you offer?',
    titlePlaceholder: 'Lawn mowing, handyman help, lessons, cleanup...',
    titleHelper:
      'Name the service people can reserve or request locally.',
    categoryLabel: 'Category (required)',
    categoryHelper: 'Keeps the service discoverable for nearby buyers.',
    priceHeading: 'Starting price',
    priceLabel: 'Starting price ($)',
    priceHelper:
      'Use the starting price buyers should expect before they reserve.',
    quantityLabel: 'Open spots available',
    quantityHelper: 'Set how many reservations you can handle right now.',
    mediaHelper:
      'Optional. Photos can help buyers understand your work or setup.',
    availabilityHeading: 'Availability',
    availabilityHelper:
      'Defaults to available now and stays ongoing. Change only if timing matters.',
    availabilityStartLabel: 'Available starting (optional)',
    availabilityEndLabel: 'Keep service available',
    fulfillmentHeading: 'Where is this available?',
    pickupLabel: 'Service area or meeting location (private, required)',
    pickupPlaceholder: 'Address, neighborhood, or meeting place',
    pickupHelper:
      'Where is this service available? Type an address or place, then select a local match. Exact details are shared after reservation.',
    deliveryHeading: 'Fulfillment',
    deliveryLabel: 'Local helper coordination supported',
    deliveryHelper:
      'Enable only if buyers may need a separate local helper to coordinate fulfillment.',
    detailsHeading: 'What is included? (optional)',
    detailsLabel: 'What is included? (optional)',
    detailsPlaceholder:
      'What buyers get, limits, materials, prep, timing, or terms...',
    previewLocation: 'Service area details shared after reservation',
    sectionOrder: [
      'primary',
      'fulfillment',
      'price',
      'availability',
      'details',
      'media',
      'preview',
      'advanced',
    ],
  }),
  food_pickup: makeCoreTaskFlowBlueprint('food_pickup', {
    label: 'Food pickup',
    headerTitle: 'Create Food Pickup Task',
    systemTitle: 'Pick up food order',
    category: 'Errands',
    primaryHeading: 'Food pickup',
    primaryFieldMode: 'pickup_resolver',
    locationMode: 'primary_resolver',
    pickupLabel: 'Restaurant or pickup place',
    pickupPlaceholder: 'Restaurant name or address',
    pickupHelper:
      'Type the restaurant name or address, then select the local match. Full address is shared only after a helper accepts.',
    pinEmptyLabel: 'Set the pickup place',
    dropoffAddressMode: 'deliveryAddress',
    dropoffHeading: 'Drop-off',
    dropoffLabel: 'Drop-off address (private)',
    dropoffPlaceholder: 'Street, city, state',
    dropoffHelper:
      'Where should the food be delivered? Shared only after a helper accepts.',
    timingStartLabel: 'Pickup time (optional)',
    priceMode: 'route_suggested',
    amountHelper:
      'Simple food pickups often start around $5-$20. Offer more for distance, waiting, or urgency.',
    notesHeading: 'Order notes (optional)',
    notesLabel: 'Order name, pickup window, or drop-off notes (optional)',
    notesPlaceholder:
      'Order name, pickup time, substitutions, porch or handoff notes...',
    mediaHelper: 'Optional. Most food pickups do not need photos.',
    previewLocation: 'Exact pickup place shared after accept',
    privateDetailsCopy: 'Exact pickup place is shared after a helper accepts.',
  }),
  grocery_pickup: makeCoreTaskFlowBlueprint('grocery_pickup', {
    label: 'Grocery pickup',
    headerTitle: 'Create Grocery Pickup Task',
    systemTitle: 'Pick up groceries',
    category: 'Errands',
    primaryHeading: 'Grocery pickup',
    primaryFieldMode: 'pickup_resolver',
    locationMode: 'primary_resolver',
    pickupLabel: 'Store or pickup place',
    pickupPlaceholder: 'Store name or address',
    pickupHelper:
      'Type the store name or address, then select the local match. Full address is shared only after a helper accepts.',
    pinEmptyLabel: 'Set the pickup place',
    dropoffAddressMode: 'deliveryAddress',
    dropoffHeading: 'Drop-off',
    dropoffLabel: 'Drop-off address (private)',
    dropoffPlaceholder: 'Street, city, state',
    dropoffHelper:
      'Where should groceries be delivered? Shared only after a helper accepts.',
    timingStartLabel: 'Pickup or drop-off time (optional)',
    priceMode: 'route_suggested',
    amountHelper:
      'Grocery pickups often need more for distance, load size, waiting, or substitutions.',
    notesHeading: 'Shopping notes (optional)',
    notesLabel: 'Order name, substitutions, or drop-off notes (optional)',
    notesPlaceholder:
      'Order name, substitution rules, cold items, drop-off or handoff notes...',
    mediaHelper: 'Optional. Most grocery pickups do not need photos.',
    previewLocation: 'Exact drop-off shared after accept',
    privateDetailsCopy: 'Exact drop-off address is shared after a helper accepts.',
  }),
  small_errand: makeCoreTaskFlowBlueprint('small_errand', {
    label: 'Small errand',
    headerTitle: 'Create Small Errand Task',
    systemTitle: 'Help with a small errand',
    category: 'Errands',
    primaryHeading: 'What do you need done?',
    titleLabel: 'Errand',
    titlePlaceholder: 'Pick up, drop off, return, call, or check on something',
    titleHelper: 'Keep it short. Add proof or handoff details later.',
    locationHeading: 'Where / access',
    pickupLabel: 'Task address or meeting point (private, required)',
    pickupHelper:
      'Where should the helper go? Exact address is shared only after a helper accepts.',
    timingStartLabel: 'Best time (optional)',
    amountHelper:
      'Small errands often start around $5-$20. Offer more for distance, waiting, or urgency.',
    notesHeading: 'Helper notes (optional)',
    notesLabel: 'Instructions, proof, or handoff notes (optional)',
    notesPlaceholder:
      'What to bring, where to go, proof needed, contact or handoff notes...',
  }),
  yard_help: makeCoreTaskFlowBlueprint('yard_help', {
    label: 'Yard help',
    headerTitle: 'Create Yard Help Task',
    systemTitle: 'Help with yard work',
    category: 'Home',
    primaryHeading: 'Yard work',
    titleLabel: 'What needs done?',
    titlePlaceholder: 'Mow, rake, haul branches, trim, clean up...',
    titleHelper: 'Name the outdoor task. Photos help helpers judge the work.',
    locationHeading: 'Where / access',
    pickupLabel: 'Yard address (private, required)',
    pickupHelper:
      'Where should the helper go? Exact address is shared only after a helper accepts.',
    timingStartLabel: 'Best time (optional)',
    amountHelper:
      'Yard help varies by effort. Offer more for heavy work, tools, hauling, heat, or urgency.',
    notesHeading: 'Yard notes (optional)',
    notesLabel: 'Tools, materials, access, or safety notes (optional)',
    notesPlaceholder:
      'Gate code, pets, tools available, bags, hauling, uneven ground...',
    mediaMode: 'inline',
    mediaHelper:
      'Optional, but photos are helpful for yard size, brush piles, or cleanup areas.',
    privateDetailsCopy: 'Exact yard address is shared after a helper accepts.',
  }),
  cleaning_help: makeCoreTaskFlowBlueprint('cleaning_help', {
    label: 'Cleaning',
    headerTitle: 'Create Cleaning Task',
    systemTitle: 'Help with cleaning',
    category: 'Home',
    primaryHeading: 'Cleaning help',
    titleLabel: 'Area or cleaning task',
    titlePlaceholder: 'Kitchen, garage, move-out clean, floors...',
    titleHelper: 'Name the area and kind of cleaning needed.',
    locationHeading: 'Where / access',
    pickupLabel: 'Cleaning address (private, required)',
    pickupHelper:
      'Where should the helper go? Exact address is shared only after a helper accepts.',
    timingStartLabel: 'Best time (optional)',
    amountHelper:
      'Cleaning pay depends on time, supplies, rooms, and effort. Offer more for bigger jobs.',
    notesHeading: 'Cleaning notes (optional)',
    notesLabel: 'Supplies, room count, access, or expectations (optional)',
    notesPlaceholder:
      'Supplies provided, estimated time, rooms, pets, parking, access...',
    mediaMode: 'inline',
    mediaHelper:
      'Optional. Add photos only if they clarify the space or scope.',
    privateDetailsCopy: 'Exact cleaning address is shared after a helper accepts.',
  }),
  home_help: makeCoreTaskFlowBlueprint('home_help', {
    label: 'Home Help',
    headerTitle: 'Create Home Help Task',
    systemTitle: 'Help with a small home task',
    category: 'Home',
    primaryHeading: 'Home task',
    titleLabel: 'What needs help?',
    titlePlaceholder: 'Move a box, assemble shelf, fix a latch...',
    titleHelper: 'Name the job so helpers can quickly judge fit.',
    locationHeading: 'Where / access',
    pickupLabel: 'Home address (private, required)',
    pickupHelper:
      'Where should the helper go? Exact address is shared only after a helper accepts.',
    timingStartLabel: 'Best time (optional)',
    amountHelper:
      'Home help varies by time, tools, weight, and complexity. Offer more for harder jobs.',
    notesHeading: 'Home task notes (optional)',
    notesLabel: 'Tools, materials, access, or safety notes (optional)',
    notesPlaceholder:
      'Tools needed, materials ready, stairs, parking, pets, access notes...',
    mediaMode: 'inline',
    mediaHelper:
      'Optional, but photos can help show the item, area, or repair.',
    privateDetailsCopy: 'Exact home address is shared after a helper accepts.',
  }),
  posting_help: makeCoreTaskFlowBlueprint('posting_help', {
    label: 'Posting help',
    headerTitle: 'Create Posting Task',
    systemTitle: 'Help with a local posting task',
    category: 'Errands',
    primaryHeading: 'Posting task',
    titleLabel: 'What needs posted or shared?',
    titlePlaceholder: 'Post flyer, list item, share announcement...',
    titleHelper: 'Name the posting job. Add proof details later.',
    locationHeading: 'Where / access',
    pickupLabel: 'Task area or meeting point (private, required)',
    pickupHelper:
      'Use the local area, meeting point, or pickup spot for materials. Exact address is shared only after a helper accepts.',
    timingStartLabel: 'Best time (optional)',
    amountHelper:
      'Posting tasks often start around $5-$20. Offer more for multiple places or proof steps.',
    notesHeading: 'Posting notes (optional)',
    notesLabel: 'Where to post, wording, materials, or proof (optional)',
    notesPlaceholder:
      'Where it should be posted, wording, screenshots, photos, completion proof...',
    mediaHelper:
      'Optional. Add media only if the helper needs the flyer, screenshot, or reference.',
  }),
  task_delivery: makeCoreTaskFlowBlueprint('task_delivery', {
    label: 'Delivery task',
    systemTitle: 'Local delivery task',
    category: 'Delivery',
    primaryHeading: 'Delivery',
    titleLabel: 'What is moving?',
    titlePlaceholder: 'Package, food, documents, item...',
    titleHelper: 'Name the item and keep private details for notes.',
    locationHeading: 'Pickup and drop-off',
    pickupLabel: 'Pickup address (private, required)',
    pickupHelper:
      'Where should the helper pick up the item? Shared only after a helper accepts.',
    deliveryLabel: 'Delivery/drop-off address (private, required)',
    deliveryHelper:
      'Where should the helper deliver it? Shared only after a helper accepts.',
    pinEmptyLabel: 'Set the pickup point',
    timingStartLabel: 'Pickup time (optional)',
    priceMode: 'route_suggested',
    amountHelper:
      'Delivery pay should cover distance, waiting, item size, urgency, and handoff effort.',
    notesHeading: 'Delivery notes (optional)',
    notesLabel: 'Size, handoff, fragile items, or delivery notes (optional)',
    notesPlaceholder:
      'Item size, pickup contact, fragile items, drop-off instructions, proof needed...',
    mediaHelper:
      'Optional. Add media only if it helps identify the item or handoff.',
    previewLocation: 'Exact pickup/drop-off shared after accept',
    privateDetailsCopy:
      'Exact pickup and drop-off addresses are shared after a helper accepts.',
  }),
  task_errand: makeCoreTaskFlowBlueprint('task_errand', {
    label: 'Errand task',
    systemTitle: 'Local errand task',
    category: 'Errands',
    primaryHeading: 'What do you need done?',
    titleLabel: 'Errand',
    titlePlaceholder: 'Pick up, drop off, check, return, or handle something',
    titleHelper: 'Keep it short. Add details and proof expectations later.',
    locationHeading: 'Where / access',
    pickupLabel: 'Task address or meeting point (private, required)',
    pickupHelper:
      'Where should the helper go? Exact address is shared only after a helper accepts.',
    timingStartLabel: 'Best time (optional)',
    amountHelper:
      'Errands often start around $5-$20. Offer more for distance, waiting, or urgency.',
    notesHeading: 'Helper notes (optional)',
    notesLabel: 'Instructions, proof, or handoff notes (optional)',
    notesPlaceholder:
      'What to do, where to go, proof needed, contact or handoff notes...',
  }),
  task_home_help: makeCoreTaskFlowBlueprint('task_home_help', {
    label: 'Home help task',
    systemTitle: 'Local home help task',
    category: 'Home',
    primaryHeading: 'Home task',
    titleLabel: 'What needs help?',
    titlePlaceholder: 'Move, clean, assemble, carry, fix, organize...',
    titleHelper: 'Name the job so helpers can quickly judge fit.',
    locationHeading: 'Where / access',
    pickupLabel: 'Task address (private, required)',
    pickupHelper:
      'Where should the helper go? Exact address is shared only after a helper accepts.',
    timingStartLabel: 'Best time (optional)',
    amountHelper:
      'Home help varies by time, tools, weight, and complexity. Offer more for harder jobs.',
    notesHeading: 'Home task notes (optional)',
    notesLabel: 'Tools, materials, access, or safety notes (optional)',
    notesPlaceholder:
      'Tools needed, materials ready, stairs, parking, pets, access notes...',
    mediaMode: 'inline',
    mediaHelper:
      'Optional, but photos can help show the item, area, or repair.',
    privateDetailsCopy: 'Exact task address is shared after a helper accepts.',
  }),
});

const DEFAULT_CREATE_FLOW_BLUEPRINT = Object.freeze({
  id: 'default',
  label: 'Default LocalOps',
  sectionOrder: [
    'details',
    'location',
    'taskMode',
    'amount',
    'media',
    'preview',
    'advanced',
  ],
  titleMode: 'editable',
  categoryMode: 'editable',
  descriptionMode: 'manual',
  priceMode: 'manual',
  paymentCopyMode: 'jgk_balance',
  visibilityDefault: 'nearby',
  trustedVisibilityMode: 'primary',
});

const resolveCreateFlowBlueprint = ({ preset, selectedMeta }) => {
  const starterId = String(preset?.starterId || '').trim();
  if (starterId && CREATE_FLOW_BLUEPRINTS[starterId]) {
    return CREATE_FLOW_BLUEPRINTS[starterId];
  }
  if (selectedMeta?.id && CREATE_FLOW_BLUEPRINTS[selectedMeta.id]) {
    return CREATE_FLOW_BLUEPRINTS[selectedMeta.id];
  }
  return {
    ...DEFAULT_CREATE_FLOW_BLUEPRINT,
    stepTwoGuidance:
      starterId && STEP_TWO_GUIDANCE[starterId]
        ? STEP_TWO_GUIDANCE[starterId]
        : selectedMeta?.id && STEP_TWO_GUIDANCE[selectedMeta.id]
        ? STEP_TWO_GUIDANCE[selectedMeta.id]
        : null,
  };
};

const TASK_PRICE_CHIPS = Object.freeze(['8', '12', '20']);

const CATEGORY_SUGGESTIONS = [
  'Food',
  'Home',
  'Services',
  'Transport',
  'Errands',
  'Wellness',
  'Education',
  'Tech',
  'Outdoor',
  'Events',
];

const HISTORY_KEY = 'localopsCreateHistory';
const HISTORY_LIMIT = 8;
const DRAFT_PREFIX = 'localopsDraft';
// Presence Phase 2+: prioritize presence-active users by time/routing only.
// Do NOT gate discovery or hide tasks from non-presence users.
const LOCALOPS_PRESENCE_GATING_ENABLED = false;
const LAST_TYPE_KEY = 'localopsLastType';
const LOCALOPS_MIN_TASK_PRICE_USD = 1;
const LOCALOPS_MIN_TASK_PRICE_LABEL = '$1.00';
const LOCALOPS_TASK_DESCRIPTION_MAX_LENGTH = 10000;
const LOCALOPS_COMMUNITY_TASK_DESCRIPTION_MIN_LENGTH = 20;
const LOCALOPS_TASK_PRICE_REQUIRED_MESSAGE =
  `Enter an amount of at least ${LOCALOPS_MIN_TASK_PRICE_LABEL} to fund this task.`;
const LOCALOPS_PIN_LOCALITY_TOLERANCE_MULTIPLIER = 1.05;
const LOCALOPS_PIN_LOCALITY_TOLERANCE_MIN_METERS = 500;
const LOCALOPS_PIN_OUTSIDE_LOCAL_AREA_MESSAGE =
  'That location is outside this LocalOps area. Add city/state or choose a closer local match.';
const LOCALOPS_ORIGIN_CONFIRMATION_REQUIRED_MESSAGE =
  'Select the matching local place to confirm this location.';
const LOCALOPS_RIDE_HELP_STARTER_ID = 'ride_help';
const LOCALOPS_ASSISTANCE_DESTINATION_CONFIRMATION_REQUIRED_MESSAGE =
  'Select a confirmed destination before posting this assistance task.';
const LOCALOPS_TASK_DESTINATION_CONFIRMATION_REQUIRED_MESSAGE =
  'Select a confirmed destination before posting this task.';
const LOCALOPS_RIDE_HELP_DESTINATION_CONFIRMATION_REQUIRED_MESSAGE =
  'Select a confirmed destination before posting this ride-help task.';
const LOCALOPS_DROPOFF_CONFIRMATION_REQUIRED_MESSAGE =
  'Select a confirmed drop-off before posting this task.';
const LOCALOPS_RIDE_HELP_DESTINATION_RETRY_MESSAGE =
  'Try adding Camden, TN or a nearby business/landmark.';
const EMPTY_ORIGIN_RESOLUTION = Object.freeze({
  candidates: [],
  status: 'idle',
  message: '',
});

const getTaskDescriptionLength = (value) => String(value || '').length;

const hasFiniteLatLon = (value) =>
  value &&
  Number.isFinite(Number(value.lat)) &&
  Number.isFinite(Number(value.lon));

const isPinOutsideLocalArea = ({ location, defaultCenter, radiusMeters }) => {
  if (!hasFiniteLatLon(location) || !hasFiniteLatLon(defaultCenter)) {
    return false;
  }
  const numericRadius = Number(radiusMeters);
  if (!Number.isFinite(numericRadius) || numericRadius <= 0) return false;
  const distance = getDistanceFromLatLonInMeters(
    Number(defaultCenter.lat),
    Number(defaultCenter.lon),
    Number(location.lat),
    Number(location.lon),
  );
  if (!Number.isFinite(distance)) return false;
  const allowedRadius = Math.max(
    numericRadius * LOCALOPS_PIN_LOCALITY_TOLERANCE_MULTIPLIER,
    numericRadius + LOCALOPS_PIN_LOCALITY_TOLERANCE_MIN_METERS,
  );
  return distance > allowedRadius;
};

const trimLocalOpsText = (value) => String(value || '').trim();

const candidateDisplayText = (candidate) =>
  trimLocalOpsText(
    candidate?.addressText ||
      candidate?.publicAddress ||
      candidate?.name ||
      candidate?.label,
  );

const candidateSecondaryText = (candidate) => {
  const primary = trimLocalOpsText(candidate?.name || candidate?.label);
  const secondary = trimLocalOpsText(
    candidate?.addressText || candidate?.publicAddress,
  );
  if (!secondary || secondary === primary) return '';
  return secondary;
};

const candidateCoordinates = (candidate) => {
  const lat = Number(candidate?.coordinates?.lat ?? candidate?.lat);
  const lon = Number(
    candidate?.coordinates?.lon ??
      candidate?.coordinates?.lng ??
      candidate?.lon ??
      candidate?.lng,
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return { lat, lon };
};

const compactLocalOpsPreviewText = (value, maxLength = 420) => {
  const normalized = String(value || '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!normalized || normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trim()}...`;
};

const parseLocalOpsPrice = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const normalizeDollarCents = (value) => {
  const parsed = parseLocalOpsPrice(value);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
};

const coerceFlag = (value, fallback = false) => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  const normalized = String(value).trim().toLowerCase();
  if (['0', 'false', 'off', 'no'].includes(normalized)) return false;
  if (['1', 'true', 'on', 'yes'].includes(normalized)) return true;
  return fallback;
};

const maskCredential = (value) => {
  const raw = String(value || '');
  if (!raw) return '';
  if (raw.length <= 8) return '***';
  return `${raw.slice(0, 4)}...${raw.slice(-4)}`;
};

const maskSensitiveLocalOpsPayload = (value) => {
  if (Array.isArray(value)) return value.map(maskSensitiveLocalOpsPayload);
  if (!value || typeof value !== 'object') return value;
  return Object.entries(value).reduce((next, [key, entry]) => {
    next[key] =
      key === 'intakeDraftToken'
        ? maskCredential(entry)
        : maskSensitiveLocalOpsPayload(entry);
    return next;
  }, {});
};

const isLocalOpsAdminRole = (role) =>
  ['admin', 'galaxyAdmin'].includes(String(role || ''));
const isRideHelpStarterPresetValue = (value) =>
  value?.rideHelpStarter === true || value?.starterId === LOCALOPS_RIDE_HELP_STARTER_ID;
const isRideHelpCreateTypeId = (typeId) => typeId === 'task_assistance';
const shouldNormalizeRideHelpCreatePreset = (value) =>
  isRideHelpStarterPresetValue(value) ||
  isRideHelpCreateTypeId(value?.type) ||
  value?.taskType === 'assisted_transport';
const buildRideHelpCreatePreset = (sourcePreset = {}) => {
  const rideHelpPreset = buildRideHelpStarterTaskPreset();
  if (!rideHelpPreset) return sourcePreset || null;
  return {
    ...sourcePreset,
    ...rideHelpPreset,
    starterId: LOCALOPS_RIDE_HELP_STARTER_ID,
    rideHelpStarter: true,
  };
};
const buildCreatePresetApplySignature = (value = {}) =>
  [
    value?.type || '',
    value?.starterId || '',
    value?.rideHelpStarter ? 'ride-help' : '',
    value?.rideTemplate || '',
    value?.intakeDraftToken || '',
    value?.offeringDraftToken || '',
    value?.offeringDraftId || '',
    value?.title || '',
    value?.category || '',
  ].join('|');
const isGuidedTaskStarterPresetValue = (value) =>
  Boolean(
    value?.starterId &&
      CREATE_FLOW_BLUEPRINTS[String(value.starterId)]?.taskFlow,
  );

const resolveModeFromType = (typeId) =>
  TYPE_OPTIONS.find((opt) => opt.id === typeId)?.mode || null;
const resolveTaskTypeFromType = (typeId) =>
  TYPE_OPTIONS.find((opt) => opt.id === typeId)?.taskType || null;
const normalizeCreateLaunchGroupHint = (value) =>
  value === 'offerings' || value === 'tasks' ? value : null;
const getDefaultDeliverySupportedForType = (typeId) =>
  typeId === 'offering_product';
const getCreateDraftKey = (playerId, mode) =>
  playerId && mode ? `${DRAFT_PREFIX}:${playerId}:${mode}` : null;
const pickLastHistoryValue = (list) =>
  Array.isArray(list) && list.length ? String(list[0]) : '';
const shouldAutoSyncRestoredDescription = (title, description) => {
  const restoredTitle = String(title || '');
  const restoredDescription = String(description || '');
  return !restoredDescription || restoredDescription === restoredTitle;
};

const buildGuidedTaskGeneratedDescription = ({
  blueprint,
  title,
  notes,
  hasDeliveryAddress,
  availabilityEndMode,
}) => {
  const summary =
    String(title || '').trim() || blueprint.systemTitle || 'Local task';
  const lines = [summary];
  const trimmedNotes = String(notes || '').trim();
  if (trimmedNotes) {
    lines.push('', 'Notes:', trimmedNotes);
  }
  lines.push('', blueprint.privateDetailsCopy);
  if (hasDeliveryAddress) {
    lines.push('Delivery/drop-off details are shared privately after accept.');
  }
  if (availabilityEndMode && availabilityEndMode !== 'specific') {
    lines.push(`Timing: ${labelForEndMode(availabilityEndMode)}.`);
  }
  return lines.filter(Boolean).join('\n');
};

const makeEmptyHistory = () => ({
  offering: {
    title: [],
    description: [],
    category: [],
    price: [],
    quantity: [],
    media: [],
  },
  task: {
    title: [],
    description: [],
    category: [],
    price: [],
    pickupAddress: [],
    deliveryAddress: [],
    media: [],
  },
});

const normalizeHistory = (value) => {
  const next = makeEmptyHistory();
  if (!value || typeof value !== 'object') return next;
  ['offering', 'task'].forEach((mode) => {
    const bucket = value[mode];
    if (!bucket || typeof bucket !== 'object') return;
    Object.keys(next[mode]).forEach((field) => {
      if (!Array.isArray(bucket[field])) return;
      if (field === 'media') {
        next[mode][field] = bucket[field]
          .map((item) => {
            if (!item) return null;
            if (typeof item === 'string') {
              return { url: item.trim(), type: isVideoFile(item) ? 'video' : 'image' };
            }
            const url = String(item.url || item.preview || '').trim();
            if (!url) return null;
            const type = item.type === 'video' || item.type === 'image' ? item.type : isVideoFile(url) ? 'video' : 'image';
            return { url, type };
          })
          .filter((m) => m && m.url);
      } else {
        next[mode][field] = bucket[field]
          .map((item) => String(item).trim())
          .filter(Boolean);
      }
    });
  });
  return next;
};

const hasRestorableDraftValue = (draft) => {
  if (!draft || typeof draft !== 'object') return false;
  const stringFields = [
    'title',
    'description',
    'pickupAddress',
    'deliveryAddress',
    'destinationAddress',
    'category',
    'price',
  ];
  if (stringFields.some((field) => String(draft[field] || '').trim())) {
    return true;
  }
  if (
    draft.quantityAvailable !== undefined &&
    String(draft.quantityAvailable || '').trim() &&
    String(draft.quantityAvailable) !== '1'
  ) {
    return true;
  }
  if (Array.isArray(draft.trustedPlayers) && draft.trustedPlayers.length > 0) {
    return true;
  }
  if (draft.visibility && draft.visibility !== 'nearby') return true;
  if (draft.pinSource && draft.pinSource !== 'default') return true;
  if (draft.customStart === true) return true;
  if (draft.availabilityEndMode && draft.availabilityEndMode !== 'ongoing') {
    return true;
  }
  return false;
};

const readCreateDraft = (draftKey) => {
  if (!draftKey || typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(draftKey);
    if (!stored) return null;
    const draft = JSON.parse(stored);
    if (!hasRestorableDraftValue(draft)) return null;
    return draft;
  } catch (err) {
    return null;
  }
};

const END_MODE_OPTIONS = [
  { id: 'specific', label: 'Specific end time' },
  { id: 'ongoing', label: 'Ongoing' },
  { id: 'supplies', label: 'While supplies last' },
  { id: 'appointment', label: 'By appointment' },
];

const formatLocalDateTime = (date) => {
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parseLocalDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const getDefaultSchedule = () => {
  const now = new Date();
  const start = new Date(now.getTime());
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    start: formatLocalDateTime(start),
    end: formatLocalDateTime(end),
  };
};

const resolveAvailabilityEnd = (startDate, endValue, mode) => {
  if (!startDate) return null;
  if (mode === 'specific') return parseLocalDateTime(endValue);
  const modeDays = {
    ongoing: 30,
    supplies: 7,
    appointment: 14,
  };
  const days = modeDays[mode] || 7;
  return new Date(startDate.getTime() + days * 24 * 60 * 60 * 1000);
};

const labelForEndMode = (mode) => {
  switch (mode) {
    case 'ongoing':
      return 'Ongoing';
    case 'supplies':
      return 'While supplies last';
    case 'appointment':
      return 'By appointment';
    default:
      return 'Specific end';
  }
};

const clampCoverIndex = (index, mediaLength) => {
  const parsed = Number(index);
  if (!Number.isInteger(parsed) || parsed < 0) return 0;
  if (!mediaLength) return 0;
  return Math.min(parsed, mediaLength - 1);
};

const buildCreatePresetMediaItems = (mediaList) => {
  if (!Array.isArray(mediaList) || !mediaList.length) return [];
  return mediaList
    .map((item) => {
      if (!item) return null;
      if (typeof item === 'object' && item.file) {
        return {
          ...item,
          preview: item.preview || '',
          isVideo: Boolean(item.isVideo) || isVideoFile(item.file?.name || ''),
          persisted: false,
        };
      }
      if (
        typeof item === 'object' &&
        (item.needsReattach || item.placeholder)
      ) {
        return {
          preview: item.preview || '',
          url: item.url || '',
          isVideo: Boolean(item.isVideo),
          placeholder: Boolean(item.placeholder),
          label: item.label || 'Draft preview expired.',
          needsReattach: true,
        };
      }
      const media = normalizeMediaList({ media: [item] })[0];
      if (!media?.url) return null;
      return {
        url: media.url,
        preview: media.url,
        isVideo: media.type === 'video' || isVideoFile(media.url),
        persisted: true,
      };
    })
    .filter(Boolean);
};

const resolveCreatedEntityId = (result) => {
  const candidates = [
    result?.taskId,
    result?.offeringId,
    result?.id,
    result?._id,
    result?.data?.taskId,
    result?.data?.offeringId,
    result?.data?.id,
    result?.data?._id,
    result?.data?.data?.taskId,
    result?.data?.data?.offeringId,
    result?.data?.data?.id,
    result?.data?.data?._id,
  ];
  return candidates
    .map((value) => String(value || '').trim())
    .find(Boolean);
};

export default function LocalOpsCreateSheet({
  open,
  onClose,
  launchId,
  launchTypeHint = null,
  launchStepHint = null,
  launchGroupHint = null,
  defaultCenter,
  radiusMeters,
  onCreateOffering,
  onCreateTask,
  presenceSelf,
  currentPlayer = null,
  currentPlayerRole = 'player',
  localOpsRegionSlug = 'camden',
  onResolveVerifiedLocalCreatorReadiness = null,
  playerBalance,
  onRequestTopUp,
  onInsufficientBalance = null,
  pendingIntent = null,
  onRetryPendingIntent = () => {},
  friendOptions = [],
  disabledOfferings,
  disabledTasks,
  preset,
}) {
  const uploaderRef = useRef(null);
  const openInitRef = useRef(false);
  const presetApplySignatureRef = useRef('');
  const addressKeyRef = useRef(null);
  const pickupGeocodeRef = useRef('');
  const originCandidateRequestRef = useRef(0);
  const destinationGeocodeRef = useRef('');
  const destinationCandidateRequestRef = useRef(0);
  const deliveryPriceGeocodeRef = useRef('');
  const rideAutoPriceRef = useRef('');
  const rideHelpPriceKeyRef = useRef('');
  const rideHelpAutoPriceRef = useRef('');
  const rideHelpHasAutoPricedRef = useRef(false);
  const guidedRoutePriceKeyRef = useRef('');
  const guidedRouteAutoPriceRef = useRef('');
  const guidedRouteHasAutoPricedRef = useRef(false);
  const visibilityTouchedRef = useRef(false);
  const publishInFlightRef = useRef(false);
  const createFunnelSessionRef = useRef('');
  const createJourneySessionRef = useRef('');
  const createFunnelLocalDedupeRef = useRef(new Set());
  const createJourneyUnavailableSignatureRef = useRef('');
  const createJourneyTouchedFieldsRef = useRef(new Set());
  const createJourneyClosedRef = useRef(false);
  const mobileView = useMobileView();
  const verifiedLocalCreatorToggleEnabled = useMemo(
    () =>
      coerceFlag(
        process.env.REACT_APP_LOCALOPS_VERIFIED_LOCAL_CREATOR_TOGGLE_ENABLED,
        false,
      ),
    [],
  );
  if (!uploaderRef.current) {
    uploaderRef.current = createMediaUploader({});
  }
  const uploader = uploaderRef.current;

  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // AI++ polish: auto-sync description from title until user opts out
  const [descriptionAuto, setDescriptionAuto] = useState(true);
  const [pickupAddress, setPickupAddress] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [taskPaymentMode, setTaskPaymentMode] = useState(
    LOCALOPS_TASK_PAYMENT_MODES.PAID,
  );
  const [quantityAvailable, setQuantityAvailable] = useState('1');
  const [requestQuote, setRequestQuote] = useState(false);
  const [deliverySupported, setDeliverySupported] = useState(false);
  const [location, setLocation] = useState(null);
  const [pinSource, setPinSource] = useState('default');
  const [destination, setDestination] = useState(null);
  const [visibility, setVisibility] = useState('nearby');
  const [trustedPlayers, setTrustedPlayers] = useState([]);
  const [requireVerifiedLocal, setRequireVerifiedLocal] = useState(false);
  const [firstCompletionAccessRequested, setFirstCompletionAccessRequested] =
    useState(false);
  const [verifiedLocalReadiness, setVerifiedLocalReadiness] = useState({
    status: 'idle',
    canExpose: false,
    key: '',
  });
  const [liveTrackingEnabled, setLiveTrackingEnabled] = useState(true);
  const [availabilityStart, setAvailabilityStart] = useState('');
  const [availabilityEnd, setAvailabilityEnd] = useState('');
  const [availabilityEndMode, setAvailabilityEndMode] = useState('ongoing');
  const [customStart, setCustomStart] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [error, setError] = useState('');
  const [mediaWarning, setMediaWarning] = useState('');
  const [uploadPct, setUploadPct] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supplyProof, setSupplyProof] = useState(null);
  const [supplyProofLoading, setSupplyProofLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTypeInfo, setActiveTypeInfo] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [inputHistory, setInputHistory] = useState(() => {
    if (typeof window === 'undefined') return makeEmptyHistory();
    try {
      const stored = window.localStorage.getItem(HISTORY_KEY);
      if (!stored) return makeEmptyHistory();
      return normalizeHistory(JSON.parse(stored));
    } catch (err) {
      return makeEmptyHistory();
    }
  });
  const [animateFromFab, setAnimateFromFab] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationLabel, setLocationLabel] = useState('');
  const [, setIsResolvingLocation] = useState(false);
  const [pinNotice, setPinNotice] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinHelper, setPinHelper] = useState('');
  const [originResolution, setOriginResolution] = useState(
    EMPTY_ORIGIN_RESOLUTION,
  );
  const [destinationResolution, setDestinationResolution] = useState(
    EMPTY_ORIGIN_RESOLUTION,
  );
  const [destinationPinNotice, setDestinationPinNotice] = useState('');
  const [destinationPinError, setDestinationPinError] = useState('');
  const [isPickupGeocoding, setIsPickupGeocoding] = useState(false);
  const [isDestinationGeocoding, setIsDestinationGeocoding] = useState(false);
  const [rideSuggestedPrice, setRideSuggestedPrice] = useState(null);
  const [rideSuggestedDistanceMi, setRideSuggestedDistanceMi] = useState(null);
  const [isRidePriceGeocoding, setIsRidePriceGeocoding] = useState(false);
  const lastTypeRef = useRef(null);
  const draftPlayerId = presenceSelf?.playerId || 'anon';
  const normalizedLaunchGroupHint =
    normalizeCreateLaunchGroupHint(launchGroupHint);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(LAST_TYPE_KEY);
    if (stored) lastTypeRef.current = stored;
  }, []);

  const selectedMeta = useMemo(
    () => TYPE_OPTIONS.find((opt) => opt.id === selectedType) || null,
    [selectedType],
  );
  const isOffering = selectedMeta?.mode === 'offering';
  const isTask = selectedMeta?.mode === 'task';
  const isDelivery = selectedMeta?.taskType === 'delivery';
  const isAssistance = selectedMeta?.taskType === 'assisted_transport';
  const effectivePreset = useMemo(() => {
    const normalizedPreset = shouldNormalizeRideHelpCreatePreset(preset)
      ? buildRideHelpCreatePreset(preset)
      : preset || null;
    const selectedTypeOverridesPreset =
      Boolean(normalizedPreset?.type && selectedMeta?.id) &&
      selectedMeta.id !== normalizedPreset.type &&
      presetApplySignatureRef.current ===
        buildCreatePresetApplySignature(normalizedPreset);
    if (selectedTypeOverridesPreset) {
      return isRideHelpCreateTypeId(selectedMeta?.id)
        ? buildRideHelpCreatePreset()
        : null;
    }
    if (normalizedPreset) return normalizedPreset;
    if (!preset && isRideHelpCreateTypeId(selectedMeta?.id)) {
      return buildRideHelpCreatePreset();
    }
    return preset || null;
  }, [preset, selectedMeta?.id]);
  const isCollisionSpecialistRidePreset =
    effectivePreset?.rideTemplate === COLLISION_SPECIALIST_RIDE_TEMPLATE;
  const createFlowBlueprint = useMemo(
    () => resolveCreateFlowBlueprint({ preset: effectivePreset, selectedMeta }),
    [effectivePreset, selectedMeta],
  );
  const isRideHelpFlow = createFlowBlueprint.id === LOCALOPS_RIDE_HELP_STARTER_ID;
  const isRideHelpStarterPreset =
    isRideHelpStarterPresetValue(effectivePreset) || isRideHelpFlow;
  const isGuidedTaskFlow =
    isTask &&
    createFlowBlueprint.taskFlow === true &&
    !isRideHelpFlow &&
    !isCollisionSpecialistRidePreset;
  const isGuidedOfferingFlow =
    isOffering && createFlowBlueprint.offeringFlow === true;
  const systemOwnedTaskCategory = useMemo(() => {
    if (!isRideHelpFlow && !isGuidedTaskFlow) return '';
    return String(createFlowBlueprint?.category || '').trim();
  }, [createFlowBlueprint?.category, isGuidedTaskFlow, isRideHelpFlow]);
  const resolvedTaskCategory = useMemo(
    () => systemOwnedTaskCategory || category.trim(),
    [category, systemOwnedTaskCategory],
  );
  const guidedTaskHasDropoffAddress =
    isGuidedTaskFlow &&
    createFlowBlueprint.dropoffAddressMode === 'deliveryAddress';
  const usesDeliveryDestinationResolver = isTask && isDelivery;
  const usesResolverDropoffAddress =
    guidedTaskHasDropoffAddress || usesDeliveryDestinationResolver;
  const usesDestinationAddress = isAssistance || usesResolverDropoffAddress;
  const isGuidedRouteSuggestedFlow =
    isGuidedTaskFlow &&
    usesResolverDropoffAddress &&
    createFlowBlueprint.priceMode === 'route_suggested';
  const hasRouteSuggestedAmountFlow =
    isRideHelpStarterPreset ||
    isCollisionSpecialistRidePreset ||
    isGuidedRouteSuggestedFlow;
  const stepTwoGuidance = useMemo(() => {
    if (createFlowBlueprint.stepTwoGuidance) {
      return createFlowBlueprint.stepTwoGuidance;
    }
    const starterId = effectivePreset?.starterId;
    if (starterId && STEP_TWO_GUIDANCE[starterId]) {
      return STEP_TWO_GUIDANCE[starterId];
    }
    if (selectedMeta?.id && STEP_TWO_GUIDANCE[selectedMeta.id]) {
      return STEP_TWO_GUIDANCE[selectedMeta.id];
    }
    return null;
  }, [createFlowBlueprint, effectivePreset?.starterId, selectedMeta?.id]);
  const normalizedLocalOpsRegionSlug = useMemo(
    () => normalizeLocalOpsRegionSlug(localOpsRegionSlug, 'camden'),
    [localOpsRegionSlug],
  );
  const canCreateCommunityTaskInRegion = useMemo(
    () =>
      isTask &&
      canCreateCommunityTask(currentPlayer, normalizedLocalOpsRegionSlug),
    [currentPlayer, isTask, normalizedLocalOpsRegionSlug],
  );
  const communityTaskTypeEligible =
    isTask &&
    selectedMeta?.taskType === 'errand' &&
    !isCollisionSpecialistRidePreset;
  const canShowCommunityTaskChoice =
    canCreateCommunityTaskInRegion && communityTaskTypeEligible;
  const canUseCommunityTaskMode =
    canShowCommunityTaskChoice && communityTaskTypeEligible;
  const communityTaskSelected =
    isTask &&
    taskPaymentMode === LOCALOPS_TASK_PAYMENT_MODES.COMMUNITY &&
    canUseCommunityTaskMode;
  const draftKey =
    selectedMeta && draftPlayerId
      ? `${DRAFT_PREFIX}:${draftPlayerId}:${selectedMeta.mode || 'unknown'}`
      : null;
  useEffect(() => {
    if (!isOffering && !isTask) return;
    if (requestQuote) setRequestQuote(false);
    if (isOffering && quantityAvailable === '') setQuantityAvailable('1');
  }, [isOffering, isTask, requestQuote, quantityAvailable]);
  useEffect(() => {
    if (!isTask || !canUseCommunityTaskMode) {
      setTaskPaymentMode(LOCALOPS_TASK_PAYMENT_MODES.PAID);
    }
  }, [canUseCommunityTaskMode, isTask]);
  const historyMode = isOffering ? 'offering' : isTask ? 'task' : null;
  const titleHistory = useMemo(
    () => (historyMode ? inputHistory[historyMode]?.title || [] : []),
    [historyMode, inputHistory],
  );
  const descriptionHistory = useMemo(
    () =>
      historyMode ? inputHistory[historyMode]?.description || [] : [],
    [historyMode, inputHistory],
  );
  const descriptionSuggestions = useMemo(
    () => descriptionHistory.slice(0, 3),
    [descriptionHistory],
  );
  const categoryHistory = useMemo(
    () => (historyMode ? inputHistory[historyMode]?.category || [] : []),
    [historyMode, inputHistory],
  );
  const priceHistory = useMemo(
    () => (historyMode ? inputHistory[historyMode]?.price || [] : []),
    [historyMode, inputHistory],
  );
  const pickupHistory = useMemo(
    () =>
      historyMode === 'task'
        ? inputHistory.task?.pickupAddress || []
        : historyMode === 'offering'
        ? inputHistory.offering?.pickupAddress || []
        : [],
    [historyMode, inputHistory],
  );
  const deliveryHistory = useMemo(
    () =>
      historyMode === 'task'
        ? inputHistory.task?.deliveryAddress || []
        : [],
    [historyMode, inputHistory],
  );
  const destinationHistory = useMemo(
    () =>
      historyMode === 'task'
        ? inputHistory.task?.deliveryAddress || []
        : [],
    [historyMode, inputHistory],
  );
  const recentTitleChips = useMemo(
    () => titleHistory.slice(0, 3),
    [titleHistory],
  );

  // AI++: auto-sync description from title until user edits description
  useEffect(() => {
    if (isGuidedTaskFlow || isGuidedOfferingFlow) return;
    if (!descriptionAuto) return;
    if (!title) {
      if (description) setDescription('');
      return;
    }
    setDescription(title);
  }, [
    title,
    description,
    descriptionAuto,
    isGuidedTaskFlow,
    isGuidedOfferingFlow,
  ]);
  const categoryOptions = useMemo(() => {
    const seen = new Set();
    const combined = [...categoryHistory, ...CATEGORY_SUGGESTIONS];
    return combined.filter((item) => {
      const normalized = String(item).trim().toLowerCase();
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  }, [categoryHistory]);
  const pickupAddressValue = useMemo(
    () => pickupAddress.trim(),
    [pickupAddress],
  );
  const deliveryAddressValue = useMemo(
    () => deliveryAddress.trim(),
    [deliveryAddress],
  );
  const destinationAddressValue = useMemo(
    () => destinationAddress.trim(),
    [destinationAddress],
  );
  const taskDeliveryAddressValue = (
    guidedTaskHasDropoffAddress || usesDeliveryDestinationResolver
  )
    ? destinationAddressValue
    : deliveryAddressValue;
  const pickupCoordsValid =
    location && Number.isFinite(location.lat) && Number.isFinite(location.lon);
  const originPinOutsideLocalArea = useMemo(
    () =>
      (isTask || isOffering) &&
      isPinOutsideLocalArea({
        location,
        defaultCenter,
        radiusMeters,
      }),
    [defaultCenter, isOffering, isTask, location, radiusMeters],
  );
  const pickupPinMatches =
    pickupAddressValue &&
    pickupGeocodeRef.current === pickupAddressValue;
  const originUsesExplicitCoordinateSource =
    pickupCoordsValid && pinSource === 'manual';
  const typedOriginNeedsCandidateConfirmation =
    (isTask || isOffering) &&
    Boolean(pickupAddressValue) &&
    !originUsesExplicitCoordinateSource &&
    !pickupPinMatches;
  const originLocationConfirmed =
    pickupCoordsValid &&
    !originPinOutsideLocalArea &&
    (originUsesExplicitCoordinateSource || pickupPinMatches);
  const verifiedLocalCreatorIsAdmin = isLocalOpsAdminRole(currentPlayerRole);
  const verifiedLocalReadinessKey =
    pickupCoordsValid && isTask
      ? `${Number(location.lat).toFixed(5)}|${Number(location.lon).toFixed(
          5,
        )}|${Math.round(Number(radiusMeters || 0))}`
      : '';
  const canShowRequireVerifiedLocal =
    verifiedLocalCreatorToggleEnabled &&
    isTask &&
    (verifiedLocalCreatorIsAdmin ||
      (verifiedLocalReadiness.status === 'ready' &&
        verifiedLocalReadiness.key === verifiedLocalReadinessKey &&
        verifiedLocalReadiness.canExpose === true));
  const firstCompletionAccessAdmin =
    String(currentPlayerRole || '') === 'admin';
  const canShowFirstCompletionAccess =
    firstCompletionAccessAdmin &&
    isTask &&
    !communityTaskSelected &&
    !isRideHelpFlow;
  const firstCompletionAccessSupported =
    canShowFirstCompletionAccess &&
    visibility === 'nearby' &&
    trustedPlayers.length === 0 &&
    !requireVerifiedLocal;
  const destinationCoordsValid =
    destination &&
    Number.isFinite(destination.lat) &&
    Number.isFinite(destination.lon);
  const assistanceDestinationConfirmed =
    usesDestinationAddress &&
    Boolean(destinationAddressValue) &&
    destinationGeocodeRef.current === destinationAddressValue &&
    destinationCoordsValid;
  const rideHelpDestinationConfirmed =
    isRideHelpStarterPreset &&
    assistanceDestinationConfirmed;
  const destinationConfirmationRequiredMessage = isRideHelpStarterPreset
    ? LOCALOPS_RIDE_HELP_DESTINATION_CONFIRMATION_REQUIRED_MESSAGE
    : usesResolverDropoffAddress
    ? isCollisionSpecialistRidePreset
      ? LOCALOPS_TASK_DESTINATION_CONFIRMATION_REQUIRED_MESSAGE
      : LOCALOPS_DROPOFF_CONFIRMATION_REQUIRED_MESSAGE
    : LOCALOPS_ASSISTANCE_DESTINATION_CONFIRMATION_REQUIRED_MESSAGE;
  const destinationConfirmedNotice =
    usesResolverDropoffAddress && !isCollisionSpecialistRidePreset
    ? 'Drop-off confirmed.'
    : 'Destination confirmed.';
  const showDestinationPinError =
    usesDestinationAddress &&
    destinationAddressValue &&
    !assistanceDestinationConfirmed;
  const isPublishBlockedByDestination =
    usesDestinationAddress &&
    destinationAddressValue &&
    !assistanceDestinationConfirmed;
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[DEBUG][STEP]', step, {
      destination,
      destinationCoordsValid,
      assistanceDestinationConfirmed,
      destinationAddressValue,
      pickupAddressValue,
      pickupCoordsValid,
      isPickupGeocoding,
      isDestinationGeocoding,
      location,
    });
    console.assert(
      !assistanceDestinationConfirmed || !showDestinationPinError,
      'INVARIANT VIOLATION: destination has coordinates but destination pin error is shown.',
    );
    console.assert(
      !assistanceDestinationConfirmed || !isPublishBlockedByDestination,
      'INVARIANT VIOLATION: destination has coordinates but publish is still blocked.',
    );
  }
  useEffect(() => {
    if (
      !open ||
      !verifiedLocalCreatorToggleEnabled ||
      !isTask ||
      verifiedLocalCreatorIsAdmin
    ) {
      setVerifiedLocalReadiness({ status: 'idle', canExpose: false, key: '' });
      return;
    }
    if (!pickupCoordsValid || !verifiedLocalReadinessKey) {
      setVerifiedLocalReadiness({
        status: 'missing_location',
        canExpose: false,
        key: '',
      });
      return;
    }
    if (typeof onResolveVerifiedLocalCreatorReadiness !== 'function') {
      setVerifiedLocalReadiness({
        status: 'unavailable',
        canExpose: false,
        key: verifiedLocalReadinessKey,
      });
      return;
    }

    let active = true;
    setVerifiedLocalReadiness({
      status: 'loading',
      canExpose: false,
      key: verifiedLocalReadinessKey,
    });
    Promise.resolve(
      onResolveVerifiedLocalCreatorReadiness({
        lat: location.lat,
        lng: location.lon,
        radiusMeters,
      }),
    )
      .then((result) => {
        if (!active) return;
        setVerifiedLocalReadiness({
          status: 'ready',
          canExpose:
            result?.canExposeVerifiedLocalCreatorToggle === true ||
            result?.canExpose === true,
          key: verifiedLocalReadinessKey,
        });
      })
      .catch(() => {
        if (!active) return;
        setVerifiedLocalReadiness({
          status: 'error',
          canExpose: false,
          key: verifiedLocalReadinessKey,
        });
      });
    return () => {
      active = false;
    };
  }, [
    open,
    verifiedLocalCreatorToggleEnabled,
    isTask,
    verifiedLocalCreatorIsAdmin,
    pickupCoordsValid,
    verifiedLocalReadinessKey,
    onResolveVerifiedLocalCreatorReadiness,
    location,
    radiusMeters,
  ]);
  const showInfoCard = showInfo;
  const headerTitle = useMemo(() => {
    if (!selectedMeta) return 'Create LocalOps';
    if (selectedMeta.mode === 'offering') {
      return selectedMeta.offeringType === 'service'
        ? 'Create Service Offering'
        : 'Create Product Offering';
    }
    if (isGuidedTaskFlow && createFlowBlueprint.headerTitle) {
      return createFlowBlueprint.headerTitle;
    }
    if (selectedMeta.taskType === 'delivery') {
      return isCollisionSpecialistRidePreset
        ? 'Request Ride Help'
        : 'Create Delivery Task';
    }
    if (selectedMeta.taskType === 'errand') return 'Create Errand Task';
    if (selectedMeta.taskType === 'home_help') return 'Create Home Help Task';
    if (selectedMeta.taskType === 'assisted_transport')
      return isRideHelpStarterPreset ? 'Create Ride Help Task' : 'Create Assistance Task';
    return 'Create Task';
  }, [
    selectedMeta,
    isGuidedTaskFlow,
    createFlowBlueprint.headerTitle,
    isCollisionSpecialistRidePreset,
    isRideHelpStarterPreset,
  ]);
  const sheetPositionClass = useMemo(() => {
    if (mobileView) return '';
    return step > 0 ? 'localops-sheet--top' : 'localops-sheet--centered';
  }, [mobileView, step]);
  const sheetClassName = useMemo(
    () =>
      [animateFromFab ? 'localops-sheet--from-fab' : '', sheetPositionClass]
        .filter(Boolean)
        .join(' '),
    [animateFromFab, sheetPositionClass],
  );
  const localOpsConfig = useMemo(() => getLocalOpsConfig(), []);
  const parsedPrice = useMemo(() => parseLocalOpsPrice(price), [price]);
  const taskPriceIsValid =
    !isTask ||
    communityTaskSelected ||
    (Number.isFinite(parsedPrice) &&
      parsedPrice >= LOCALOPS_MIN_TASK_PRICE_USD);
  const taskDescriptionLength = useMemo(
    () => getTaskDescriptionLength(description),
    [description],
  );
  const taskDescriptionOverLimit =
    isTask && taskDescriptionLength > LOCALOPS_TASK_DESCRIPTION_MAX_LENGTH;
  const communityDescriptionIsValid =
    !communityTaskSelected ||
    String(description || '').trim().length >=
      LOCALOPS_COMMUNITY_TASK_DESCRIPTION_MIN_LENGTH;
  const escrowRequiredJGK = useMemo(() => {
    if (communityTaskSelected) return 0;
    if (!isTask) return 0;
    if (
      !Number.isFinite(parsedPrice) ||
      parsedPrice < LOCALOPS_MIN_TASK_PRICE_USD
    ) {
      return 0;
    }
    const rate = Number.isFinite(localOpsConfig?.jgkPerUsd)
      ? localOpsConfig.jgkPerUsd
      : 1000;
    return Math.round(parsedPrice * rate);
  }, [communityTaskSelected, isTask, parsedPrice, localOpsConfig]);
  const escrowUsdFirstLabel = useMemo(
    () => formatUsdJgkPairFromJgk(escrowRequiredJGK, localOpsConfig?.jgkPerUsd),
    [escrowRequiredJGK, localOpsConfig],
  );
  const feeLabel = formatPercent(localOpsConfig.feePercent);
  const hasPaidTaskAmount =
    isTask && !communityTaskSelected && escrowRequiredJGK > 0;
  const buildCreateSessionKey = useCallback(
    () =>
      [
        'create-sheet',
        launchId || 'direct',
        Date.now(),
        Math.random().toString(36).slice(2, 10),
      ].join(':'),
    [launchId],
  );
  const getCreateFunnelSessionKey = useCallback(() => {
    if (!createFunnelSessionRef.current) {
      createFunnelSessionRef.current = buildCreateSessionKey();
    }
    return createFunnelSessionRef.current;
  }, [buildCreateSessionKey]);
  const getCreateJourneySessionKey = useCallback(() => {
    if (!createJourneySessionRef.current) {
      createJourneySessionRef.current = buildCreateSessionKey();
    }
    return createJourneySessionRef.current;
  }, [buildCreateSessionKey]);
  const resolveCreateAmountSource = useCallback(() => {
    if (
      rideHelpAutoPriceRef.current ||
      guidedRouteAutoPriceRef.current ||
      deliveryPriceGeocodeRef.current ||
      rideSuggestedPrice
    ) {
      return 'route_calculated';
    }
    if (String(price || '').trim()) return 'manual';
    return 'default';
  }, [price, rideSuggestedPrice]);
  const resolveCreateShortfallUSD = useCallback(() => {
    const balance = Number(playerBalance);
    const rate = Number.isFinite(localOpsConfig?.jgkPerUsd)
      ? localOpsConfig.jgkPerUsd
      : 1000;
    if (!Number.isFinite(balance) || !Number.isFinite(rate) || rate <= 0) {
      return undefined;
    }
    const shortfallJGK = Math.max(0, escrowRequiredJGK - balance);
    if (shortfallJGK <= 0) return undefined;
    return Math.round((shortfallJGK / rate) * 100) / 100;
  }, [escrowRequiredJGK, localOpsConfig, playerBalance]);
  const resolveCreateJourneyAmountState = useCallback(() => {
    if (!String(price || '').trim()) return 'unset';
    if (
      !Number.isFinite(parsedPrice) ||
      parsedPrice < LOCALOPS_MIN_TASK_PRICE_USD
    ) {
      return 'invalid';
    }
    return resolveCreateAmountSource();
  }, [parsedPrice, price, resolveCreateAmountSource]);
  const buildCreateJourneyPayload = useCallback(
    (payload = {}) => {
      const amountUSD = Number.isFinite(parsedPrice) ? parsedPrice : undefined;
      const starterId =
        effectivePreset?.starterId || createFlowBlueprint?.id || selectedMeta?.id;
      const taskType =
        selectedMeta?.taskType ||
        selectedMeta?.offeringType ||
        selectedMeta?.mode ||
        undefined;
      const currentStep =
        payload.currentStep || getCreateJourneyStepName(step);
      const field = normalizeCreateJourneyFieldName(payload.field);
      const firstBlockingField = normalizeCreateJourneyFieldName(
        payload.firstBlockingField,
      );
      return {
        createFlowId: getCreateJourneySessionKey(),
        regionSlug: normalizedLocalOpsRegionSlug,
        source: normalizedLaunchGroupHint || 'create_sheet',
        intent: normalizedLaunchGroupHint || undefined,
        taskType,
        starterId,
        selectedType: selectedType || selectedMeta?.id || undefined,
        entrySurface: 'localops_create_sheet',
        surface: 'create_sheet',
        currentStep,
        amountState: resolveCreateJourneyAmountState(),
        hasPickup: Boolean(pickupAddressValue),
        hasDestination: Boolean(destinationAddressValue),
        hasDeliveryAddress: Boolean(taskDeliveryAddressValue),
        hasAmount: Boolean(
          amountUSD && amountUSD >= LOCALOPS_MIN_TASK_PRICE_USD,
        ),
        hasMedia: mediaItems.length > 0,
        isRideHelp: Boolean(isRideHelpFlow),
        isGuidedTask: Boolean(isGuidedTaskFlow),
        isOffering: Boolean(isOffering),
        isTask: Boolean(isTask),
        ...payload,
        ...(field ? { field } : {}),
        ...(firstBlockingField ? { firstBlockingField } : {}),
      };
    },
    [
      createFlowBlueprint,
      destinationAddressValue,
      getCreateJourneySessionKey,
      isGuidedTaskFlow,
      isOffering,
      isRideHelpFlow,
      isTask,
      mediaItems.length,
      normalizedLaunchGroupHint,
      normalizedLocalOpsRegionSlug,
      parsedPrice,
      pickupAddressValue,
      effectivePreset?.starterId,
      resolveCreateJourneyAmountState,
      selectedMeta,
      selectedType,
      step,
      taskDeliveryAddressValue,
    ],
  );
  const emitCreateJourneyEvent = useCallback(
    (eventName, payload = {}, options = {}) => {
      if (!eventName) return;
      void recordLocalOpsCreateSheetJourneyEvent(
        eventName,
        buildCreateJourneyPayload(payload),
        {
          regionSlug: normalizedLocalOpsRegionSlug,
          timeoutMs: 1200,
          ...options,
        },
      );
    },
    [buildCreateJourneyPayload, normalizedLocalOpsRegionSlug],
  );
  const emitCreateFunnelEvent = useCallback(
    (eventName, payload = {}, options = {}) => {
      if (!eventName) return;
      const sessionKey = getCreateFunnelSessionKey();
      const {
        oncePerSheet = false,
        dedupeKey,
        oncePerSession,
        ttlMs,
      } = options;
      const localKey = oncePerSheet
        ? `${sessionKey}:${dedupeKey || eventName}`
        : '';
      if (localKey) {
        if (createFunnelLocalDedupeRef.current.has(localKey)) return;
        createFunnelLocalDedupeRef.current.add(localKey);
      }
      const amountUSD = Number.isFinite(parsedPrice) ? parsedPrice : undefined;
      const starterId =
        effectivePreset?.starterId || createFlowBlueprint?.id || selectedMeta?.id;
      const taskType =
        selectedMeta?.taskType ||
        selectedMeta?.offeringType ||
        selectedMeta?.mode ||
        undefined;
      void recordLocalOpsFunnelEvent(
        eventName,
        {
          regionSlug: normalizedLocalOpsRegionSlug,
          taskType,
          starterId,
          selectedType: selectedType || selectedMeta?.id || undefined,
          entrySurface: 'localops_create_sheet',
          surface: 'create_sheet',
          amountUSD,
          paymentMode: isTask
            ? communityTaskSelected
              ? LOCALOPS_TASK_PAYMENT_MODES.COMMUNITY
              : LOCALOPS_TASK_PAYMENT_MODES.PAID
            : isOffering
            ? 'offering'
            : taskPaymentMode,
          hasPickup: Boolean(pickupAddressValue),
          hasDestination: Boolean(destinationAddressValue),
          hasDeliveryAddress: Boolean(taskDeliveryAddressValue),
          hasAmount: Boolean(amountUSD && amountUSD >= LOCALOPS_MIN_TASK_PRICE_USD),
          hasMedia: mediaItems.length > 0,
          isRideHelp: Boolean(isRideHelpFlow),
          isGuidedTask: Boolean(isGuidedTaskFlow),
          isOffering: Boolean(isOffering),
          isTask: Boolean(isTask),
          ...payload,
        },
        {
          transport: 'launch',
          regionSlug: normalizedLocalOpsRegionSlug,
          ...(dedupeKey ? { dedupeKey: `${sessionKey}:${dedupeKey}` } : {}),
          ...(oncePerSession ? { oncePerSession: true } : {}),
          ...(ttlMs ? { ttlMs } : {}),
        },
      );
      if (String(eventName || '').startsWith('localops_create_')) {
        emitCreateJourneyEvent(eventName, payload);
      }
    },
    [
      communityTaskSelected,
      createFlowBlueprint,
      destinationAddressValue,
      getCreateFunnelSessionKey,
      isGuidedTaskFlow,
      isOffering,
      isRideHelpFlow,
      isTask,
      mediaItems.length,
      normalizedLocalOpsRegionSlug,
      parsedPrice,
      pickupAddressValue,
      effectivePreset?.starterId,
      selectedMeta,
      selectedType,
      emitCreateJourneyEvent,
      taskDeliveryAddressValue,
      taskPaymentMode,
    ],
  );
  const emitCreateFieldFirstInteraction = useCallback(
    (field) => {
      const normalizedField = normalizeCreateJourneyFieldName(field);
      if (!normalizedField) return;
      if (createJourneyTouchedFieldsRef.current.has(normalizedField)) return;
      createJourneyTouchedFieldsRef.current.add(normalizedField);
      emitCreateJourneyEvent(
        LOCALOPS_FUNNEL_EVENTS.CREATE_FIELD_FIRST_INTERACTION,
        {
          currentStep: getCreateJourneyStepName(step),
          field: normalizedField,
        },
      );
    },
    [emitCreateJourneyEvent, step],
  );
  const displayedSupplyProof = supplyProof || GENERIC_SUPPLY_PROOF;
  const rideReviewAmountLabel = useMemo(() => {
    if (
      !Number.isFinite(parsedPrice) ||
      parsedPrice < LOCALOPS_MIN_TASK_PRICE_USD
    ) {
      return 'Not set yet';
    }
    return parsedPrice % 1 === 0
      ? `$${parsedPrice.toFixed(0)}`
      : `$${parsedPrice.toFixed(2)}`;
  }, [parsedPrice]);
  const rideSuggestedDistanceLabel = useMemo(() => {
    const miles = Number(rideSuggestedDistanceMi);
    if (!Number.isFinite(miles)) return null;
    if (miles < 0.1) return 'under 0.1 mi';
    return `${miles.toFixed(1)} mi`;
  }, [rideSuggestedDistanceMi]);
  const rideHelpTimeLabel = useMemo(() => {
    const parsedStart = parseLocalDateTime(availabilityStart);
    if (customStart && parsedStart) {
      return parsedStart.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
    if (availabilityEndMode === 'specific' && availabilityEnd) {
      const parsedEnd = parseLocalDateTime(availabilityEnd);
      if (parsedEnd) {
        return `Before ${parsedEnd.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}`;
      }
    }
    return 'As soon as a helper is available';
  }, [availabilityStart, availabilityEnd, availabilityEndMode, customStart]);
  const rideHelpGeneratedDescription = useMemo(() => {
    if (!isRideHelpFlow) return description.trim();
    const notes = description.trim();
    const lines = [
      'I need local ride help.',
      'Exact pickup and destination are shared after a helper accepts and starts.',
    ];
    lines.push(`When: ${rideHelpTimeLabel}`);
    if (rideReviewAmountLabel !== 'Not set yet') {
      lines.push(`Amount: ${rideReviewAmountLabel}`);
    }
    if (notes) lines.push(`Notes: ${notes}`);
    return lines.join('\n');
  }, [
    isRideHelpFlow,
    description,
    rideHelpTimeLabel,
    rideReviewAmountLabel,
  ]);
  const guidedTaskGeneratedDescription = useMemo(() => {
    if (!isGuidedTaskFlow) return description.trim();
    return buildGuidedTaskGeneratedDescription({
      blueprint: createFlowBlueprint,
      title,
      notes: description,
      hasDeliveryAddress:
        (isDelivery || guidedTaskHasDropoffAddress) &&
        Boolean(taskDeliveryAddressValue),
      availabilityEndMode,
    });
  }, [
    isGuidedTaskFlow,
    createFlowBlueprint,
    title,
    description,
    isDelivery,
    guidedTaskHasDropoffAddress,
    taskDeliveryAddressValue,
    availabilityEndMode,
  ]);
  const publishLabel = useMemo(() => {
    if (isSubmitting) return 'Publishing...';
    if (communityTaskSelected) return 'Publish Community Task';
    if (escrowRequiredJGK > 0) {
      if (isRideHelpFlow) return 'Publish Ride Help task';
      return isCollisionSpecialistRidePreset
        ? 'Approve & request ride help'
        : 'Approve & Publish';
    }
    return isCollisionSpecialistRidePreset ? 'Request ride help' : 'Publish';
  }, [
    isSubmitting,
    communityTaskSelected,
    escrowRequiredJGK,
    isCollisionSpecialistRidePreset,
    isRideHelpFlow,
  ]);

  useEffect(() => {
    if (
      !open ||
      step !== 2 ||
      !isTask ||
      communityTaskSelected ||
      !hasPaidTaskAmount ||
      !pickupCoordsValid ||
      !selectedMeta?.taskType
    ) {
      setSupplyProof(null);
      setSupplyProofLoading(false);
      return undefined;
    }

    let cancelled = false;
    setSupplyProofLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axiosConn.post(
          'local-ops/tasks/fanout-summary',
          {
            taskType: selectedMeta.taskType,
            originLocation: {
              type: 'Point',
              coordinates: [location.lon, location.lat],
            },
            trustRequirement:
              canShowRequireVerifiedLocal && requireVerifiedLocal
                ? 'verified_local'
                : 'open',
            radiusMeters: radiusMeters || undefined,
          },
        );
        if (cancelled) return;
        const proof =
          response?.data?.data?.supplyProof ||
          response?.data?.supplyProof ||
          null;
        setSupplyProof(proof || GENERIC_SUPPLY_PROOF);
      } catch (err) {
        if (!cancelled) {
          setSupplyProof(GENERIC_SUPPLY_PROOF);
        }
      } finally {
        if (!cancelled) setSupplyProofLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    open,
    step,
    isTask,
    hasPaidTaskAmount,
    communityTaskSelected,
    pickupCoordsValid,
    selectedMeta?.taskType,
    location,
    radiusMeters,
    canShowRequireVerifiedLocal,
    requireVerifiedLocal,
  ]);

  const showDescriptionHistory =
    descriptionSuggestions.length > 0 &&
    (focusedField === 'description' || !description.trim());

  const clearRouteSuggestedAmount = useCallback(() => {
    if (!hasRouteSuggestedAmountFlow) return;
    deliveryPriceGeocodeRef.current = '';
    rideAutoPriceRef.current = '';
    rideHelpPriceKeyRef.current = '';
    rideHelpAutoPriceRef.current = '';
    rideHelpHasAutoPricedRef.current = false;
    guidedRoutePriceKeyRef.current = '';
    guidedRouteAutoPriceRef.current = '';
    guidedRouteHasAutoPricedRef.current = false;
    setRideSuggestedPrice(null);
    setRideSuggestedDistanceMi(null);
    setIsRidePriceGeocoding(false);
    setPrice('');
  }, [hasRouteSuggestedAmountFlow]);

  const handlePickupAddressChange = useCallback(
    (value) => {
      emitCreateFieldFirstInteraction(
        isGuidedOfferingFlow ? 'service_area' : 'pickup',
      );
      const nextValue = String(value || '');
      const trimmedValue = nextValue.trim();
      if (trimmedValue) {
        const searchKey = `journey:location_search:${
          isGuidedOfferingFlow ? 'service_area' : 'pickup'
        }`;
        if (!createFunnelLocalDedupeRef.current.has(searchKey)) {
          createFunnelLocalDedupeRef.current.add(searchKey);
          emitCreateJourneyEvent(
            LOCALOPS_FUNNEL_EVENTS.CREATE_LOCATION_SEARCH_STARTED,
            {
              currentStep: 'details',
              field: isGuidedOfferingFlow ? 'service_area' : 'pickup',
              locationMethod: 'typed_search',
            },
          );
        }
      }
      const shouldClearSuggestedAmount =
        hasRouteSuggestedAmountFlow &&
        (!trimmedValue || pickupGeocodeRef.current !== trimmedValue);
      originCandidateRequestRef.current += 1;
      setPickupAddress(nextValue);
      setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
      setPinHelper('');
      setPinError('');
      if (shouldClearSuggestedAmount) {
        clearRouteSuggestedAmount();
      }
      if (!trimmedValue) {
        pickupGeocodeRef.current = '';
        if (pinSource !== 'manual') {
          setLocation(null);
          setLocationLabel('');
          addressKeyRef.current = null;
          setPinSource('default');
        }
        setPinNotice('');
        return;
      }
      const addressChangedFromConfirmedPin =
        pickupGeocodeRef.current &&
        pickupGeocodeRef.current !== trimmedValue;
      if (addressChangedFromConfirmedPin) {
        pickupGeocodeRef.current = '';
        setPinNotice('');
        if (pinSource === 'pickup') {
          setPinSource('default');
        }
        emitCreateJourneyEvent(
          LOCALOPS_FUNNEL_EVENTS.CREATE_LOCATION_CONFIRMATION_CLEARED,
          {
            currentStep: 'details',
            field: isGuidedOfferingFlow ? 'service_area' : 'pickup',
            reasonCodes: ['location_cleared'],
          },
        );
      }
      if (
        pinSource !== 'manual' &&
        (!pickupGeocodeRef.current || pickupGeocodeRef.current !== trimmedValue)
      ) {
        setLocation(null);
        setLocationLabel('');
        addressKeyRef.current = null;
      }
    },
    [
      clearRouteSuggestedAmount,
      emitCreateFieldFirstInteraction,
      emitCreateJourneyEvent,
      hasRouteSuggestedAmountFlow,
      isGuidedOfferingFlow,
      pinSource,
    ],
  );

  const handleSelectOriginCandidate = useCallback(
    (candidate) => {
      const coords = candidateCoordinates(candidate);
      const nextAddress = candidateDisplayText(candidate);
      if (!coords || !nextAddress) {
        setPinError('Select a local address with a confirmed location.');
        return;
      }
      setPickupAddress(nextAddress);
      setLocation(coords);
      setPinSource('pickup');
      pickupGeocodeRef.current = nextAddress;
      addressKeyRef.current = `${coords.lat.toFixed(4)}|${coords.lon.toFixed(4)}`;
      setLocationLabel(nextAddress);
      setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
      setPinNotice('Location confirmed.');
      setPinError('');
      setPinHelper('');
      emitCreateJourneyEvent(
        LOCALOPS_FUNNEL_EVENTS.CREATE_LOCATION_CANDIDATE_SELECTED,
        {
          currentStep: 'details',
          field: isGuidedOfferingFlow ? 'service_area' : 'pickup',
          locationMethod: 'candidate_select',
        },
      );
      emitCreateFunnelEvent(
        LOCALOPS_FUNNEL_EVENTS.CREATE_LOCATION_CONFIRMED,
        {
          field: isGuidedOfferingFlow ? 'serviceArea' : 'pickup',
        },
        {
          oncePerSheet: true,
          oncePerSession: true,
          dedupeKey: `location_confirmed:${
            isGuidedOfferingFlow ? 'serviceArea' : 'pickup'
          }`,
        },
      );
    },
    [emitCreateFunnelEvent, emitCreateJourneyEvent, isGuidedOfferingFlow],
  );

  const handleDestinationAddressChange = useCallback(
    (value) => {
      emitCreateFieldFirstInteraction(
        isDelivery && !isAssistance ? 'dropoff' : 'destination',
      );
      const nextValue = String(value || '');
      const trimmedValue = nextValue.trim();
      if (trimmedValue) {
        const field = isDelivery && !isAssistance ? 'dropoff' : 'destination';
        const searchKey = `journey:location_search:${field}`;
        if (!createFunnelLocalDedupeRef.current.has(searchKey)) {
          createFunnelLocalDedupeRef.current.add(searchKey);
          emitCreateJourneyEvent(
            LOCALOPS_FUNNEL_EVENTS.CREATE_LOCATION_SEARCH_STARTED,
            {
              currentStep: 'details',
              field,
              locationMethod: 'typed_search',
            },
          );
        }
      }
      const shouldClearSuggestedAmount =
        hasRouteSuggestedAmountFlow &&
        (!trimmedValue || destinationGeocodeRef.current !== trimmedValue);
      setDestinationAddress(nextValue);
      if (usesResolverDropoffAddress) {
        setDeliveryAddress('');
      }
      if (!usesDestinationAddress) return;
      destinationCandidateRequestRef.current += 1;
      setDestinationResolution(EMPTY_ORIGIN_RESOLUTION);
      setDestinationPinNotice('');
      setDestinationPinError('');
      if (shouldClearSuggestedAmount) {
        clearRouteSuggestedAmount();
      }
      if (!trimmedValue) {
        setDestination(null);
        destinationGeocodeRef.current = '';
        return;
      }
      const destinationChangedFromConfirmed =
        destinationGeocodeRef.current &&
        destinationGeocodeRef.current !== trimmedValue;
      if (destinationChangedFromConfirmed) {
        setDestination(null);
        destinationGeocodeRef.current = '';
        emitCreateJourneyEvent(
          LOCALOPS_FUNNEL_EVENTS.CREATE_LOCATION_CONFIRMATION_CLEARED,
          {
            currentStep: 'details',
            field: isDelivery && !isAssistance ? 'dropoff' : 'destination',
            reasonCodes: ['location_cleared'],
          },
        );
      } else if (!destinationGeocodeRef.current) {
        setDestination(null);
      }
    },
    [
      clearRouteSuggestedAmount,
      emitCreateFieldFirstInteraction,
      emitCreateJourneyEvent,
      hasRouteSuggestedAmountFlow,
      isAssistance,
      isDelivery,
      usesDestinationAddress,
      usesResolverDropoffAddress,
    ],
  );

  const handleSelectDestinationCandidate = useCallback(
    (candidate) => {
      const coords = candidateCoordinates(candidate);
      const nextAddress = candidateDisplayText(candidate);
      if (!coords || !nextAddress) {
        setDestinationPinError(destinationConfirmationRequiredMessage);
        return;
      }
      setDestinationAddress(nextAddress);
      if (usesResolverDropoffAddress) {
        setDeliveryAddress(nextAddress);
      }
      setDestination({ lat: coords.lat, lon: coords.lon });
      destinationGeocodeRef.current = nextAddress;
      setDestinationResolution(EMPTY_ORIGIN_RESOLUTION);
      setDestinationPinNotice(destinationConfirmedNotice);
      setDestinationPinError('');
      const field = isDelivery && !isAssistance ? 'delivery' : 'destination';
      emitCreateJourneyEvent(
        LOCALOPS_FUNNEL_EVENTS.CREATE_LOCATION_CANDIDATE_SELECTED,
        {
          currentStep: 'details',
          field,
          locationMethod: 'candidate_select',
        },
      );
      emitCreateFunnelEvent(
        LOCALOPS_FUNNEL_EVENTS.CREATE_LOCATION_CONFIRMED,
        {
          field,
        },
        {
          oncePerSheet: true,
          oncePerSession: true,
          dedupeKey: `location_confirmed:${field}`,
        },
      );
    },
    [
      destinationConfirmationRequiredMessage,
      destinationConfirmedNotice,
      emitCreateFunnelEvent,
      emitCreateJourneyEvent,
      isAssistance,
      isDelivery,
      usesResolverDropoffAddress,
    ],
  );

  const handlePriceInputChange = useCallback(
    (value) => {
      emitCreateFieldFirstInteraction('amount');
      if (isRideHelpStarterPreset) {
        const nextCents = normalizeDollarCents(value);
        const autoCents = normalizeDollarCents(rideHelpAutoPriceRef.current);
        if (autoCents !== null && nextCents !== null && nextCents !== autoCents) {
          rideHelpAutoPriceRef.current = '';
        }
      }
      if (isGuidedRouteSuggestedFlow) {
        const nextCents = normalizeDollarCents(value);
        const autoCents = normalizeDollarCents(guidedRouteAutoPriceRef.current);
        if (autoCents !== null && nextCents !== null && nextCents !== autoCents) {
          guidedRouteAutoPriceRef.current = '';
        }
      }
      setPrice(value);
    },
    [
      emitCreateFieldFirstInteraction,
      isRideHelpStarterPreset,
      isGuidedRouteSuggestedFlow,
    ],
  );

  useEffect(() => {
    if (
      !open ||
      !isCollisionSpecialistRidePreset ||
      !pickupAddressValue ||
      pickupPinMatches ||
      pinSource === 'manual'
    ) {
      return undefined;
    }
    let active = true;
    setIsPickupGeocoding(true);
    setPinHelper('Confirming preset pickup location...');
    setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
    getCoordinatesFromAddress(pickupAddressValue)
      .then((coords) => {
        if (!active) return;
        if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lon)) {
          throw new Error('No results');
        }
        const nextLocation = { lat: coords.lat, lon: coords.lon };
        setLocation(nextLocation);
        setPinSource('pickup');
        pickupGeocodeRef.current = pickupAddressValue;
        addressKeyRef.current = `${nextLocation.lat.toFixed(4)}|${nextLocation.lon.toFixed(4)}`;
        setLocationLabel(pickupAddressValue);
        setPinNotice('Location confirmed.');
        setPinError('');
        setPinHelper('');
      })
      .catch(() => {
        if (!active) return;
        setPinHelper('');
        setPinError("Couldn't confirm the preset pickup location.");
      })
      .finally(() => {
        if (active) setIsPickupGeocoding(false);
      });
    return () => {
      active = false;
    };
  }, [
    open,
    isCollisionSpecialistRidePreset,
    pickupAddressValue,
    pickupPinMatches,
    pinSource,
  ]);

  // Resolve typed origin candidates without silently accepting the first result.
  useEffect(() => {
    if (!open || (!isTask && !isOffering)) {
      setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
      setIsPickupGeocoding(false);
      return undefined;
    }
    if (isCollisionSpecialistRidePreset) {
      setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
      return undefined;
    }
    if (!pickupAddressValue) {
      setPinHelper('');
      setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
      setIsPickupGeocoding(false);
      return undefined;
    }
    if (originUsesExplicitCoordinateSource || pickupPinMatches) {
      setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
      setIsPickupGeocoding(false);
      return undefined;
    }
    const current = pickupAddressValue;
    const requestId = originCandidateRequestRef.current + 1;
    originCandidateRequestRef.current = requestId;
    let active = true;
    setIsPickupGeocoding(true);
    setPinHelper('Finding local address matches...');
    setOriginResolution({
      candidates: [],
      status: 'resolving',
      message: 'Finding local address matches...',
    });
    const timer = setTimeout(async () => {
      try {
        const candidates = await resolveLocalOpsPlaceCandidates(current, {
          regionSlug: normalizedLocalOpsRegionSlug,
        });
        if (!active || originCandidateRequestRef.current !== requestId) return;
        const confirmableCandidates = (Array.isArray(candidates) ? candidates : [])
          .filter((candidate) => candidateDisplayText(candidate))
          .filter((candidate) => candidateCoordinates(candidate));
        setOriginResolution({
          candidates: confirmableCandidates,
          status: confirmableCandidates.length ? 'candidates' : 'unavailable',
          message: confirmableCandidates.length
            ? LOCALOPS_ORIGIN_CONFIRMATION_REQUIRED_MESSAGE
            : 'No confident local match. Add city/state or use your location.',
        });
        setPinHelper('');
      } catch (err) {
        if (!active || originCandidateRequestRef.current !== requestId) return;
        setOriginResolution({
          candidates: [],
          status: 'error',
          message: 'No confident local match. Add city/state or use your location.',
        });
        setPinHelper('');
      } finally {
        if (active && originCandidateRequestRef.current === requestId) {
          setIsPickupGeocoding(false);
        }
      }
    }, 400);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [
    open,
    pickupAddressValue,
    isTask,
    isOffering,
    originUsesExplicitCoordinateSource,
    pickupPinMatches,
    isCollisionSpecialistRidePreset,
    normalizedLocalOpsRegionSlug,
  ]);

  useEffect(() => {
    if (!open || !usesDestinationAddress) {
      setDestinationResolution(EMPTY_ORIGIN_RESOLUTION);
      return undefined;
    }
    if (!destinationAddressValue) {
      destinationCandidateRequestRef.current += 1;
      setDestinationResolution(EMPTY_ORIGIN_RESOLUTION);
      setIsDestinationGeocoding(false);
      setDestinationPinNotice('');
      setDestinationPinError('');
      return undefined;
    }
    if (assistanceDestinationConfirmed) {
      setDestinationResolution(EMPTY_ORIGIN_RESOLUTION);
      setIsDestinationGeocoding(false);
      return undefined;
    }
    const current = destinationAddressValue;
    const requestId = destinationCandidateRequestRef.current + 1;
    destinationCandidateRequestRef.current = requestId;
    let active = true;
    setIsDestinationGeocoding(true);
    setDestinationPinNotice('');
    setDestinationPinError('');
    setDestinationResolution({
      candidates: [],
      status: 'resolving',
      message: 'Finding local destination matches...',
    });
    const timer = setTimeout(async () => {
      try {
        const candidates = await resolveLocalOpsPlaceCandidates(current, {
          regionSlug: normalizedLocalOpsRegionSlug,
        });
        if (!active || destinationCandidateRequestRef.current !== requestId) return;
        const confirmableCandidates = (Array.isArray(candidates) ? candidates : [])
          .filter((candidate) => candidateDisplayText(candidate))
          .filter((candidate) => candidateCoordinates(candidate));
        setDestinationResolution({
          candidates: confirmableCandidates,
          status: confirmableCandidates.length ? 'candidates' : 'unavailable',
          message: confirmableCandidates.length
            ? destinationConfirmationRequiredMessage
            : LOCALOPS_RIDE_HELP_DESTINATION_RETRY_MESSAGE,
        });
      } catch (err) {
        if (!active || destinationCandidateRequestRef.current !== requestId) return;
        setDestinationResolution({
          candidates: [],
          status: 'error',
          message: LOCALOPS_RIDE_HELP_DESTINATION_RETRY_MESSAGE,
        });
      } finally {
        if (active && destinationCandidateRequestRef.current === requestId) {
          setIsDestinationGeocoding(false);
        }
      }
    }, 400);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [
    open,
    usesDestinationAddress,
    destinationAddressValue,
    assistanceDestinationConfirmed,
    destinationConfirmationRequiredMessage,
    normalizedLocalOpsRegionSlug,
  ]);
  const previewData = useMemo(() => {
    const baseTitle =
      (isRideHelpFlow || isGuidedTaskFlow || isGuidedOfferingFlow
        ? title.trim() || createFlowBlueprint.systemTitle
        : title.trim()) ||
      (isOffering ? 'New offering' : 'New task');
    const typeLabel =
      selectedMeta?.label ||
      (isOffering ? 'Offering' : 'Task');
    // Keep preview honest: offerings require category, so avoid masking missing input.
    const categoryLabel = isRideHelpFlow || isGuidedTaskFlow
      ? resolvedTaskCategory || createFlowBlueprint.category
      : isCollisionSpecialistRidePreset
      ? 'Ride help'
      : category.trim() || (isOffering ? 'Category required' : typeLabel);
    let priceLabel = '';
    if (communityTaskSelected) {
      priceLabel = 'No cash payout';
    } else if (price === '' || !Number.isFinite(parsedPrice)) {
      priceLabel = isOffering ? 'Price required' : 'Amount required';
    } else if (
      isTask &&
      parsedPrice < LOCALOPS_MIN_TASK_PRICE_USD
    ) {
      priceLabel = 'Amount required';
    } else if (isOffering && Number(price) === 0) {
      priceLabel = 'Free';
    } else {
      priceLabel = `$${price}`;
    }
    let quantityLabel = '';
    if (isOffering) {
      const qtyValue = Number(quantityAvailable);
      quantityLabel =
        Number.isInteger(qtyValue) && qtyValue > 0
          ? `${qtyValue} available`
          : 'Quantity required';
    }
    return {
      title: baseTitle,
      description: isTask
        ? compactLocalOpsPreviewText(
            isRideHelpFlow
              ? rideHelpGeneratedDescription
              : isGuidedTaskFlow
              ? guidedTaskGeneratedDescription
              : description,
          )
        : description.trim(),
      category: categoryLabel,
      priceLabel,
      quantityLabel,
      location: isRideHelpFlow
        ? 'Exact route shared after accept & start'
        : isGuidedTaskFlow
        ? createFlowBlueprint.previewLocation
        : isGuidedOfferingFlow
        ? createFlowBlueprint.previewLocation
        : locationLabel || 'Exact address shared after accept',
      endMode: availabilityEndMode === 'specific'
        ? null
        : labelForEndMode(availabilityEndMode),
      media: mediaItems.slice(0, 3),
      showMediaPlaceholders:
        !isRideHelpFlow && !isGuidedTaskFlow && !isGuidedOfferingFlow,
    };
  }, [
    title,
    description,
    isRideHelpFlow,
    isGuidedTaskFlow,
    isGuidedOfferingFlow,
    createFlowBlueprint,
    resolvedTaskCategory,
    rideHelpGeneratedDescription,
    guidedTaskGeneratedDescription,
    isOffering,
    isTask,
    communityTaskSelected,
    selectedMeta,
    isCollisionSpecialistRidePreset,
    category,
    price,
    parsedPrice,
    quantityAvailable,
    locationLabel,
    availabilityEndMode,
    mediaItems,
  ]);

  const presenceReady = useMemo(() => {
    if (!LOCALOPS_PRESENCE_GATING_ENABLED) return true;
    if (!presenceSelf) return true;
    const presenceActive =
      typeof presenceSelf.presenceActive === 'boolean'
        ? presenceSelf.presenceActive
        : true;
    const presenceStatus =
      presenceSelf.presenceStatus || presenceSelf.status || 'online';
    return presenceActive && presenceStatus !== 'offline';
  }, [presenceSelf]);

  const clearMediaItems = useCallback(() => {
    setMediaItems((prev) => {
      prev.forEach((item) => {
        if (item.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(item.preview);
        }
      });
      return [];
    });
    setCoverIndex(0);
  }, []);

  const resetForm = useCallback(
    ({
      nextType = null,
      nextStep = 0,
      keepLocation = false,
      typeGroupHint = null,
    } = {}) => {
      const normalizedTypeGroupHint =
        normalizeCreateLaunchGroupHint(typeGroupHint);
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[DEBUG][RESET FORM]', {
          nextType,
          nextStep,
          keepLocation,
          typeGroupHint: normalizedTypeGroupHint,
          selectedType,
          step,
        });
      }
      const fallbackType =
        nextType ||
        (normalizedTypeGroupHint ? null : selectedType || lastTypeRef.current) ||
        null;
      const mode = resolveModeFromType(fallbackType);
      const historyBucket = mode ? inputHistory[mode] || {} : {};
      const restoredDraft = readCreateDraft(getCreateDraftKey(draftPlayerId, mode));
      const restoredTitle = restoredDraft
        ? String(restoredDraft.title || '')
        : pickLastHistoryValue(historyBucket.title);
      const restoredDescription = restoredDraft
        ? String(restoredDraft.description || '')
        : pickLastHistoryValue(historyBucket.description);
      const shouldRestoreLocationFields = !keepLocation || !!restoredDraft;
      const restoredPickupAddress = restoredDraft
        ? String(restoredDraft.pickupAddress || '')
        : mode === 'task'
        ? pickLastHistoryValue(historyBucket.pickupAddress)
        : '';
      setTitle(restoredTitle);
      setDescription(restoredDescription);
      setDescriptionAuto(
        shouldAutoSyncRestoredDescription(restoredTitle, restoredDescription),
      );
      if (shouldRestoreLocationFields) {
        setPickupAddress(restoredPickupAddress);
        setDeliveryAddress(
          restoredDraft
            ? String(restoredDraft.deliveryAddress || '')
            : mode === 'task'
            ? pickLastHistoryValue(historyBucket.deliveryAddress)
            : '',
        );
        setDestinationAddress(
          restoredDraft
            ? String(
                restoredDraft.destinationAddress ||
                  restoredDraft.deliveryAddress ||
                  '',
              )
            : mode === 'task'
            ? pickLastHistoryValue(historyBucket.deliveryAddress)
            : '',
        );
      }
      setCategory(
        restoredDraft
          ? String(restoredDraft.category || '')
          : pickLastHistoryValue(historyBucket.category),
      );
      setPrice(
        restoredDraft
          ? String(restoredDraft.price || '')
          : pickLastHistoryValue(historyBucket.price),
      );
      if (mode === 'offering') {
        setQuantityAvailable(
          restoredDraft
            ? String(restoredDraft.quantityAvailable || '1')
            : pickLastHistoryValue(historyBucket.quantity) || '1',
        );
      } else {
        setQuantityAvailable('1');
      }
      setTaskPaymentMode(
        restoredDraft?.taskPaymentMode === LOCALOPS_TASK_PAYMENT_MODES.COMMUNITY
          ? LOCALOPS_TASK_PAYMENT_MODES.COMMUNITY
          : LOCALOPS_TASK_PAYMENT_MODES.PAID,
      );
      setRequestQuote(false);
      setDeliverySupported(
        typeof restoredDraft?.deliverySupported === 'boolean'
          ? restoredDraft.deliverySupported
          : getDefaultDeliverySupportedForType(fallbackType),
      );
      if (shouldRestoreLocationFields) {
        if (
          restoredDraft?.destination &&
          Number.isFinite(restoredDraft.destination.lat) &&
          Number.isFinite(restoredDraft.destination.lon)
        ) {
          setDestination({ ...restoredDraft.destination });
        } else {
          setDestination(null);
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[DEBUG][DEST RESET]', {
            reason: 'resetForm',
            step,
            destinationAddressValue,
          });
        }
      }
      setVisibility('nearby');
      visibilityTouchedRef.current = false;
      setTrustedPlayers([]);
      setRequireVerifiedLocal(false);
      setFirstCompletionAccessRequested(false);
      setLiveTrackingEnabled(true);
      if (!keepLocation) {
        setPinSource('default');
        setPinNotice('');
        setPinError('');
        setDestinationPinNotice('');
        setDestinationPinError('');
        setIsPickupGeocoding(false);
        setIsDestinationGeocoding(false);
        pickupGeocodeRef.current = '';
        originCandidateRequestRef.current += 1;
        setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
        destinationCandidateRequestRef.current += 1;
        setDestinationResolution(EMPTY_ORIGIN_RESOLUTION);
        destinationGeocodeRef.current = '';
        deliveryPriceGeocodeRef.current = '';
        rideAutoPriceRef.current = '';
        rideHelpPriceKeyRef.current = '';
        rideHelpAutoPriceRef.current = '';
        rideHelpHasAutoPricedRef.current = false;
        guidedRoutePriceKeyRef.current = '';
        guidedRouteAutoPriceRef.current = '';
        guidedRouteHasAutoPricedRef.current = false;
      }
      setRideSuggestedPrice(null);
      setRideSuggestedDistanceMi(null);
      setIsRidePriceGeocoding(false);
      setError('');
      setUploadPct(0);
      setIsSubmitting(false);
      setShowInfo(false);
      setShowAdvanced(false);
      setActiveTypeInfo(null);
      setFocusedField(null);
      setSearchTerm('');
      setAvailabilityEndMode(restoredDraft?.availabilityEndMode || 'ongoing');
      const nextSchedule = getDefaultSchedule();
      setAvailabilityStart(restoredDraft?.availabilityStart || nextSchedule.start);
      setAvailabilityEnd(restoredDraft?.availabilityEnd || nextSchedule.end);
      setCustomStart(restoredDraft?.customStart === true);
      if (shouldRestoreLocationFields) {
        addressKeyRef.current = null;
        setLocationLabel('');
        setIsResolvingLocation(false);
        if (
          restoredDraft?.location &&
          Number.isFinite(restoredDraft.location.lat) &&
          Number.isFinite(restoredDraft.location.lon)
        ) {
          setLocation({ ...restoredDraft.location });
          if (restoredPickupAddress) {
            pickupGeocodeRef.current = restoredPickupAddress;
          }
        } else {
          setLocation(defaultCenter ? { ...defaultCenter } : null);
        }
      }
      if (restoredDraft?.pinSource) setPinSource(restoredDraft.pinSource);
      if (restoredDraft?.visibility) setVisibility(restoredDraft.visibility);
      if (Array.isArray(restoredDraft?.trustedPlayers)) {
        setTrustedPlayers(restoredDraft.trustedPlayers);
      }
      clearMediaItems();
      setSelectedType(fallbackType);
      if (fallbackType && typeof window !== 'undefined') {
        lastTypeRef.current = fallbackType;
        try {
          window.localStorage.setItem(LAST_TYPE_KEY, fallbackType);
        } catch (err) {
          // ignore storage failures
        }
      }
      setStep(nextStep);
    },
    [clearMediaItems, defaultCenter, draftPlayerId, inputHistory, selectedType],
  );

  const handleSelectType = useCallback(
    (typeId) => {
      const typeMeta = TYPE_OPTIONS.find((opt) => opt.id === typeId);
      emitCreateFunnelEvent(
        LOCALOPS_FUNNEL_EVENTS.CREATE_TASK_TYPE_SELECTED,
        {
          taskType:
            typeMeta?.taskType ||
            typeMeta?.offeringType ||
            typeMeta?.mode ||
            undefined,
          selectedType: typeId,
          starterId: isRideHelpCreateTypeId(typeId)
            ? LOCALOPS_RIDE_HELP_STARTER_ID
            : effectivePreset?.starterId || typeId,
        },
        {
          oncePerSheet: true,
          oncePerSession: true,
          dedupeKey: `type_selected:${typeId}`,
        },
      );
      if (!preset && isRideHelpCreateTypeId(typeId)) {
        presetApplySignatureRef.current = '';
      }
      resetForm({ nextType: typeId, nextStep: 1, keepLocation: true });
      lastTypeRef.current = typeId;
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(LAST_TYPE_KEY, typeId);
        } catch (err) {
          // ignore storage failures
        }
      }
    },
    [emitCreateFunnelEvent, effectivePreset?.starterId, preset, resetForm],
  );

  useEffect(() => {
    if (!open || !launchId) return;
    setAnimateFromFab(true);
    const timer = setTimeout(() => setAnimateFromFab(false), 420);
    return () => clearTimeout(timer);
  }, [open, launchId]);

  useEffect(() => {
    if (!open) {
      createFunnelSessionRef.current = '';
      createJourneySessionRef.current = '';
      createFunnelLocalDedupeRef.current = new Set();
      createJourneyUnavailableSignatureRef.current = '';
      createJourneyTouchedFieldsRef.current = new Set();
      createJourneyClosedRef.current = false;
      return;
    }
    emitCreateFunnelEvent(
      LOCALOPS_FUNNEL_EVENTS.CREATE_SHEET_OPENED,
      {
        actionContext: normalizedLaunchGroupHint || 'open',
      },
      {
        oncePerSheet: true,
        oncePerSession: true,
        dedupeKey: 'sheet_opened',
      },
    );
  }, [emitCreateFunnelEvent, normalizedLaunchGroupHint, open]);

  useEffect(() => {
    if (!open) {
      openInitRef.current = false;
      presetApplySignatureRef.current = '';
      return;
    }
    if (openInitRef.current) return;
    openInitRef.current = true;
    const nextType = effectivePreset?.type || launchTypeHint || null;
    const nextGroupHint = normalizedLaunchGroupHint;
    const nextStep = effectivePreset?.type
      ? 1
      : Number.isInteger(launchStepHint)
      ? launchStepHint
      : nextType
      ? 1
      : 0;
    resetForm({ nextType, nextStep, typeGroupHint: nextGroupHint });
    if (nextType) {
      const typeMeta = TYPE_OPTIONS.find((opt) => opt.id === nextType);
      emitCreateFunnelEvent(
        LOCALOPS_FUNNEL_EVENTS.CREATE_TASK_TYPE_SELECTED,
        {
          taskType:
            typeMeta?.taskType ||
            typeMeta?.offeringType ||
            typeMeta?.mode ||
            undefined,
          selectedType: nextType,
          starterId: effectivePreset?.starterId || nextType,
        },
        {
          oncePerSheet: true,
          oncePerSession: true,
          dedupeKey: `type_selected:${nextType}`,
        },
      );
    }
  }, [
    emitCreateFunnelEvent,
    open,
    effectivePreset,
    launchTypeHint,
    launchStepHint,
    normalizedLaunchGroupHint,
    resetForm,
  ]);

  // Draft persistence (debounced)
  useEffect(() => {
    if (!draftKey) return undefined;
    const timer = setTimeout(() => {
      try {
        const payload = {
          title,
          description,
          pickupAddress,
          deliveryAddress: usesResolverDropoffAddress
            ? taskDeliveryAddressValue
            : deliveryAddress,
          destinationAddress,
          category,
          price,
          taskPaymentMode,
          quantityAvailable,
          location,
          pinSource,
          availabilityStart,
          availabilityEnd,
          availabilityEndMode,
          customStart,
          deliverySupported,
          visibility,
          trustedPlayers,
        };
        window.localStorage.setItem(draftKey, JSON.stringify(payload));
      } catch (err) {
        // ignore draft failures
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [
    draftKey,
    title,
    description,
    pickupAddress,
    deliveryAddress,
    taskDeliveryAddressValue,
    usesResolverDropoffAddress,
    destinationAddress,
    category,
    price,
    taskPaymentMode,
    quantityAvailable,
    location,
    pinSource,
    availabilityStart,
    availabilityEnd,
    availabilityEndMode,
    customStart,
    deliverySupported,
    visibility,
    trustedPlayers,
  ]);

  useEffect(() => {
    if (!open || !effectivePreset?.type) return;
    const presetIsRideHelpStarter =
      isRideHelpStarterPresetValue(effectivePreset);
    const presetIsGuidedTaskStarter =
      isGuidedTaskStarterPresetValue(effectivePreset);
    const presetApplySignature =
      buildCreatePresetApplySignature(effectivePreset);
    if (presetApplySignatureRef.current === presetApplySignature) return;
    presetApplySignatureRef.current = presetApplySignature;
    originCandidateRequestRef.current += 1;
    setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
    destinationCandidateRequestRef.current += 1;
    setDestinationResolution(EMPTY_ORIGIN_RESOLUTION);
    rideHelpPriceKeyRef.current = '';
    rideHelpAutoPriceRef.current = '';
    setSelectedType(effectivePreset.type);
    setStep(1);
    emitCreateFunnelEvent(
      LOCALOPS_FUNNEL_EVENTS.CREATE_TASK_TYPE_SELECTED,
      {
        selectedType: effectivePreset.type,
        starterId: effectivePreset.starterId || effectivePreset.type,
      },
      {
        oncePerSheet: true,
        oncePerSession: true,
        dedupeKey: `type_selected:${effectivePreset.type}`,
      },
    );
    if (presetIsRideHelpStarter) {
      rideHelpHasAutoPricedRef.current = false;
    }
    if (effectivePreset.title) setTitle(effectivePreset.title);
    if (presetIsRideHelpStarter) {
      setDescription('');
      setDescriptionAuto(false);
    } else if (presetIsGuidedTaskStarter) {
      setDescription('');
      setDescriptionAuto(false);
    } else if (effectivePreset.description) {
      setDescription(effectivePreset.description);
      setDescriptionAuto(false);
    }
    if (effectivePreset.category) setCategory(effectivePreset.category);
    if (presetIsRideHelpStarter) {
      setPickupAddress('');
      setDeliveryAddress('');
      setDestinationAddress('');
      setDestination(null);
      setLocation(defaultCenter ? { ...defaultCenter } : null);
      setPinSource('default');
      addressKeyRef.current = null;
      pickupGeocodeRef.current = '';
      setVisibility('nearby');
      visibilityTouchedRef.current = false;
      setTrustedPlayers([]);
      setDestinationPinNotice('');
      setDestinationPinError('');
      destinationGeocodeRef.current = '';
      if (effectivePreset.price === undefined || effectivePreset.price === null) {
        setPrice('');
      }
    } else {
      if (effectivePreset.pickupAddress) setPickupAddress(effectivePreset.pickupAddress);
      if (effectivePreset.deliveryAddress) {
        setDeliveryAddress(effectivePreset.deliveryAddress);
        setDestinationAddress(effectivePreset.deliveryAddress);
      }
      if (effectivePreset.destinationAddress) setDestinationAddress(effectivePreset.destinationAddress);
    }
    if (effectivePreset.price !== undefined && effectivePreset.price !== null) {
      setPrice(String(effectivePreset.price));
      setRequestQuote(false);
    }
    if (effectivePreset.paymentMode === LOCALOPS_TASK_PAYMENT_MODES.COMMUNITY) {
      setTaskPaymentMode(LOCALOPS_TASK_PAYMENT_MODES.COMMUNITY);
    }
    if (effectivePreset.quantityAvailable !== undefined && effectivePreset.quantityAvailable !== null) {
      setQuantityAvailable(String(effectivePreset.quantityAvailable));
    }
    if (effectivePreset.deliverySupported !== undefined) {
      setDeliverySupported(effectivePreset.deliverySupported === true);
    }
    if (Array.isArray(effectivePreset.media)) {
      const nextMediaItems = buildCreatePresetMediaItems(effectivePreset.media);
      setMediaItems(nextMediaItems);
      setCoverIndex(clampCoverIndex(effectivePreset.coverIndex, nextMediaItems.length));
    }
    if (effectivePreset.rideTemplate === COLLISION_SPECIALIST_RIDE_TEMPLATE) {
      setRequestQuote(false);
    }
    if (effectivePreset.origin) {
      setLocation({ ...effectivePreset.origin });
      if (effectivePreset.pickupAddress) {
        pickupGeocodeRef.current = effectivePreset.pickupAddress;
        setLocationLabel(effectivePreset.pickupAddress);
      }
      const lat = Number(effectivePreset.origin.lat);
      const lon = Number(effectivePreset.origin.lon);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        addressKeyRef.current = `${lat.toFixed(4)}|${lon.toFixed(4)}`;
      }
    }
    if (!presetIsRideHelpStarter && effectivePreset.destination && effectivePreset.type === 'task_assistance') {
      setDestination({ ...effectivePreset.destination });
      if (effectivePreset.destinationAddress) {
        setDestinationAddress(effectivePreset.destinationAddress);
        destinationGeocodeRef.current = String(effectivePreset.destinationAddress || '').trim();
      }
    }
  }, [open, effectivePreset, defaultCenter, emitCreateFunnelEvent]);

  useEffect(() => {
    if (!open || !isRideHelpFlow || visibility !== 'trusted') return;
    const hasTrustedOptions =
      Array.isArray(friendOptions) && friendOptions.length > 0;
    if (!visibilityTouchedRef.current || !hasTrustedOptions) {
      setVisibility('nearby');
      setTrustedPlayers([]);
    }
  }, [open, isRideHelpFlow, visibility, friendOptions]);

  useEffect(() => {
    if (!open || !defaultCenter) return;
    setLocation((prev) => prev || { ...defaultCenter });
  }, [open, defaultCenter]);

  useEffect(() => {
    if (!open || !location) return;
    if (!Number.isFinite(location.lat) || !Number.isFinite(location.lon)) {
      setLocationLabel('');
      return;
    }
    const key = `${location.lat.toFixed(4)}|${location.lon.toFixed(4)}`;
    if (addressKeyRef.current === key) return;
    addressKeyRef.current = key;
    let active = true;
    setIsResolvingLocation(true);
    getAddressFromCoordinates(location.lat, location.lon)
      .then((address) => {
        if (!active) return;
        const label =
          formatCoarseLocationLabel(address) ||
          `${location.lat.toFixed(3)}, ${location.lon.toFixed(3)}`;
        setLocationLabel(label);
      })
      .catch(() => {
        if (!active) return;
        setLocationLabel(
          `${location.lat.toFixed(3)}, ${location.lon.toFixed(3)}`,
        );
      })
      .finally(() => {
        if (!active) return;
        setIsResolvingLocation(false);
      });
    return () => {
      active = false;
    };
  }, [open, location]);

  // Keep default destination geocoding behavior unchanged. Resolver-backed drop-offs use explicit candidate confirmation.
  useEffect(() => {
    if (!open || isRideHelpStarterPreset || usesDestinationAddress) return;
    let active = true;
    const coordsValid = (loc) =>
      loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lon);
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[LocalOpsCreateSheet][prefill-geo:start]', {
        step,
        destinationAddressValue,
        destinationCoordsValid: coordsValid(destination),
      });
    }
    (async () => {
      if (
        destinationAddressValue &&
        destinationAddressValue !== destinationGeocodeRef.current
      ) {
        const currentDestinationAddress = destinationAddressValue;
        try {
          setIsDestinationGeocoding(true);
          const coords = await getCoordinatesFromAddress(currentDestinationAddress);
          if (!active) return;
          setDestination({ lat: coords.lat, lon: coords.lon });
          destinationGeocodeRef.current = currentDestinationAddress;
          setDestinationPinNotice(destinationConfirmedNotice);
          setDestinationPinError('');
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[LocalOpsCreateSheet][prefill-geo:dest:success]', {
              address: currentDestinationAddress,
              coords,
              step,
            });
          }
        } catch (err) {
          if (!active) return;
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[LocalOpsCreateSheet][prefill-geo:dest:fail]', {
              address: currentDestinationAddress,
              error: err?.message,
              step,
            });
          }
        } finally {
          if (active) setIsDestinationGeocoding(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [
    open,
    destinationAddressValue,
    isRideHelpStarterPreset,
    usesDestinationAddress,
    destinationConfirmedNotice,
  ]);

  useEffect(() => {
    if (!open || !isCollisionSpecialistRidePreset || !isDelivery) {
      setRideSuggestedPrice(null);
      setRideSuggestedDistanceMi(null);
      deliveryPriceGeocodeRef.current = '';
      return;
    }
    if (!taskDeliveryAddressValue) {
      clearRouteSuggestedAmount();
      return;
    }
    if (!pickupCoordsValid || !destinationCoordsValid) {
      clearRouteSuggestedAmount();
      return;
    }

    const geocodeKey = `${pickupAddressValue}|${taskDeliveryAddressValue}`;
    if (deliveryPriceGeocodeRef.current === geocodeKey) return;

    let active = true;
    setIsRidePriceGeocoding(true);
    Promise.resolve()
      .then(() => {
        if (!active || !destinationCoordsValid) return;
        const meters = getDistanceFromLatLonInMeters(
          location.lat,
          location.lon,
          destination.lat,
          destination.lon,
        );
        const miles = meters / 1609.344;
        const suggested = getCollisionSpecialistRideSuggestedPrice(miles);
        if (!Number.isFinite(suggested)) return;
        const suggestedString = String(suggested);
        setRideSuggestedPrice(suggested);
        setRideSuggestedDistanceMi(miles);
        deliveryPriceGeocodeRef.current = geocodeKey;
        setPrice((prev) => {
          const current = String(prev || '').trim();
          if (!current || current === rideAutoPriceRef.current) {
            rideAutoPriceRef.current = suggestedString;
            return suggestedString;
          }
          return prev;
        });
      })
      .catch(() => {
        if (!active) return;
        setRideSuggestedPrice(null);
        setRideSuggestedDistanceMi(null);
      })
      .finally(() => {
        if (!active) return;
        setIsRidePriceGeocoding(false);
      });

    return () => {
      active = false;
    };
  }, [
    open,
    isCollisionSpecialistRidePreset,
    isDelivery,
    taskDeliveryAddressValue,
    clearRouteSuggestedAmount,
    pickupAddressValue,
    pickupCoordsValid,
    destination,
    destinationCoordsValid,
    location,
  ]);

  useEffect(() => {
    if (!open || !isRideHelpStarterPreset || !isAssistance) {
      rideHelpPriceKeyRef.current = '';
      rideHelpAutoPriceRef.current = '';
      rideHelpHasAutoPricedRef.current = false;
      if (!isCollisionSpecialistRidePreset) {
        setRideSuggestedPrice(null);
        setRideSuggestedDistanceMi(null);
        setIsRidePriceGeocoding(false);
      }
      return;
    }

    if (
      !originLocationConfirmed ||
      !rideHelpDestinationConfirmed ||
      !pickupCoordsValid ||
      !destinationCoordsValid
    ) {
      clearRouteSuggestedAmount();
      return;
    }

    const priceKey = [
      Number(location.lat).toFixed(6),
      Number(location.lon).toFixed(6),
      Number(destination.lat).toFixed(6),
      Number(destination.lon).toFixed(6),
    ].join('|');
    if (rideHelpPriceKeyRef.current === priceKey) return;

    setIsRidePriceGeocoding(true);
    const meters = getDistanceFromLatLonInMeters(
      location.lat,
      location.lon,
      destination.lat,
      destination.lon,
    );
    const miles = meters / 1609.344;
    const suggested = getCollisionSpecialistRideSuggestedPrice(miles);
    if (!Number.isFinite(suggested)) {
      setRideSuggestedPrice(null);
      setRideSuggestedDistanceMi(null);
      setIsRidePriceGeocoding(false);
      return;
    }

    const suggestedString = String(suggested);
    setRideSuggestedPrice(suggested);
    setRideSuggestedDistanceMi(miles);
    rideHelpPriceKeyRef.current = priceKey;
    setPrice((prev) => {
      const current = String(prev || '').trim();
      const currentCents = normalizeDollarCents(current);
      const autoCents = normalizeDollarCents(rideHelpAutoPriceRef.current);
      if (
        !rideHelpHasAutoPricedRef.current ||
        !current ||
        (currentCents !== null && currentCents === autoCents)
      ) {
        rideHelpHasAutoPricedRef.current = true;
        rideHelpAutoPriceRef.current = suggestedString;
        return suggestedString;
      }
      return prev;
    });
    setIsRidePriceGeocoding(false);
  }, [
    open,
    isRideHelpStarterPreset,
    isAssistance,
    isCollisionSpecialistRidePreset,
    clearRouteSuggestedAmount,
    originLocationConfirmed,
    rideHelpDestinationConfirmed,
    pickupCoordsValid,
    destinationCoordsValid,
    location,
    destination,
  ]);

  useEffect(() => {
    if (!open || !isGuidedRouteSuggestedFlow) {
      guidedRoutePriceKeyRef.current = '';
      guidedRouteAutoPriceRef.current = '';
      guidedRouteHasAutoPricedRef.current = false;
      if (!isRideHelpStarterPreset && !isCollisionSpecialistRidePreset) {
        setRideSuggestedPrice(null);
        setRideSuggestedDistanceMi(null);
        setIsRidePriceGeocoding(false);
      }
      return;
    }

    if (
      !originLocationConfirmed ||
      !assistanceDestinationConfirmed ||
      !pickupCoordsValid ||
      !destinationCoordsValid
    ) {
      clearRouteSuggestedAmount();
      return;
    }

    const priceKey = [
      Number(location.lat).toFixed(6),
      Number(location.lon).toFixed(6),
      Number(destination.lat).toFixed(6),
      Number(destination.lon).toFixed(6),
    ].join('|');
    if (guidedRoutePriceKeyRef.current === priceKey) return;

    setIsRidePriceGeocoding(true);
    const meters = getDistanceFromLatLonInMeters(
      location.lat,
      location.lon,
      destination.lat,
      destination.lon,
    );
    const miles = meters / 1609.344;
    const suggested = getCollisionSpecialistRideSuggestedPrice(miles);
    if (!Number.isFinite(suggested)) {
      setRideSuggestedPrice(null);
      setRideSuggestedDistanceMi(null);
      setIsRidePriceGeocoding(false);
      return;
    }

    const suggestedString = String(suggested);
    setRideSuggestedPrice(suggested);
    setRideSuggestedDistanceMi(miles);
    guidedRoutePriceKeyRef.current = priceKey;
    setPrice((prev) => {
      const current = String(prev || '').trim();
      const currentCents = normalizeDollarCents(current);
      const autoCents = normalizeDollarCents(guidedRouteAutoPriceRef.current);
      if (
        !guidedRouteHasAutoPricedRef.current ||
        !current ||
        (currentCents !== null && currentCents === autoCents)
      ) {
        guidedRouteHasAutoPricedRef.current = true;
        guidedRouteAutoPriceRef.current = suggestedString;
        return suggestedString;
      }
      return prev;
    });
    setIsRidePriceGeocoding(false);
  }, [
    open,
    isGuidedRouteSuggestedFlow,
    isRideHelpStarterPreset,
    isCollisionSpecialistRidePreset,
    clearRouteSuggestedAmount,
    originLocationConfirmed,
    assistanceDestinationConfirmed,
    pickupCoordsValid,
    destinationCoordsValid,
    location,
    destination,
  ]);

  // When typed origin changes, clear stale candidate coords until re-confirmed.
  useEffect(() => {
    if (!open) return;
    if (!pickupAddressValue) return;
    if (pinSource !== 'manual' && pickupAddressValue !== pickupGeocodeRef.current) {
      setLocation(null);
      setLocationLabel('');
      addressKeyRef.current = null;
    }
  }, [open, pickupAddressValue, pinSource]);

  useEffect(() => {
    if (!pickupAddressValue) {
      if (pinNotice) setPinNotice('');
      if (pinError) setPinError('');
      return;
    }
    if (
      pickupGeocodeRef.current &&
      pickupAddressValue !== pickupGeocodeRef.current
    ) {
      if (pinNotice) setPinNotice('');
      if (pinError) setPinError('');
    }
  }, [pickupAddressValue, pinNotice, pinError]);

  useEffect(() => {
    if (isAssistance && !isRideHelpStarterPreset && visibility !== 'trusted') {
      setVisibility('trusted');
    }
  }, [isAssistance, isRideHelpStarterPreset, visibility]);

  useEffect(() => {
    if (!canShowRequireVerifiedLocal) {
      setRequireVerifiedLocal(false);
    }
  }, [canShowRequireVerifiedLocal]);

  useEffect(() => {
    if (!firstCompletionAccessSupported && firstCompletionAccessRequested) {
      setFirstCompletionAccessRequested(false);
    }
  }, [firstCompletionAccessRequested, firstCompletionAccessSupported]);

  useEffect(() => {
    return () => {
      mediaItems.forEach((item) => {
        if (item.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(item.preview);
        }
      });
    };
  }, [mediaItems]);

  const mediaNeedsReattach = useMemo(
    () => mediaItems.some((item) => item.needsReattach || item.placeholder),
    [mediaItems],
  );

  useEffect(() => {
    if (!mediaNeedsReattach && mediaWarning) {
      setMediaWarning('');
    }
  }, [mediaNeedsReattach, mediaWarning]);

  const toggleTrusted = useCallback((id) => {
    setTrustedPlayers((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  }, []);

  const addFiles = useCallback(
    (files) => {
      const list = Array.from(files || []);
      if (!list.length) return;
      const next = [];
      const errs = [];
      list.forEach((file) => {
        try {
          uploader.validate(file);
          const preview = URL.createObjectURL(file);
          next.push({
            file,
            preview,
            isVideo: isVideoFile(file.name),
            needsReattach: false,
          });
        } catch (err) {
          errs.push(err.message || `${file.name} is not valid.`);
        }
      });
      if (errs.length) setError(errs.join(' | '));
      setMediaItems((prev) => [...prev, ...next]);
      setMediaWarning('');
      setError((prev) =>
        prev && prev.toLowerCase().includes('reattach') ? '' : prev,
      );
    },
    [uploader],
  );

  const removeMedia = useCallback((idx) => {
    setMediaItems((prev) => {
      const next = [...prev];
      const removed = next.splice(idx, 1);
      removed.forEach((item) => {
        if (item.preview?.startsWith('blob:')) URL.revokeObjectURL(item.preview);
      });
      return next;
    });
    setCoverIndex((prev) => {
      if (idx === prev) return 0;
      if (idx < prev) return Math.max(0, prev - 1);
      return prev;
    });
  }, []);

  const invalidateMedia = useCallback((idx) => {
    setMediaItems((prev) => {
      if (!prev[idx]) return prev;
      const next = [...prev];
      const old = next[idx];
      if (old.preview?.startsWith('blob:')) URL.revokeObjectURL(old.preview);
      next[idx] = {
        preview: '',
        url: '',
        isVideo: false,
        placeholder: true,
        label: 'Draft preview expired.',
        needsReattach: true,
      };
      return next;
    });
  }, []);

  const uploadMediaItems = useCallback(async () => {
    if (!mediaItems.length) return [];
    const uploaded = [];
    let completed = 0;
    for (const item of mediaItems) {
      if (item.needsReattach || item.placeholder) {
        throw new Error('MEDIA_REATTACH_REQUIRED');
      }
      if (item.persisted && item.url) {
        uploaded.push({
          url: item.url,
          type: item.isVideo ? 'video' : 'image',
        });
      } else if (item.file) {
        const completedBeforeUpload = completed;
        const { transformedUrl } = await uploader.upload(item.file, {
          onProgress: (pct) => {
            const slice =
              (completedBeforeUpload + pct / 100) / mediaItems.length;
            setUploadPct(Math.round(slice * 100));
          },
          publicIdPrefix: 'Images',
        });
        uploaded.push({
          url: transformedUrl,
          type: item.isVideo ? 'video' : 'image',
        });
      } else if (item.url) {
        uploaded.push({
          url: item.url,
          type: item.isVideo ? 'video' : 'image',
        });
      } else {
        throw new Error('MEDIA_REATTACH_REQUIRED');
      }
      completed += 1;
    }
    setUploadPct(uploaded.length ? 100 : 0);
    return uploaded;
  }, [mediaItems, uploader]);

  const parsedQuantity = useMemo(() => {
    const val = Number(quantityAvailable);
    if (!Number.isFinite(val) || val < 1) return 1;
    return Math.floor(val);
  }, [quantityAvailable]);

  const computeStartDate = useCallback(() => {
    const parsed = parseLocalDateTime(availabilityStart);
    if (customStart && parsed) return parsed;
    return new Date();
  }, [availabilityStart, customStart]);

  const hasKnownBalance = useMemo(
    () => Number.isFinite(playerBalance),
    [playerBalance],
  );
  const hasSufficientBalance = useMemo(() => {
    if (!isTask || escrowRequiredJGK <= 0) return true;
    if (!hasKnownBalance) return true;
    return playerBalance >= escrowRequiredJGK;
  }, [isTask, escrowRequiredJGK, hasKnownBalance, playerBalance]);

  const canPublish = useMemo(() => {
    // Debug assist: surface why publish is blocked (development only)
    const quantityValue = parsedQuantity;
    const startDate = computeStartDate();
    const endDate = resolveAvailabilityEnd(
      startDate,
      availabilityEnd,
      availabilityEndMode,
    );
    if (!selectedMeta) return false;
    if (isOffering && disabledOfferings) return false;
    if (isTask && disabledTasks) return false;
    if (isTask && !presenceReady) return false;
    if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lon))
      return false;
    if ((isTask || isOffering) && !pickupAddress.trim()) return false;
    if (typedOriginNeedsCandidateConfirmation) return false;
    if (originPinOutsideLocalArea) return false;
    if (isDelivery && !taskDeliveryAddressValue) return false;
    if (
      usesDestinationAddress &&
      destinationAddress.trim() &&
      !assistanceDestinationConfirmed
    ) {
      return false;
    }
    if (!startDate || !endDate) return false;
    if (taskDescriptionOverLimit) return false;
    if (!communityDescriptionIsValid) return false;
    if (isTask && requestQuote) return false;
    if (!taskPriceIsValid) return false;
    if (
      isOffering &&
      !requestQuote &&
      (parsedPrice === null ||
        !Number.isFinite(parsedPrice) ||
        parsedPrice < 0)
    ) {
      return false;
    }
    if (isOffering && requestQuote) return false;
    if (isOffering && !category.trim()) return false;
    if (isOffering && (!Number.isInteger(quantityValue) || quantityValue < 1)) {
      return false;
    }
    if (isAssistance) {
      if (!destinationAddress.trim()) return false;
      if (!assistanceDestinationConfirmed) return false;
      if (!isRideHelpStarterPreset && trustedPlayers.length === 0) return false;
    }
    return true;
  }, [
    selectedMeta,
    isOffering,
    isTask,
    disabledOfferings,
    disabledTasks,
    presenceReady,
    location,
    originPinOutsideLocalArea,
    typedOriginNeedsCandidateConfirmation,
    pickupAddress,
    taskDeliveryAddressValue,
    availabilityEnd,
    availabilityEndMode,
    computeStartDate,
    requestQuote,
    parsedPrice,
    taskPriceIsValid,
    communityDescriptionIsValid,
    parsedQuantity,
    category,
    isAssistance,
    assistanceDestinationConfirmed,
    isRideHelpStarterPreset,
    isDelivery,
    usesDestinationAddress,
    destinationAddress,
    trustedPlayers,
    taskDescriptionOverLimit,
  ]);

  const publishBlockers = useMemo(() => {
    if (canPublish) return [];
    const issues = [];
    const quantityValue = parsedQuantity;
    const startDate = computeStartDate();
    const endDate = resolveAvailabilityEnd(
      startDate,
      availabilityEnd,
      availabilityEndMode,
    );
    if (!selectedMeta) issues.push('Choose a type');
    if (isTask && disabledTasks) issues.push('Tasks are disabled');
    if (isOffering && disabledOfferings) issues.push('Offerings are disabled');
    if (isTask && !presenceReady) issues.push('Presence not active');
    if (
      (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lon)) &&
      !typedOriginNeedsCandidateConfirmation
    ) {
      issues.push('Location not confirmed');
    }
    if ((isTask || isOffering) && !pickupAddress.trim())
      issues.push(isOffering ? 'Offering address required' : 'Pickup address required');
    if (typedOriginNeedsCandidateConfirmation) {
      issues.push(LOCALOPS_ORIGIN_CONFIRMATION_REQUIRED_MESSAGE);
    }
    if (originPinOutsideLocalArea) {
      issues.push(LOCALOPS_PIN_OUTSIDE_LOCAL_AREA_MESSAGE);
    }
    if (isDelivery && !taskDeliveryAddressValue) {
      issues.push(
        isCollisionSpecialistRidePreset
          ? 'Destination address required'
          : 'Delivery address required',
      );
    }
    if (
      !isAssistance &&
      usesDestinationAddress &&
      destinationAddress.trim() &&
      !assistanceDestinationConfirmed
    ) {
      issues.push(destinationConfirmationRequiredMessage);
    }
    if (!startDate || !endDate) issues.push('Availability required');
    if (taskDescriptionOverLimit) {
      issues.push(
        `Description must be ${LOCALOPS_TASK_DESCRIPTION_MAX_LENGTH.toLocaleString(
          'en-US',
        )} characters or fewer`,
      );
    }
    if (!communityDescriptionIsValid) {
      issues.push('Add clear volunteer instructions');
    }
    if (
      isTask &&
      !communityTaskSelected &&
      (requestQuote ||
        !Number.isFinite(parsedPrice) ||
        parsedPrice < LOCALOPS_MIN_TASK_PRICE_USD)
    ) {
      issues.push(LOCALOPS_TASK_PRICE_REQUIRED_MESSAGE);
    } else if (
      isOffering &&
      !requestQuote &&
      (parsedPrice === null ||
        !Number.isFinite(parsedPrice) ||
        parsedPrice < 0)
    ) {
      issues.push('Price required');
    }
    if (isOffering && requestQuote) issues.push('Offerings cannot request quote');
    if (isOffering && !category.trim()) issues.push('Category required');
    if (isOffering && (!Number.isInteger(quantityValue) || quantityValue < 1)) {
      issues.push('Quantity required');
    }
    if (isAssistance) {
      if (!destinationAddress.trim()) issues.push('Destination address required');
      else if (!assistanceDestinationConfirmed) {
        issues.push(destinationConfirmationRequiredMessage);
      }
      if (!isRideHelpStarterPreset && trustedPlayers.length === 0) {
        issues.push(
          'Select at least one trusted player to publish this assistance task.',
        );
      }
    }
    return issues;
  }, [
    canPublish,
    selectedMeta,
    isTask,
    isOffering,
    disabledTasks,
    disabledOfferings,
    presenceReady,
    location,
    originPinOutsideLocalArea,
    typedOriginNeedsCandidateConfirmation,
    pickupAddress,
    taskDeliveryAddressValue,
    computeStartDate,
    availabilityEnd,
    availabilityEndMode,
    requestQuote,
    communityTaskSelected,
    communityDescriptionIsValid,
    parsedPrice,
    parsedQuantity,
    category,
    isDelivery,
    isAssistance,
    isCollisionSpecialistRidePreset,
    usesDestinationAddress,
    destinationAddress,
    assistanceDestinationConfirmed,
    destinationConfirmationRequiredMessage,
    isRideHelpStarterPreset,
    trustedPlayers,
    taskDescriptionOverLimit,
  ]);

  const resolvePublishBlockedReason = useCallback(() => {
    if (canPublish) return '';
    if (!selectedMeta) return 'validation_failed';
    if (isTask && !presenceReady) return 'auth_required';
    if (typedOriginNeedsCandidateConfirmation) {
      return 'location_confirmation_required';
    }
    if (originPinOutsideLocalArea) return 'location_outside_area';
    if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lon)) {
      return 'missing_location';
    }
    if ((isTask || isOffering) && !pickupAddress.trim()) {
      return 'missing_pickup';
    }
    if (isDelivery && !taskDeliveryAddressValue) return 'missing_destination';
    if (isAssistance && !destinationAddress.trim()) return 'missing_destination';
    if (usesDestinationAddress && destinationAddress.trim() && !assistanceDestinationConfirmed) {
      return 'location_confirmation_required';
    }
    if (
      isTask &&
      !communityTaskSelected &&
      (requestQuote ||
        !Number.isFinite(parsedPrice) ||
        parsedPrice < LOCALOPS_MIN_TASK_PRICE_USD)
    ) {
      return 'missing_amount';
    }
    if (visibility === 'trusted' && trustedPlayers.length === 0) {
      return 'missing_visibility';
    }
    return 'validation_failed';
  }, [
    assistanceDestinationConfirmed,
    canPublish,
    communityTaskSelected,
    destinationAddress,
    isAssistance,
    isDelivery,
    isOffering,
    isTask,
    location,
    originPinOutsideLocalArea,
    parsedPrice,
    pickupAddress,
    presenceReady,
    requestQuote,
    selectedMeta,
    taskDeliveryAddressValue,
    trustedPlayers.length,
    typedOriginNeedsCandidateConfirmation,
    usesDestinationAddress,
    visibility,
  ]);

  const resolvePublishFailureReason = useCallback((err) => {
    const httpStatus = Number(err?.response?.status || err?.status);
    if (Number.isFinite(httpStatus) && httpStatus >= 400) {
      return `http_${Math.round(httpStatus)}`;
    }
    if (err?.code === 'ECONNABORTED') return 'network_timeout';
    if (err?.code === 'ERR_NETWORK') return 'network_error';
    return 'publish_failed';
  }, []);

  useEffect(() => {
    if (!open || !selectedMeta || step !== 1) return;
    emitCreateFunnelEvent(
      LOCALOPS_FUNNEL_EVENTS.CREATE_DETAILS_STEP_REACHED,
      {},
      {
        oncePerSheet: true,
        oncePerSession: true,
        dedupeKey: 'details_step_reached',
      },
    );
  }, [emitCreateFunnelEvent, open, selectedMeta, step]);

  useEffect(() => {
    if (
      !open ||
      !selectedMeta ||
      !Number.isFinite(parsedPrice) ||
      parsedPrice < LOCALOPS_MIN_TASK_PRICE_USD
    ) {
      return;
    }
    emitCreateFunnelEvent(
      LOCALOPS_FUNNEL_EVENTS.CREATE_AMOUNT_SET,
      {
        amountUSD: parsedPrice,
        amountSource: resolveCreateAmountSource(),
      },
      {
        oncePerSheet: true,
        oncePerSession: true,
        dedupeKey: 'amount_set',
      },
    );
  }, [
    emitCreateFunnelEvent,
    open,
    parsedPrice,
    resolveCreateAmountSource,
    selectedMeta,
  ]);

  useEffect(() => {
    if (!open || !selectedMeta || step !== 2) return;
    emitCreateFunnelEvent(
      LOCALOPS_FUNNEL_EVENTS.CREATE_REVIEW_STEP_REACHED,
      {
        hasRequiredFields: canPublish,
        requiresTopUp: hasPaidTaskAmount && !hasSufficientBalance,
        shortfallUSD: resolveCreateShortfallUSD(),
      },
      {
        oncePerSheet: true,
        oncePerSession: true,
        dedupeKey: 'review_step_reached',
      },
    );
  }, [
    canPublish,
    emitCreateFunnelEvent,
    hasPaidTaskAmount,
    hasSufficientBalance,
    open,
    resolveCreateShortfallUSD,
    selectedMeta,
    step,
  ]);

  const canAdvanceFromDetails = useMemo(() => {
    if (step !== 1 || !selectedMeta) return true;
    if (isGuidedOfferingFlow) {
      const quantityValue = Number(quantityAvailable);
      if (!category.trim()) return false;
      if (!pickupAddressValue) return false;
      if (!originLocationConfirmed) return false;
      if (originPinOutsideLocalArea) return false;
      if (
        requestQuote ||
        !Number.isFinite(parsedPrice) ||
        parsedPrice < 0
      ) {
        return false;
      }
      return Number.isInteger(quantityValue) && quantityValue >= 1;
    }
    if (!isTask) return true;
    if (taskDescriptionOverLimit) return false;
    if (!communityDescriptionIsValid) return false;
    if (communityTaskSelected) return true;
    if (isRideHelpFlow) {
      if (!pickupAddressValue) return false;
      if (!originLocationConfirmed) return false;
      if (!destinationAddressValue) return false;
      if (!assistanceDestinationConfirmed) return false;
      return (
        !requestQuote &&
        Number.isFinite(parsedPrice) &&
        parsedPrice >= LOCALOPS_MIN_TASK_PRICE_USD
      );
    }
    if (isAssistance && !assistanceDestinationConfirmed) return false;
    if (isDelivery) {
      if (!taskDeliveryAddressValue) return false;
      if (!assistanceDestinationConfirmed) return false;
    }
    return (
      !requestQuote &&
      Number.isFinite(parsedPrice) &&
      parsedPrice >= LOCALOPS_MIN_TASK_PRICE_USD
    );
  }, [
    step,
    selectedMeta,
    isTask,
    isGuidedOfferingFlow,
    quantityAvailable,
    category,
    taskDescriptionOverLimit,
    communityDescriptionIsValid,
    communityTaskSelected,
    isRideHelpFlow,
    pickupAddressValue,
    originLocationConfirmed,
    originPinOutsideLocalArea,
    destinationAddressValue,
    isAssistance,
    isDelivery,
    taskDeliveryAddressValue,
    assistanceDestinationConfirmed,
    requestQuote,
    parsedPrice,
  ]);
  const stepTwoBlockerCopy = useMemo(() => {
    if (step !== 1 || canAdvanceFromDetails) return '';
    if (isGuidedOfferingFlow && !category.trim()) {
      return 'Add an offering category to continue.';
    }
    if (isGuidedOfferingFlow && !pickupAddressValue) {
      return 'Add pickup or fulfillment location to continue.';
    }
    if (isGuidedOfferingFlow && !originLocationConfirmed) {
      return 'Select a local offering location match to continue.';
    }
    if (isGuidedOfferingFlow && originPinOutsideLocalArea) {
      return LOCALOPS_PIN_OUTSIDE_LOCAL_AREA_MESSAGE;
    }
    if (
      isGuidedOfferingFlow &&
      (requestQuote || !Number.isFinite(parsedPrice) || parsedPrice < 0)
    ) {
      return 'Add a valid offering price to continue.';
    }
    if (
      isGuidedOfferingFlow &&
      (!Number.isInteger(Number(quantityAvailable)) ||
        Number(quantityAvailable) < 1)
    ) {
      return 'Add quantity available to continue.';
    }
    if (taskDescriptionOverLimit) {
      return `Shorten the description to ${LOCALOPS_TASK_DESCRIPTION_MAX_LENGTH.toLocaleString(
        'en-US',
      )} characters or fewer to continue.`;
    }
    if (!communityDescriptionIsValid) {
      return 'Add clear volunteer instructions to continue.';
    }
    if (isRideHelpFlow && !pickupAddressValue) {
      return 'Add pickup to continue.';
    }
    if (isRideHelpFlow && !originLocationConfirmed) {
      return 'Select a local pickup match to continue.';
    }
    if (isAssistance && !destinationAddress.trim()) {
      return 'Add destination to continue.';
    }
    if (isAssistance && !assistanceDestinationConfirmed) {
      return 'Select a confirmed destination to continue.';
    }
    if (isDelivery && !taskDeliveryAddressValue) {
      return isCollisionSpecialistRidePreset
        ? 'Add destination to continue.'
        : 'Add drop-off to continue.';
    }
    if (isDelivery && !assistanceDestinationConfirmed) {
      return isCollisionSpecialistRidePreset
        ? 'Select a confirmed destination to continue.'
        : 'Select a confirmed drop-off to continue.';
    }
    if (
      isTask &&
      !communityTaskSelected &&
      (requestQuote ||
        !Number.isFinite(parsedPrice) ||
        parsedPrice < LOCALOPS_MIN_TASK_PRICE_USD)
    ) {
      return 'Add amount to continue.';
    }
    return 'Complete the required details to continue.';
  }, [
    step,
    canAdvanceFromDetails,
    isGuidedOfferingFlow,
    category,
    quantityAvailable,
    taskDescriptionOverLimit,
    communityDescriptionIsValid,
    isTask,
    isRideHelpFlow,
    pickupAddressValue,
    originLocationConfirmed,
    originPinOutsideLocalArea,
    isAssistance,
    isDelivery,
    taskDeliveryAddressValue,
    assistanceDestinationConfirmed,
    isCollisionSpecialistRidePreset,
    destinationAddress,
    communityTaskSelected,
    requestQuote,
    parsedPrice,
  ]);
  const detailsBlockerTelemetry = useMemo(() => {
    if (step !== 1 || canAdvanceFromDetails) {
      return {
        currentStep: 'details',
        firstBlockingField: '',
        reasonCodes: [],
      };
    }
    if (isGuidedOfferingFlow && !category.trim()) {
      return {
        currentStep: 'details',
        firstBlockingField: 'category',
        reasonCodes: ['missing_category'],
      };
    }
    if (isGuidedOfferingFlow && !pickupAddressValue) {
      return {
        currentStep: 'details',
        firstBlockingField: 'pickup',
        reasonCodes: ['missing_pickup'],
      };
    }
    if (isGuidedOfferingFlow && !originLocationConfirmed) {
      return {
        currentStep: 'details',
        firstBlockingField: 'service_area',
        reasonCodes: ['pickup_unconfirmed'],
      };
    }
    if (isGuidedOfferingFlow && originPinOutsideLocalArea) {
      return {
        currentStep: 'details',
        firstBlockingField: 'service_area',
        reasonCodes: ['pickup_outside_area'],
      };
    }
    if (
      isGuidedOfferingFlow &&
      (requestQuote || !Number.isFinite(parsedPrice) || parsedPrice < 0)
    ) {
      return {
        currentStep: 'details',
        firstBlockingField: 'amount',
        reasonCodes: ['invalid_price'],
      };
    }
    if (
      isGuidedOfferingFlow &&
      (!Number.isInteger(Number(quantityAvailable)) ||
        Number(quantityAvailable) < 1)
    ) {
      return {
        currentStep: 'details',
        firstBlockingField: 'quantity',
        reasonCodes: ['invalid_quantity'],
      };
    }
    if (taskDescriptionOverLimit) {
      return {
        currentStep: 'details',
        firstBlockingField: 'description',
        reasonCodes: ['description_too_long'],
      };
    }
    if (!communityDescriptionIsValid) {
      return {
        currentStep: 'details',
        firstBlockingField: 'description',
        reasonCodes: ['missing_description'],
      };
    }
    if (isRideHelpFlow && !pickupAddressValue) {
      return {
        currentStep: 'details',
        firstBlockingField: 'pickup',
        reasonCodes: ['missing_pickup'],
      };
    }
    if (isRideHelpFlow && !originLocationConfirmed) {
      return {
        currentStep: 'details',
        firstBlockingField: 'pickup',
        reasonCodes: ['pickup_unconfirmed'],
      };
    }
    if (isAssistance && !destinationAddress.trim()) {
      return {
        currentStep: 'details',
        firstBlockingField: 'destination',
        reasonCodes: ['missing_destination'],
      };
    }
    if (isAssistance && !assistanceDestinationConfirmed) {
      return {
        currentStep: 'details',
        firstBlockingField: 'destination',
        reasonCodes: ['destination_unconfirmed'],
      };
    }
    if (isDelivery && !taskDeliveryAddressValue) {
      return {
        currentStep: 'details',
        firstBlockingField: 'dropoff',
        reasonCodes: ['missing_dropoff'],
      };
    }
    if (isDelivery && !assistanceDestinationConfirmed) {
      return {
        currentStep: 'details',
        firstBlockingField: 'dropoff',
        reasonCodes: ['dropoff_unconfirmed'],
      };
    }
    if (
      isTask &&
      !communityTaskSelected &&
      (requestQuote ||
        !Number.isFinite(parsedPrice) ||
        parsedPrice < LOCALOPS_MIN_TASK_PRICE_USD)
    ) {
      return {
        currentStep: 'details',
        firstBlockingField: 'amount',
        reasonCodes: ['invalid_amount'],
      };
    }
    return {
      currentStep: 'details',
      firstBlockingField: 'unknown',
      reasonCodes: ['missing_required_fields'],
    };
  }, [
    step,
    canAdvanceFromDetails,
    isGuidedOfferingFlow,
    category,
    pickupAddressValue,
    originLocationConfirmed,
    originPinOutsideLocalArea,
    requestQuote,
    parsedPrice,
    quantityAvailable,
    taskDescriptionOverLimit,
    communityDescriptionIsValid,
    isRideHelpFlow,
    isAssistance,
    destinationAddress,
    assistanceDestinationConfirmed,
    isDelivery,
    taskDeliveryAddressValue,
    isTask,
    communityTaskSelected,
  ]);
  useEffect(() => {
    if (!open || step !== 1 || canAdvanceFromDetails) {
      createJourneyUnavailableSignatureRef.current = '';
      return;
    }
    const signature = buildCreateJourneyBlockerSignature(detailsBlockerTelemetry);
    if (!signature || createJourneyUnavailableSignatureRef.current === signature) {
      return;
    }
    createJourneyUnavailableSignatureRef.current = signature;
    emitCreateJourneyEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_NEXT_UNAVAILABLE, {
      ...detailsBlockerTelemetry,
      hasRequiredFields: false,
    });
  }, [
    canAdvanceFromDetails,
    detailsBlockerTelemetry,
    emitCreateJourneyEvent,
    open,
    step,
  ]);
  const handleAdvanceFromDetails = useCallback(() => {
    if (!canAdvanceFromDetails) {
      emitCreateJourneyEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_NEXT_BLOCKED, {
        ...detailsBlockerTelemetry,
        hasRequiredFields: false,
      });
      return;
    }
    emitCreateJourneyEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_NEXT_CLICKED, {
      currentStep: 'details',
      hasRequiredFields: true,
    });
    setStep((prev) => Math.min(2, prev + 1));
  }, [canAdvanceFromDetails, detailsBlockerTelemetry, emitCreateJourneyEvent]);
  const detailsReviewLabel = useMemo(() => {
    if (isRideHelpFlow && canAdvanceFromDetails) {
      return `Review ${rideReviewAmountLabel} Ride Help task`;
    }
    if (isGuidedOfferingFlow && canAdvanceFromDetails) {
      return 'Review offering';
    }
    return 'Next: Review';
  }, [
    isRideHelpFlow,
    isGuidedOfferingFlow,
    canAdvanceFromDetails,
    rideReviewAmountLabel,
  ]);
  const emitCreateJourneyClose = useCallback(
    (closeMethod = 'unknown') => {
      if (createJourneyClosedRef.current) return;
      createJourneyClosedRef.current = true;
      emitCreateJourneyEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_SHEET_CLOSED, {
        currentStep: getCreateJourneyStepName(step),
        closeMethod,
      });
    },
    [emitCreateJourneyEvent, step],
  );
  const resetCreateJourneySession = useCallback(() => {
    createJourneySessionRef.current = '';
    createJourneyUnavailableSignatureRef.current = '';
    createJourneyTouchedFieldsRef.current = new Set();
    createJourneyClosedRef.current = false;
  }, []);
  const handleSheetClose = useCallback(() => {
    emitCreateJourneyClose('close_button');
    onClose?.();
  }, [emitCreateJourneyClose, onClose]);
  const handleResetSheet = useCallback(() => {
    emitCreateJourneyClose('reset');
    resetCreateJourneySession();
    resetForm({
      nextType: selectedType,
      nextStep: selectedType ? 1 : 0,
      keepLocation: false,
    });
  }, [
    emitCreateJourneyClose,
    resetCreateJourneySession,
    resetForm,
    selectedType,
  ]);
  const handleStepBack = useCallback(() => {
    emitCreateJourneyEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_STEP_BACK, {
      currentStep: getCreateJourneyStepName(step),
      previousStep: getCreateJourneyStepName(Math.max(0, step - 1)),
    });
    setStep((prev) => Math.max(0, prev - 1));
  }, [emitCreateJourneyEvent, step]);
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[DEBUG][PUBLISH CHECK]', {
      destination,
      destinationCoordsValid,
      destinationAddressValue,
      blocked: isPublishBlockedByDestination,
      pickupAddressValue,
      pickupCoordsValid,
      step,
    });
  }

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[DEBUG][STEP VIEW]', {
        step,
        pickupAddressValue,
        destinationAddressValue,
        pickupCoordsValid,
        destinationCoordsValid,
        isPickupGeocoding,
        isDestinationGeocoding,
        location,
        destination,
      });
    }
  }, [
    step,
    pickupAddressValue,
    destinationAddressValue,
    pickupCoordsValid,
    destinationCoordsValid,
    isPickupGeocoding,
    isDestinationGeocoding,
    location,
    destination,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[DEBUG][DESTINATION STATE]', {
        step,
        destinationAddressValue,
        destination,
        destinationCoordsValid,
      });
    }
  }, [step, destinationAddressValue, destination, destinationCoordsValid]);

  const persistHistory = useCallback((mode, updates) => {
    if (!mode) return;
    setInputHistory((prev) => {
      const next = normalizeHistory(prev);
      const bucket = next[mode] || {};
      Object.entries(updates || {}).forEach(([field, raw]) => {
        if (!field || raw === undefined || raw === null) return;
        if (field === 'media') return;
        const list = Array.isArray(bucket[field]) ? bucket[field] : [];
        const value = String(raw).trim();
        if (!value) return;
        const lower = value.toLowerCase();
        const deduped = [
          value,
          ...list.filter((item) => item.toLowerCase() !== lower),
        ].slice(0, HISTORY_LIMIT);
        bucket[field] = deduped;
      });
      next[mode] = bucket;
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
        } catch (err) {
          // ignore storage failures
        }
      }
      return next;
    });
  }, []);

  const persistHistoryExternal = useCallback(
    (mode, payload) => {
      if (!mode || !payload) return;
      const updates = {
        title: payload.title || '',
        description: payload.description || '',
        category: payload.category || '',
        price:
          payload.price ?? payload.price === 0 ? String(payload.price) : '',
      };
      if (mode === 'task') {
        updates.pickupAddress = payload.originLocation?.address || '';
        updates.deliveryAddress = payload.meta?.deliveryAddress || '';
      }
      if (mode === 'offering') {
        updates.quantity = payload.quantityAvailable
          ? String(payload.quantityAvailable)
          : '';
      }
      persistHistory(mode, updates);
    },
    [persistHistory],
  );

  const handlePublish = async () => {
    if (publishInFlightRef.current || isSubmitting) return;
    if (!selectedMeta) return;
    emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_ATTEMPTED, {
      requiresTopUp: hasPaidTaskAmount && !hasSufficientBalance,
      shortfallUSD: resolveCreateShortfallUSD(),
    });
    setError('');
    if (!canPublish) {
      emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_BLOCKED, {
        reason: resolvePublishBlockedReason() || 'validation_failed',
        shortfallUSD: resolveCreateShortfallUSD(),
      });
      if (typedOriginNeedsCandidateConfirmation) {
        setError(LOCALOPS_ORIGIN_CONFIRMATION_REQUIRED_MESSAGE);
      } else if (originPinOutsideLocalArea) {
        setError(LOCALOPS_PIN_OUTSIDE_LOCAL_AREA_MESSAGE);
      } else {
        setError('Please complete the required fields.');
      }
      return;
    }
    publishInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[LocalOpsCreateSheet][publish:start]', {
          step,
          mode: selectedMeta.mode,
          taskType: selectedMeta.taskType,
          pickupAddressValue,
          destinationAddressValue,
          pickupLocation: location,
          destinationLocation: destination,
          pickupCoordsValid: location && Number.isFinite(location.lat) && Number.isFinite(location.lon),
          destinationCoordsValid:
            destination && Number.isFinite(destination.lat) && Number.isFinite(destination.lon),
        });
      }
      const coordsValid = (loc) =>
        loc &&
        Number.isFinite(loc.lat) &&
        Number.isFinite(loc.lon);
      if (pickupAddressValue && !coordsValid(location)) {
        emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_BLOCKED, {
          reason: 'location_confirmation_required',
        });
        setIsSubmitting(false);
        publishInFlightRef.current = false;
        setError('Pickup address needs a confirmed location. Please wait for it to resolve or reselect.');
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[LocalOpsCreateSheet][publish:block]', {
            reason: 'pickup coords missing',
            pickupAddressValue,
            location,
          });
        }
        return;
      }
      if ((isTask || isOffering) && originPinOutsideLocalArea) {
        emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_BLOCKED, {
          reason: 'location_outside_area',
        });
        setIsSubmitting(false);
        publishInFlightRef.current = false;
        setError(LOCALOPS_PIN_OUTSIDE_LOCAL_AREA_MESSAGE);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[LocalOpsCreateSheet][publish:block]', {
            reason: 'origin pin outside local area',
            pickupAddressValue,
            location,
            defaultCenter,
            radiusMeters,
          });
        }
        return;
      }
      if (isAssistance) {
        if (!destinationAddressValue) {
          emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_BLOCKED, {
            reason: 'missing_destination',
          });
          setIsSubmitting(false);
          publishInFlightRef.current = false;
          setError('Destination address required');
          return;
        }
        if (!assistanceDestinationConfirmed) {
          emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_BLOCKED, {
            reason: 'location_confirmation_required',
          });
          setIsSubmitting(false);
          publishInFlightRef.current = false;
          setError(destinationConfirmationRequiredMessage);
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[LocalOpsCreateSheet][publish:block]', {
              reason: 'assistance destination not confirmed',
              destinationAddressValue,
              destination,
            });
          }
          return;
        }
      }
      if (
        usesDestinationAddress &&
        destinationAddressValue &&
        !assistanceDestinationConfirmed
      ) {
        emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_BLOCKED, {
          reason: 'location_confirmation_required',
        });
        setIsSubmitting(false);
        publishInFlightRef.current = false;
        setError(destinationConfirmationRequiredMessage);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[LocalOpsCreateSheet][publish:block]', {
            reason: 'destination coords missing',
            destinationAddressValue,
            destination,
          });
        }
        return;
      }
      const numericPrice = parsedPrice;
      let media = [];
      try {
        media = await uploadMediaItems();
      } catch (mediaErr) {
        emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_BLOCKED, {
          reason:
            mediaErr?.message === 'MEDIA_REATTACH_REQUIRED' ||
            mediaErr?.message === 'MEDIA_MISSING_FILE'
              ? 'media_reattach_required'
              : 'validation_failed',
        });
        setIsSubmitting(false);
        publishInFlightRef.current = false;
        const message =
          mediaErr?.message === 'MEDIA_REATTACH_REQUIRED'
            ? 'Reattach or remove the highlighted media before publishing.'
            : mediaErr?.message === 'MEDIA_MISSING_FILE'
              ? 'Add media again or remove the expired media before publishing.'
              : mediaErr?.message || 'Reattach or remove the highlighted media before publishing.';
        setError(message);
        return;
      }
      const startDate = computeStartDate();
      const endDate = resolveAvailabilityEnd(
        startDate,
        availabilityEnd,
        availabilityEndMode,
      );
      const payloadBase = {
        title: isRideHelpFlow
          ? createFlowBlueprint.systemTitle
          : isGuidedTaskFlow
          ? title.trim() || createFlowBlueprint.systemTitle
          : title.trim(),
        description: isRideHelpFlow
          ? rideHelpGeneratedDescription
          : isGuidedTaskFlow
          ? guidedTaskGeneratedDescription
          : description.trim(),
        media,
        coverIndex: Number.isInteger(coverIndex) ? coverIndex : 0,
        visibility,
        trustedPlayers:
          visibility === 'trusted' ? trustedPlayers : [],
        availabilityEndMode,
        category:
          isRideHelpFlow || isGuidedTaskFlow
            ? resolvedTaskCategory || createFlowBlueprint.category
            : category.trim(),
        locationLabel: locationLabel || '',
      };
      const availabilityWindow = {
        startAt: startDate?.toISOString(),
        endAt: endDate?.toISOString(),
      };
      if (isOffering) {
        const payload = {
          offeringType: selectedMeta.offeringType,
          category: category.trim(),
          description: payloadBase.description || payloadBase.title || 'Offering',
          price: requestQuote ? undefined : numericPrice,
          requestQuote: false,
          quantityAvailable: parsedQuantity,
          location: {
            type: 'Point',
            coordinates: [location.lon, location.lat],
            address: pickupAddress.trim(),
          },
          deliverySupported: !!deliverySupported,
          availabilitySchedule: {
            windows: availabilityWindow.startAt
              ? [
                  {
                    startAt: availabilityWindow.startAt,
                    endAt: availabilityWindow.endAt,
                  },
                ]
              : [],
          },
          meta: {
            ...payloadBase,
            pickupAddress: pickupAddress.trim(),
          },
          offeringDraftToken: effectivePreset?.offeringDraftToken || undefined,
          offeringDraftId: effectivePreset?.offeringDraftId || undefined,
        };
        if (
          !hasSufficientBalance &&
          Number.isFinite(playerBalance)
        ) {
          const shortfallJGK = Math.max(0, escrowRequiredJGK - playerBalance);
          const shortfallUSD = resolveCreateShortfallUSD();
          emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_TOPUP_REQUIRED, {
            requiresTopUp: true,
            shortfallUSD,
          });
          emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_TOPUP_STARTED, {
            requiresTopUp: true,
            shortfallUSD,
            source: 'create_sheet',
          });
          if (onInsufficientBalance) {
            // eslint-disable-next-line no-console
            console.log('[jgk-intent] insufficient balance', {
              kind: 'create_offering',
              requiredJGK: escrowRequiredJGK,
              playerBalance,
            });
            onInsufficientBalance({
              type: 'create_offering',
              request: payload,
              requiredJGK: escrowRequiredJGK,
              topUp: {
                neededJGK: escrowRequiredJGK,
                shortfallJGK,
                jgkPerUsd: localOpsConfig?.jgkPerUsd,
              },
              uiContext: { sheet: 'create_offering', step },
            });
          } else {
            onRequestTopUp?.();
          }
          setIsSubmitting(false);
          publishInFlightRef.current = false;
          return;
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[LocalOpsCreateSheet][publish:offering:payload]', {
            step,
            pickupAddress: payload.location?.address,
            location: payload.location,
            metaPickupAddress: payload.meta?.pickupAddress,
          });
        }
        await onCreateOffering?.(payload);
        emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_SUCCEEDED, {
          actionContext: 'offering',
        });
      } else {
        const payload = {
          taskType: selectedMeta.taskType,
          paymentMode: communityTaskSelected
            ? LOCALOPS_TASK_PAYMENT_MODES.COMMUNITY
            : LOCALOPS_TASK_PAYMENT_MODES.PAID,
          title: payloadBase.title,
          description: payloadBase.description,
          originLocation: {
            type: 'Point',
            coordinates: [location.lon, location.lat],
            address: pickupAddress.trim(),
          },
          destinationLocation:
            isAssistance &&
            destination &&
            Number.isFinite(destination.lat) &&
            Number.isFinite(destination.lon)
              ? {
                  type: 'Point',
                  coordinates: [destination.lon, destination.lat],
                  address: destinationAddress.trim(),
                }
              : undefined,
          pickupAddress: pickupAddress.trim(),
          destinationAddress:
            isAssistance && destinationAddress.trim()
              ? destinationAddress.trim()
              : undefined,
          ...(communityTaskSelected ? {} : { price: numericPrice }),
          quoteRequested: false,
          availabilityWindow,
          // Presence Phase 2+: prioritize presence-active users by time/routing only.
          // Do NOT gate discovery or hide tasks from non-presence users.
          presenceRequirements: {
            requiresPresence: false,
            requiresTaskTypeOptIn: false,
          },
          liveTrackingEnabled: isAssistance ? true : !!liveTrackingEnabled,
          shareWithTrustedPlayers:
            visibility === 'trusted' ? trustedPlayers : [],
          trustRequirement:
            canShowRequireVerifiedLocal && requireVerifiedLocal
              ? 'verified_local'
              : 'open',
          radiusMeters: radiusMeters || undefined,
          intakeDraftToken: effectivePreset?.intakeDraftToken || undefined,
          uiContext: isCollisionSpecialistRidePreset
            ? { rideTemplate: COLLISION_SPECIALIST_RIDE_TEMPLATE }
            : isRideHelpFlow
            ? { starterId: LOCALOPS_RIDE_HELP_STARTER_ID }
            : undefined,
          meta: {
            ...payloadBase,
            pickupAddress: pickupAddress.trim(),
            deliveryAddress:
              isDelivery || guidedTaskHasDropoffAddress
                ? taskDeliveryAddressValue
                : undefined,
            ...(isRideHelpStarterPreset
              ? {
                  starterId: LOCALOPS_RIDE_HELP_STARTER_ID,
                  rideHelpStarter: true,
                }
              : {}),
          },
          firstCompletionAccessRequested:
            firstCompletionAccessSupported && firstCompletionAccessRequested
              ? true
              : undefined,
          status: 'discoverable',
        };
        if (
          !hasSufficientBalance &&
          Number.isFinite(playerBalance)
        ) {
          const shortfallJGK = Math.max(0, escrowRequiredJGK - playerBalance);
          const shortfallUSD = resolveCreateShortfallUSD();
          emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_TOPUP_REQUIRED, {
            requiresTopUp: true,
            shortfallUSD,
          });
          emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_TOPUP_STARTED, {
            requiresTopUp: true,
            shortfallUSD,
            source: 'create_sheet',
          });
          if (onInsufficientBalance) {
            // eslint-disable-next-line no-console
            console.log('[jgk-intent] insufficient balance', {
              kind: 'create_task',
              requiredJGK: escrowRequiredJGK,
              playerBalance,
            });
            onInsufficientBalance({
              type: 'create_task',
              request: payload,
              requiredJGK: escrowRequiredJGK,
              topUp: {
                neededJGK: escrowRequiredJGK,
                shortfallJGK,
                jgkPerUsd: localOpsConfig?.jgkPerUsd,
              },
              uiContext: {
                sheet: 'create_task',
                step,
                ...(isCollisionSpecialistRidePreset
                  ? { rideTemplate: COLLISION_SPECIALIST_RIDE_TEMPLATE }
                  : isRideHelpFlow
                  ? { starterId: LOCALOPS_RIDE_HELP_STARTER_ID }
                  : {}),
              },
            });
            persistHistoryExternal('task', payload);
          } else {
            onRequestTopUp?.();
          }
          setIsSubmitting(false);
          publishInFlightRef.current = false;
          return;
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[LocalOpsCreateSheet][publish:task:payload]', {
            step,
            pickupAddress: payload.originLocation?.address,
            destinationAddress: payload.destinationLocation?.address || payload.destinationAddress,
            originLocation: payload.originLocation,
            destinationLocation: payload.destinationLocation,
            metaPickupAddress: payload.meta?.pickupAddress,
            metaDeliveryAddress: payload.meta?.deliveryAddress,
            paymentMode: payload.paymentMode,
          });
          // eslint-disable-next-line no-console
          console.log(
            '[DEBUG][CLIENT TASK CREATE PAYLOAD]',
            JSON.stringify(maskSensitiveLocalOpsPayload(payload), null, 2),
          );
        }
        const createdTask = await onCreateTask?.(payload);
        emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_SUCCEEDED, {
          taskId: resolveCreatedEntityId(createdTask),
        });
      }
      if (historyMode) {
        const historyPayload = {
          title: title.trim(),
          description: description.trim(),
          category:
            isRideHelpFlow || isGuidedTaskFlow
              ? resolvedTaskCategory
              : category.trim(),
        };
        if (
          !communityTaskSelected &&
          (historyMode !== 'task' || numericPrice >= LOCALOPS_MIN_TASK_PRICE_USD) &&
          numericPrice !== null &&
          Number.isFinite(numericPrice)
        ) {
          historyPayload.price = String(numericPrice);
        }
        if (historyMode === 'offering') {
          historyPayload.quantity = String(parsedQuantity);
        }
        if (historyMode === 'task') {
          historyPayload.pickupAddress = pickupAddress.trim();
          if (isDelivery || guidedTaskHasDropoffAddress) {
            historyPayload.deliveryAddress = taskDeliveryAddressValue;
          }
          if (isAssistance && destinationAddress.trim()) {
            historyPayload.deliveryAddress = destinationAddress.trim();
          }
        }
        persistHistory(historyMode, historyPayload);
      }
      if (draftKey) {
        try {
          window.localStorage.removeItem(draftKey);
        } catch (err) {
          // ignore
        }
      }
      setIsSubmitting(false);
      publishInFlightRef.current = false;
      emitCreateJourneyClose('publish_success');
      onClose?.();
    } catch (err) {
      emitCreateFunnelEvent(LOCALOPS_FUNNEL_EVENTS.CREATE_PUBLISH_FAILED, {
        reason: resolvePublishFailureReason(err),
        httpStatus: err?.response?.status || err?.status,
      });
      setError(err.message || 'Publish failed.');
      setIsSubmitting(false);
      publishInFlightRef.current = false;
    }
  };

  const typeDisabled = (option) => {
    if (option.mode === 'offering') {
      if (disabledOfferings) return true;
      if (
        LOCALOPS_PRESENCE_GATING_ENABLED &&
        presenceSelf?.taskEligibility &&
        presenceSelf.taskEligibility.offering === false
      )
        return true;
      return false;
    }
    if (disabledTasks) return true;
    if (
      LOCALOPS_PRESENCE_GATING_ENABLED &&
      presenceSelf?.taskEligibility &&
      option.taskType
    ) {
      const eligible = presenceSelf.taskEligibility[option.taskType];
      if (eligible === false) return true;
    }
    return false;
  };
  const filteredFriends = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return friendOptions;
    return friendOptions.filter((friend) => {
      const haystack = [
        friend.displayName,
        friend.playerId,
        friend.label,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [friendOptions, searchTerm]);

  const renderGuidedPickupResolver = () => (
    <>
      <div className="localops-field">
        <label>{createFlowBlueprint.pickupLabel}</label>
        <input
          value={pickupAddress}
          onChange={(e) => handlePickupAddressChange(e.target.value)}
          placeholder={
            createFlowBlueprint.pickupPlaceholder || 'Street, city, state'
          }
          list="localops-pickup-history"
          autoComplete="street-address"
          name="localops-pickup-address"
        />
        <datalist id="localops-pickup-history">
          {pickupHistory.map((item, index) => (
            <option value={item} key={`localops-pickup-${index}`} />
          ))}
        </datalist>
        {originResolution.candidates.length ? (
          <div
            className="localops-origin-candidates"
            data-testid="localops-origin-candidates"
          >
            {originResolution.candidates.map((candidate, index) => {
              const primary = candidateDisplayText(candidate);
              const secondary = candidateSecondaryText(candidate);
              return (
                <button
                  type="button"
                  className="localops-origin-candidate"
                  key={candidate.id || `${primary}-${index}`}
                  onClick={() => handleSelectOriginCandidate(candidate)}
                >
                  <span className="localops-origin-candidate__main">
                    {primary}
                  </span>
                  {secondary ? (
                    <span className="localops-origin-candidate__secondary">
                      {secondary}
                    </span>
                  ) : null}
                  <span className="localops-origin-candidate__action">
                    Select to confirm
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
        {originResolution.message ? (
          <div className="localops-helper localops-origin-resolution">
            {originResolution.message}
          </div>
        ) : null}
        <div className="localops-helper">
          {createFlowBlueprint.pickupHelper}
        </div>
        <div className="localops-helper">
          {isPickupGeocoding
            ? 'Finding local address matches...'
            : originLocationConfirmed
            ? 'Location confirmed.'
            : 'Select a local match to confirm location.'}
        </div>
        {originPinOutsideLocalArea ? (
          <div className="localops-error">
            {LOCALOPS_PIN_OUTSIDE_LOCAL_AREA_MESSAGE}
          </div>
        ) : originLocationConfirmed ? (
          <div className="localops-earn-row">
            <CheckCircleOutlineIcon
              className="localops-earn-icon"
              fontSize="small"
            />
            <span>{pinNotice || 'Location confirmed.'}</span>
          </div>
        ) : pinNotice ? (
          <div className="localops-earn-row">
            <CheckCircleOutlineIcon
              className="localops-earn-icon"
              fontSize="small"
            />
            <span>{pinNotice}</span>
          </div>
        ) : null}
        {!pickupCoordsValid && pinError ? (
          <div className="localops-error">{pinError}</div>
        ) : null}
      </div>
    </>
  );

  const renderGuidedOfferingPrimarySection = () => (
    <div className="localops-section">
      <h4>{createFlowBlueprint.primaryHeading}</h4>
      <div className="localops-row">
        <div className="localops-field">
          <label>{createFlowBlueprint.titleLabel}</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={createFlowBlueprint.titlePlaceholder}
            list="localops-title-history"
            autoComplete="on"
            name="localops-offering-title"
            onFocus={() => {
              if (!title.trim()) setFocusedField('title');
            }}
            onBlur={() => setFocusedField(null)}
          />
          <datalist id="localops-title-history">
            {titleHistory.map((item, index) => (
              <option value={item} key={`localops-title-${index}`} />
            ))}
          </datalist>
          {focusedField === 'title' && !title.trim() && recentTitleChips.length ? (
            <div className="localops-input-history">
              <div className="localops-input-history__label">Recent</div>
              <div className="localops-input-history__list">
                {recentTitleChips.map((item, index) => (
                  <button
                    key={`localops-title-chip-${index}`}
                    type="button"
                    className="localops-input-history__chip"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      setTitle(item);
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <div className="localops-helper">
            {createFlowBlueprint.titleHelper}
          </div>
        </div>
        <div className="localops-field">
          <label>{createFlowBlueprint.categoryLabel}</label>
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Select or type a category"
            list="localops-category-list"
            autoComplete="on"
            name="localops-offering-category"
          />
          <datalist id="localops-category-list">
            {categoryOptions.map((item) => (
              <option value={item} key={item} />
            ))}
          </datalist>
          <div className="localops-helper">
            {createFlowBlueprint.categoryHelper}
          </div>
        </div>
      </div>
    </div>
  );

  const renderGuidedOfferingPriceSection = () => (
    <div className="localops-section">
      <h4>{createFlowBlueprint.priceHeading}</h4>
      <div className="localops-row">
        <div className="localops-field">
          <label>{createFlowBlueprint.priceLabel}</label>
          <input
            type="number"
            value={price}
            onChange={(e) => handlePriceInputChange(e.target.value)}
            min="0"
            step="0.01"
            aria-invalid={
              price !== '' &&
              (!Number.isFinite(parsedPrice) || parsedPrice < 0)
                ? 'true'
                : undefined
            }
            list="localops-price-history"
            autoComplete="on"
            name="localops-offering-price"
            onWheel={(e) => {
              e.target.blur();
              e.preventDefault();
            }}
          />
          <datalist id="localops-price-history">
            {priceHistory.map((item, index) => (
              <option value={item} key={`localops-price-${index}`} />
            ))}
          </datalist>
          <div className="localops-helper">
            {createFlowBlueprint.priceHelper}
          </div>
        </div>
        <div className="localops-field">
          <label>{createFlowBlueprint.quantityLabel}</label>
          <input
            type="number"
            min="1"
            step="1"
            value={quantityAvailable}
            onChange={(e) => setQuantityAvailable(e.target.value)}
            name="localops-offering-quantity"
          />
          <div className="localops-helper">
            {createFlowBlueprint.quantityHelper}
          </div>
        </div>
      </div>
    </div>
  );

  const renderGuidedOfferingAvailabilitySection = () => (
    <div className="localops-section">
      <h4>{createFlowBlueprint.availabilityHeading}</h4>
      <div className="localops-helper">
        {createFlowBlueprint.availabilityHelper}
      </div>
      <div className="localops-row">
        <div className="localops-field">
          <label>{createFlowBlueprint.availabilityStartLabel}</label>
          <input
            type="datetime-local"
            value={availabilityStart}
            onChange={(e) => {
              setAvailabilityStart(e.target.value);
              setCustomStart(!!e.target.value);
            }}
          />
        </div>
        <div className="localops-field">
          <label>{createFlowBlueprint.availabilityEndLabel}</label>
          <select
            value={availabilityEndMode}
            onChange={(e) => setAvailabilityEndMode(e.target.value)}
          >
            {END_MODE_OPTIONS.map((option) => (
              <option value={option.id} key={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {availabilityEndMode === 'specific' ? (
        <div className="localops-row">
          <div className="localops-field">
            <label>End</label>
            <input
              type="datetime-local"
              value={availabilityEnd}
              onChange={(e) => setAvailabilityEnd(e.target.value)}
            />
          </div>
        </div>
      ) : null}
      {availabilityEndMode !== 'specific' ? (
        <div className="localops-helper">
          {labelForEndMode(availabilityEndMode)} keeps this available while
          buyers decide.
        </div>
      ) : null}
    </div>
  );

  const renderGuidedOfferingFulfillmentSection = () => (
    <div className="localops-section">
      <h4>{createFlowBlueprint.fulfillmentHeading}</h4>
      {renderGuidedPickupResolver()}
      <div className="localops-field">
        <label className="localops-pill-toggle">
          <input
            type="checkbox"
            checked={deliverySupported}
            onChange={(e) => setDeliverySupported(e.target.checked)}
          />
          {createFlowBlueprint.deliveryLabel}
        </label>
        <div className="localops-helper">
          {createFlowBlueprint.deliveryHelper}
        </div>
      </div>
    </div>
  );

  const renderGuidedOfferingDetailsSection = () => (
    <div className="localops-section">
      <h4>{createFlowBlueprint.detailsHeading}</h4>
      <div className="localops-field">
        <label>{createFlowBlueprint.detailsLabel}</label>
        {stepTwoGuidance?.chips?.length ? (
          <div
            className="localops-guidance-chips"
            aria-label="Offering details checklist"
          >
            {stepTwoGuidance.chips.map((chip) => (
              <span className="localops-guidance-chip" key={chip}>
                {chip}
              </span>
            ))}
          </div>
        ) : null}
        <textarea
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            setDescriptionAuto(false);
          }}
          placeholder={createFlowBlueprint.detailsPlaceholder}
          autoComplete="on"
          name="localops-offering-description"
          onFocus={() => setFocusedField('description')}
          onBlur={() => setFocusedField(null)}
        />
      </div>
      {descriptionAuto === false &&
      title.trim() &&
      description.trim() !== title.trim() ? (
        <div className="localops-input-history">
          <button
            type="button"
            className="localops-input-history__chip"
            onMouseDown={(event) => {
              event.preventDefault();
              setDescription(title);
              setDescriptionAuto(false);
            }}
          >
            Use offering name as details
          </button>
        </div>
      ) : null}
      {showDescriptionHistory ? (
        <div className="localops-input-history">
          <div className="localops-input-history__label">Recent</div>
          <div className="localops-input-history__list">
            {descriptionSuggestions.map((item, index) => (
              <button
                key={`localops-desc-${index}`}
                type="button"
                className="localops-input-history__chip"
                onMouseDown={(event) => {
                  event.preventDefault();
                  setDescription(item);
                  setDescriptionAuto(false);
                }}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );

  const renderGuidedOfferingMediaSection = () => (
    <LocalOpsMediaPicker
      mediaItems={mediaItems}
      coverIndex={coverIndex}
      onCoverChange={setCoverIndex}
      onRemove={removeMedia}
      onInvalidate={invalidateMedia}
      onAddFiles={addFiles}
      error={error}
      uploadPct={uploadPct}
      warning={mediaWarning}
      helperText={createFlowBlueprint.mediaHelper}
    />
  );

  const renderGuidedOfferingSection = (section) => {
    if (section === 'primary') return renderGuidedOfferingPrimarySection();
    if (section === 'price') return renderGuidedOfferingPriceSection();
    if (section === 'availability') {
      return renderGuidedOfferingAvailabilitySection();
    }
    if (section === 'fulfillment') {
      return renderGuidedOfferingFulfillmentSection();
    }
    if (section === 'details') return renderGuidedOfferingDetailsSection();
    if (section === 'media') return renderGuidedOfferingMediaSection();
    return null;
  };

  return (
    <LocalOpsSheet
      open={open}
      onClose={handleSheetClose}
      title={headerTitle}
      className={sheetClassName}
      headerRight={
        <button
          type="button"
          className="localops-info-btn"
          onClick={() => setShowInfo((prev) => !prev)}
          aria-label="What is LocalOps"
        >
          <InfoOutlinedIcon fontSize="small" />
        </button>
      }
    >
      <div className="localops-section">
        <div className="localops-helper">Step {step + 1} of 3</div>
      </div>
      {showInfoCard ? (
        <div className="localops-info-card">{INFO_COPY}</div>
      ) : null}
      {step === 0 ? (
        <div className="localops-section">
          <h4>
            {normalizedLaunchGroupHint === 'offerings'
              ? 'Choose an offering type'
              : normalizedLaunchGroupHint === 'tasks'
              ? 'Choose a task type'
              : 'Choose a type'}
          </h4>
          <div className="localops-helper">
            {normalizedLaunchGroupHint === 'offerings'
              ? 'Offerings are what you provide.'
              : normalizedLaunchGroupHint === 'tasks'
              ? 'Tasks are what you need done.'
              : 'Offerings are what you provide. Tasks are what you need done.'}
          </div>
          {normalizedLaunchGroupHint !== 'offerings' ? (
            <div className="localops-type-group">
              <div className="localops-type-group__title">Tasks</div>
              <div className="localops-type-grid">
                {TYPE_OPTIONS.filter((opt) => opt.group === 'tasks').map((opt) => {
                  const Icon = opt.Icon;
                  const isDisabled = typeDisabled(opt);
                  const isActive = selectedType === opt.id;
                  return (
                    <div
                      key={opt.id}
                      role="button"
                      tabIndex={isDisabled ? -1 : 0}
                      aria-disabled={isDisabled}
                      className={`localops-type-card ${
                        isActive ? 'is-active' : ''
                      } ${isDisabled ? 'is-disabled' : ''}`}
                      onClick={() => {
                        if (isDisabled) return;
                        handleSelectType(opt.id);
                      }}
                      onKeyDown={(event) => {
                        if (isDisabled) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleSelectType(opt.id);
                        }
                      }}
                    >
                      <span className="localops-type-card__icon">
                        <Icon fontSize="small" />
                      </span>
                      <span className="localops-type-card__body">
                        <span className="localops-type-card__title">
                          {opt.label}
                        </span>
                        <span className="localops-type-card__desc">
                          {opt.description}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="localops-type-card__info"
                        data-tooltip={opt.tooltip}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!mobileView) return;
                          setActiveTypeInfo((prev) =>
                            prev === opt.id ? null : opt.id,
                          );
                        }}
                        aria-label={`${opt.label} info`}
                      >
                        i
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          {normalizedLaunchGroupHint !== 'tasks' ? (
            <div className="localops-type-group">
              <div className="localops-type-group__title">Offerings</div>
              <div className="localops-type-grid">
                {TYPE_OPTIONS.filter((opt) => opt.group === 'offerings').map(
                  (opt) => {
                    const Icon = opt.Icon;
                    const isDisabled = typeDisabled(opt);
                    const isActive = selectedType === opt.id;
                    return (
                      <div
                        key={opt.id}
                        role="button"
                        tabIndex={isDisabled ? -1 : 0}
                        aria-disabled={isDisabled}
                        className={`localops-type-card ${
                          isActive ? 'is-active' : ''
                        } ${isDisabled ? 'is-disabled' : ''}`}
                        onClick={() => {
                          if (isDisabled) return;
                          handleSelectType(opt.id);
                        }}
                        onKeyDown={(event) => {
                          if (isDisabled) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleSelectType(opt.id);
                          }
                        }}
                      >
                        <span className="localops-type-card__icon">
                          <Icon fontSize="small" />
                        </span>
                        <span className="localops-type-card__body">
                          <span className="localops-type-card__title">
                            {opt.label}
                          </span>
                          <span className="localops-type-card__desc">
                            {opt.description}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="localops-type-card__info"
                          data-tooltip={opt.tooltip}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!mobileView) return;
                            setActiveTypeInfo((prev) =>
                              prev === opt.id ? null : opt.id,
                            );
                          }}
                          aria-label={`${opt.label} info`}
                        >
                          i
                        </button>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          ) : null}
          {mobileView && activeTypeInfo ? (
            <div className="localops-info-card">
              {TYPE_OPTIONS.find((opt) => opt.id === activeTypeInfo)?.tooltip}
            </div>
          ) : null}
        </div>
      ) : null}

      {step === 1 && selectedMeta ? (
        <div
          data-testid="localops-create-flow"
          data-flow-blueprint-id={createFlowBlueprint.id}
        >
          {stepTwoGuidance ? (
            <div
              className="localops-task-brief"
              data-testid="localops-step2-brief"
            >
              <div>
                <div className="localops-task-brief__eyebrow">
                  {isOffering ? 'Offering Brief' : 'Task Brief'}
                </div>
                <h4>{stepTwoGuidance.title}</h4>
                <p>{stepTwoGuidance.copy}</p>
              </div>
              <ul className="localops-task-brief__list">
                {stepTwoGuidance.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {isRideHelpFlow ? (
            <div
              className="localops-ride-step2-flow"
              data-testid="localops-ride-help-step2-flow"
            >
              <div className="localops-section">
                <h4>Pickup</h4>
                <div className="localops-field">
                  <label>Pickup address or place</label>
                  <input
                    value={pickupAddress}
                    onChange={(e) => handlePickupAddressChange(e.target.value)}
                    placeholder="Street, city, state"
                    autoComplete="off"
                    name="localops-pickup-address"
                  />
                  {originResolution.candidates.length ? (
                    <div
                      className="localops-origin-candidates"
                      data-testid="localops-origin-candidates"
                    >
                      {originResolution.candidates.map((candidate, index) => {
                        const primary = candidateDisplayText(candidate);
                        const secondary = candidateSecondaryText(candidate);
                        return (
                          <button
                            type="button"
                            className="localops-origin-candidate"
                            key={candidate.id || `${primary}-${index}`}
                            onClick={() => handleSelectOriginCandidate(candidate)}
                          >
                            <span className="localops-origin-candidate__main">
                              {primary}
                            </span>
                            {secondary ? (
                              <span className="localops-origin-candidate__secondary">
                                {secondary}
                              </span>
                            ) : null}
                            <span className="localops-origin-candidate__action">
                              Select pickup
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  {originResolution.message ? (
                    <div className="localops-helper localops-origin-resolution">
                      {originResolution.message}
                    </div>
                  ) : null}
                  <div className="localops-helper">
                    Shared only after a helper accepts.
                  </div>
                  <div className="localops-helper">
                    {isPickupGeocoding
                      ? 'Finding local pickup matches...'
                      : originLocationConfirmed
                      ? 'Pickup confirmed.'
                      : 'Select a local match to confirm pickup.'}
                  </div>
                  {originPinOutsideLocalArea ? (
                    <div className="localops-error">
                      {LOCALOPS_PIN_OUTSIDE_LOCAL_AREA_MESSAGE}
                    </div>
                  ) : originLocationConfirmed ? (
                    <div className="localops-earn-row">
                      <CheckCircleOutlineIcon
                        className="localops-earn-icon"
                        fontSize="small"
                      />
                      <span>{pinNotice || 'Pickup confirmed.'}</span>
                    </div>
                  ) : pinNotice ? (
                    <div className="localops-earn-row">
                      <CheckCircleOutlineIcon
                        className="localops-earn-icon"
                        fontSize="small"
                      />
                      <span>{pinNotice}</span>
                    </div>
                  ) : null}
                  {!pickupCoordsValid && pinError ? (
                    <div className="localops-error">{pinError}</div>
                  ) : null}
                </div>
              </div>

              <div className="localops-section">
                <h4>Destination</h4>
                <div className="localops-field">
                  <label>Destination address or place</label>
                  <input
                    value={destinationAddress}
                    onChange={(e) =>
                      handleDestinationAddressChange(e.target.value)
                    }
                    placeholder="Street, city, state"
                    autoComplete="off"
                    name="localops-destination-address"
                  />
                  {destinationResolution.candidates.length ? (
                    <div
                      className="localops-origin-candidates"
                      data-testid="localops-destination-candidates"
                    >
                      {destinationResolution.candidates.map((candidate, index) => {
                        const primary = candidateDisplayText(candidate);
                        const secondary = candidateSecondaryText(candidate);
                        return (
                          <button
                            type="button"
                            className="localops-origin-candidate"
                            key={candidate.id || `${primary}-${index}`}
                            onClick={() =>
                              handleSelectDestinationCandidate(candidate)
                            }
                          >
                            <span className="localops-origin-candidate__main">
                              {primary}
                            </span>
                            {secondary ? (
                              <span className="localops-origin-candidate__secondary">
                                {secondary}
                              </span>
                            ) : null}
                            <span className="localops-origin-candidate__action">
                              Select destination
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  {destinationResolution.message ? (
                    <div className="localops-helper localops-origin-resolution">
                      {destinationResolution.message}
                    </div>
                  ) : null}
                  <div className="localops-helper">
                    Type a place name or address, then select a local match.
                    Shared only after a helper accepts.
                  </div>
                  <div className="localops-helper">
                    {isDestinationGeocoding
                      ? 'Finding local destination matches...'
                      : assistanceDestinationConfirmed
                      ? 'Destination confirmed.'
                      : 'Select a local destination match before posting.'}
                  </div>
                  {assistanceDestinationConfirmed ? (
                    <div className="localops-earn-row">
                      <CheckCircleOutlineIcon className="localops-earn-icon" />
                      <div className="localops-earn-text">
                        {destinationPinNotice || 'Destination confirmed.'}
                      </div>
                    </div>
                  ) : destinationPinNotice ? (
                    <div className="localops-earn-row">
                      <CheckCircleOutlineIcon className="localops-earn-icon" />
                      <div className="localops-earn-text">
                        {destinationPinNotice}
                      </div>
                    </div>
                  ) : null}
                  {!assistanceDestinationConfirmed && destinationPinError ? (
                    <div className="localops-error">{destinationPinError}</div>
                  ) : null}
                </div>
              </div>

              <div className="localops-section">
                <h4>Timing (optional)</h4>
                <div className="localops-helper">
                  Defaults to now and stays ongoing. Change only if timing matters.
                </div>
                <div className="localops-row">
                  <div className="localops-field">
                    <label>Need ride around (optional)</label>
                    <input
                      type="datetime-local"
                      value={availabilityStart}
                      onChange={(e) => {
                        setAvailabilityStart(e.target.value);
                        setCustomStart(!!e.target.value);
                      }}
                    />
                  </div>
                  <div className="localops-field">
                    <label>Keep request live</label>
                    <select
                      value={availabilityEndMode}
                      onChange={(e) => setAvailabilityEndMode(e.target.value)}
                    >
                      {END_MODE_OPTIONS.map((option) => (
                        <option value={option.id} key={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {availabilityEndMode === 'specific' ? (
                  <div className="localops-row">
                    <div className="localops-field">
                      <label>End</label>
                      <input
                        type="datetime-local"
                        value={availabilityEnd}
                        onChange={(e) => setAvailabilityEnd(e.target.value)}
                      />
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="localops-section">
                <h4>Suggested amount</h4>
                <div className="localops-row">
                  <div className="localops-field">
                    <label>Suggested amount ($)</label>
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => handlePriceInputChange(e.target.value)}
                      min={LOCALOPS_MIN_TASK_PRICE_USD}
                      step="0.01"
                      aria-invalid={
                        price !== '' && !taskPriceIsValid ? 'true' : undefined
                      }
                      list="localops-price-history"
                      autoComplete="on"
                      name="localops-task-price"
                      onWheel={(e) => {
                        e.target.blur();
                        e.preventDefault();
                      }}
                    />
                    <datalist id="localops-price-history">
                      {priceHistory.map((item, index) => (
                        <option
                          value={item}
                          key={`localops-price-${index}`}
                        />
                      ))}
                    </datalist>
                    <div className="localops-helper">
                      {Number.isFinite(rideSuggestedPrice) &&
                      rideSuggestedDistanceLabel
                        ? `Suggested amount: ${rideReviewAmountLabel}. Based on about ${rideSuggestedDistanceLabel} at $2/mile.`
                        : isRidePriceGeocoding
                        ? 'Estimating suggested amount from pickup and destination...'
                        : 'Add a confirmed pickup and destination to estimate a suggested amount.'}
                    </div>
                  </div>
                </div>
                {hasPaidTaskAmount ? (
                  <LocalOpsCompletionFlowHint variant="paidRequester" compact />
                ) : (
                  <div className="localops-helper">
                    {`Enter ${LOCALOPS_MIN_TASK_PRICE_LABEL} or more to fund this task before continuing.`}
                  </div>
                )}
              </div>

              <div className="localops-section">
                <h4>Notes for helper (optional)</h4>
                <div className="localops-field">
                  <label>Comments, rider details, or access notes (optional)</label>
                  <textarea
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setDescriptionAuto(false);
                    }}
                    placeholder="Anything the helper should know?"
                    autoComplete="on"
                    aria-invalid={
                      taskDescriptionOverLimit ? 'true' : undefined
                    }
                    aria-describedby="localops-task-description-counter"
                    name="localops-task-description"
                    onFocus={() => setFocusedField('description')}
                    onBlur={() => setFocusedField(null)}
                  />
                  <div
                    id="localops-task-description-counter"
                    className={`localops-character-count ${
                      taskDescriptionOverLimit ? 'is-over-limit' : ''
                    }`}
                    role={taskDescriptionOverLimit ? 'alert' : undefined}
                  >
                    {taskDescriptionLength.toLocaleString('en-US')} /{' '}
                    {LOCALOPS_TASK_DESCRIPTION_MAX_LENGTH.toLocaleString(
                      'en-US',
                    )}{' '}
                    characters
                  </div>
                </div>
                {showDescriptionHistory ? (
                  <div className="localops-input-history">
                    <div className="localops-input-history__label">Recent</div>
                    <div className="localops-input-history__list">
                      {descriptionSuggestions.map((item, index) => (
                        <button
                          key={`localops-desc-${index}`}
                          type="button"
                          className="localops-input-history__chip"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            setDescription(item);
                            setDescriptionAuto(false);
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          {isGuidedTaskFlow ? (
            <div
              className="localops-guided-task-step2-flow"
              data-testid="localops-guided-task-step2-flow"
            >
              <div className="localops-section">
                <h4>{createFlowBlueprint.primaryHeading}</h4>
                {createFlowBlueprint.primaryFieldMode === 'pickup_resolver' ? (
                  renderGuidedPickupResolver()
                ) : (
                  <div className="localops-field">
                    <label>{createFlowBlueprint.titleLabel}</label>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={createFlowBlueprint.titlePlaceholder}
                      list="localops-title-history"
                      autoComplete="on"
                      name="localops-task-title"
                      onFocus={() => {
                        if (!title.trim()) setFocusedField('title');
                      }}
                      onBlur={() => setFocusedField(null)}
                    />
                    <datalist id="localops-title-history">
                      {titleHistory.map((item, index) => (
                        <option
                          value={item}
                          key={`localops-title-${index}`}
                        />
                      ))}
                    </datalist>
                    {focusedField === 'title' && !title.trim() && recentTitleChips.length ? (
                      <div className="localops-input-history">
                        <div className="localops-input-history__label">Recent</div>
                        <div className="localops-input-history__list">
                          {recentTitleChips.map((item, index) => (
                            <button
                              key={`localops-title-chip-${index}`}
                              type="button"
                              className="localops-input-history__chip"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                setTitle(item);
                              }}
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <div className="localops-helper">
                      {createFlowBlueprint.titleHelper}
                    </div>
                  </div>
                )}
              </div>

              {createFlowBlueprint.mediaMode === 'inline' ? (
                <LocalOpsMediaPicker
                  mediaItems={mediaItems}
                  coverIndex={coverIndex}
                  onCoverChange={setCoverIndex}
                  onRemove={removeMedia}
                  onInvalidate={invalidateMedia}
                  onAddFiles={addFiles}
                  error={error}
                  uploadPct={uploadPct}
                  warning={mediaWarning}
                  helperText={createFlowBlueprint.mediaHelper}
                />
              ) : null}

              {guidedTaskHasDropoffAddress ? (
                <div className="localops-section">
                  <h4>{createFlowBlueprint.dropoffHeading || 'Drop-off'}</h4>
                  <div className="localops-field">
                    <label>
                      {createFlowBlueprint.dropoffLabel ||
                        'Drop-off address (private)'}
                    </label>
                    <input
                      value={destinationAddress}
                      onChange={(e) =>
                        handleDestinationAddressChange(e.target.value)
                      }
                      placeholder={
                        createFlowBlueprint.dropoffPlaceholder ||
                        'Street, city, state'
                      }
                      list="localops-destination-history"
                      autoComplete="street-address"
                      name="localops-destination-address"
                    />
                    <datalist id="localops-destination-history">
                      {destinationHistory.map((item, index) => (
                        <option
                          value={item}
                          key={`localops-destination-${index}`}
                        />
                      ))}
                    </datalist>
                    {destinationResolution.candidates.length ? (
                      <div
                        className="localops-origin-candidates"
                        data-testid="localops-destination-candidates"
                      >
                        {destinationResolution.candidates.map((candidate, index) => {
                          const primary = candidateDisplayText(candidate);
                          const secondary = candidateSecondaryText(candidate);
                          return (
                            <button
                              type="button"
                              className="localops-origin-candidate"
                              key={candidate.id || `${primary}-${index}`}
                              onClick={() =>
                                handleSelectDestinationCandidate(candidate)
                              }
                            >
                              <span className="localops-origin-candidate__main">
                                {primary}
                              </span>
                              {secondary ? (
                                <span className="localops-origin-candidate__secondary">
                                  {secondary}
                                </span>
                              ) : null}
                              <span className="localops-origin-candidate__action">
                                Select drop-off
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                    {destinationResolution.message ? (
                      <div className="localops-helper localops-origin-resolution">
                        {destinationResolution.message}
                      </div>
                    ) : null}
                    <div className="localops-helper">
                      {createFlowBlueprint.dropoffHelper ||
                        'Where should this be delivered? Shared only after a helper accepts.'}
                    </div>
                    <div className="localops-helper">
                      {isDestinationGeocoding
                        ? 'Finding local drop-off matches...'
                        : assistanceDestinationConfirmed
                        ? destinationConfirmedNotice
                        : 'Select a local drop-off match to confirm.'}
                    </div>
                    {assistanceDestinationConfirmed ? (
                      <div className="localops-earn-row">
                        <CheckCircleOutlineIcon
                          className="localops-earn-icon"
                          fontSize="small"
                        />
                        <span>{destinationPinNotice || destinationConfirmedNotice}</span>
                      </div>
                    ) : destinationPinNotice ? (
                      <div className="localops-earn-row">
                        <CheckCircleOutlineIcon
                          className="localops-earn-icon"
                          fontSize="small"
                        />
                        <span>{destinationPinNotice}</span>
                      </div>
                    ) : null}
                    {!assistanceDestinationConfirmed && destinationPinError ? (
                      <div className="localops-error">{destinationPinError}</div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {createFlowBlueprint.locationMode !== 'primary_resolver' ? (
              <div className="localops-section">
                <h4>{createFlowBlueprint.locationHeading}</h4>
                <div className="localops-field">
                  <label>{createFlowBlueprint.pickupLabel}</label>
                  <input
                    value={pickupAddress}
                    onChange={(e) => handlePickupAddressChange(e.target.value)}
                    placeholder="Street, city, state"
                    list="localops-pickup-history"
                    autoComplete="street-address"
                    name="localops-pickup-address"
                  />
                  <datalist id="localops-pickup-history">
                    {pickupHistory.map((item, index) => (
                      <option
                        value={item}
                        key={`localops-pickup-${index}`}
                      />
                    ))}
                  </datalist>
                  {originResolution.candidates.length ? (
                    <div
                      className="localops-origin-candidates"
                      data-testid="localops-origin-candidates"
                    >
                      {originResolution.candidates.map((candidate, index) => {
                        const primary = candidateDisplayText(candidate);
                        const secondary = candidateSecondaryText(candidate);
                        return (
                          <button
                            type="button"
                            className="localops-origin-candidate"
                            key={candidate.id || `${primary}-${index}`}
                            onClick={() => handleSelectOriginCandidate(candidate)}
                          >
                            <span className="localops-origin-candidate__main">
                              {primary}
                            </span>
                            {secondary ? (
                              <span className="localops-origin-candidate__secondary">
                                {secondary}
                              </span>
                            ) : null}
                            <span className="localops-origin-candidate__action">
                              Select to confirm
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  {originResolution.message ? (
                    <div className="localops-helper localops-origin-resolution">
                      {originResolution.message}
                    </div>
                  ) : null}
                  <div className="localops-helper">
                    {createFlowBlueprint.pickupHelper}
                  </div>
                  <div className="localops-helper">
                    {isPickupGeocoding
                      ? 'Finding local address matches...'
                      : originLocationConfirmed
                      ? 'Location confirmed.'
                      : 'Select a local match to confirm location.'}
                  </div>
                  {originPinOutsideLocalArea ? (
                    <div className="localops-error">
                      {LOCALOPS_PIN_OUTSIDE_LOCAL_AREA_MESSAGE}
                    </div>
                  ) : originLocationConfirmed ? (
                    <div className="localops-earn-row">
                      <CheckCircleOutlineIcon
                        className="localops-earn-icon"
                        fontSize="small"
                      />
                      <span>{pinNotice || 'Location confirmed.'}</span>
                    </div>
                  ) : pinNotice ? (
                    <div className="localops-earn-row">
                      <CheckCircleOutlineIcon
                        className="localops-earn-icon"
                        fontSize="small"
                      />
                      <span>{pinNotice}</span>
                    </div>
                  ) : null}
                  {!pickupCoordsValid && pinError ? (
                    <div className="localops-error">{pinError}</div>
                  ) : null}
                </div>

                {isDelivery ? (
                  <div className="localops-field">
                    <label>
                      {createFlowBlueprint.deliveryLabel ||
                        'Delivery address (private)'}
                    </label>
                    <input
                      value={destinationAddress}
                      onChange={(e) =>
                        handleDestinationAddressChange(e.target.value)
                      }
                      placeholder="Street, city, state"
                      list="localops-delivery-history"
                      autoComplete="street-address"
                      name="localops-delivery-address"
                    />
                    <datalist id="localops-delivery-history">
                      {deliveryHistory.map((item, index) => (
                        <option
                          value={item}
                          key={`localops-delivery-${index}`}
                        />
                      ))}
                    </datalist>
                    {destinationResolution.candidates.length ? (
                      <div
                        className="localops-origin-candidates"
                        data-testid="localops-destination-candidates"
                      >
                        {destinationResolution.candidates.map((candidate, index) => {
                          const primary = candidateDisplayText(candidate);
                          const secondary = candidateSecondaryText(candidate);
                          return (
                            <button
                              type="button"
                              className="localops-origin-candidate"
                              key={candidate.id || `${primary}-${index}`}
                              onClick={() =>
                                handleSelectDestinationCandidate(candidate)
                              }
                            >
                              <span className="localops-origin-candidate__main">
                                {primary}
                              </span>
                              {secondary ? (
                                <span className="localops-origin-candidate__secondary">
                                  {secondary}
                                </span>
                              ) : null}
                              <span className="localops-origin-candidate__action">
                                Select drop-off
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                    {destinationResolution.message ? (
                      <div className="localops-helper localops-origin-resolution">
                        {destinationResolution.message}
                      </div>
                    ) : null}
                    <div className="localops-helper">
                      {createFlowBlueprint.deliveryHelper ||
                        'Where should the item be delivered? This is only shared after a helper accepts.'}
                    </div>
                    <div className="localops-helper">
                      {isDestinationGeocoding
                        ? 'Finding local drop-off matches...'
                        : assistanceDestinationConfirmed
                        ? destinationConfirmedNotice
                        : 'Select a local drop-off match to confirm.'}
                    </div>
                    {assistanceDestinationConfirmed ? (
                      <div className="localops-earn-row">
                        <CheckCircleOutlineIcon
                          className="localops-earn-icon"
                          fontSize="small"
                        />
                        <span>{destinationPinNotice || destinationConfirmedNotice}</span>
                      </div>
                    ) : destinationPinNotice ? (
                      <div className="localops-earn-row">
                        <CheckCircleOutlineIcon
                          className="localops-earn-icon"
                          fontSize="small"
                        />
                        <span>{destinationPinNotice}</span>
                      </div>
                    ) : null}
                    {!assistanceDestinationConfirmed && destinationPinError ? (
                      <div className="localops-error">{destinationPinError}</div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              ) : null}

              <div className="localops-section">
                <h4>Timing (optional)</h4>
                <div className="localops-helper">
                  Defaults to now and stays ongoing. Change only if timing matters.
                </div>
                <div className="localops-row">
                  <div className="localops-field">
                    <label>{createFlowBlueprint.timingStartLabel}</label>
                    <input
                      type="datetime-local"
                      value={availabilityStart}
                      onChange={(e) => {
                        setAvailabilityStart(e.target.value);
                        setCustomStart(!!e.target.value);
                      }}
                    />
                  </div>
                  <div className="localops-field">
                    <label>Keep request live</label>
                    <select
                      value={availabilityEndMode}
                      onChange={(e) => setAvailabilityEndMode(e.target.value)}
                    >
                      {END_MODE_OPTIONS.map((option) => (
                        <option value={option.id} key={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {availabilityEndMode === 'specific' ? (
                  <div className="localops-row">
                    <div className="localops-field">
                      <label>End</label>
                      <input
                        type="datetime-local"
                        value={availabilityEnd}
                        onChange={(e) => setAvailabilityEnd(e.target.value)}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          {isGuidedOfferingFlow ? (
            <div
              className="localops-guided-offering-step2-flow"
              data-testid="localops-guided-offering-step2-flow"
            >
              {createFlowBlueprint.sectionOrder.map((section) => {
                const rendered = renderGuidedOfferingSection(section);
                return rendered ? (
                  <React.Fragment key={section}>{rendered}</React.Fragment>
                ) : null;
              })}
            </div>
          ) : null}
          {!isRideHelpFlow && !isGuidedTaskFlow && !isGuidedOfferingFlow ? (
          <div className="localops-section">
            <h4>Details</h4>
            <div className="localops-row">
              <div className="localops-field">
                <label>Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Short title"
                  list="localops-title-history"
                  autoComplete="on"
                  name={
                    isOffering
                      ? 'localops-offering-title'
                      : 'localops-task-title'
                  }
                  onFocus={() => {
                    if (!title.trim()) setFocusedField('title');
                  }}
                  onBlur={() => setFocusedField(null)}
                />
                <datalist id="localops-title-history">
                  {titleHistory.map((item, index) => (
                    <option
                      value={item}
                      key={`localops-title-${index}`}
                    />
                  ))}
                </datalist>
                {focusedField === 'title' && !title.trim() && recentTitleChips.length ? (
                  <div className="localops-input-history">
                    <div className="localops-input-history__label">Recent</div>
                    <div className="localops-input-history__list">
                      {recentTitleChips.map((item, index) => (
                        <button
                          key={`localops-title-chip-${index}`}
                          type="button"
                          className="localops-input-history__chip"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            setTitle(item);
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="localops-field">
                <label>{isOffering ? 'Category (required)' : 'Category (optional)'}</label>
                <input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Select or type a category"
                  list="localops-category-list"
                  autoComplete="on"
                  name={
                    isOffering
                      ? 'localops-offering-category'
                      : 'localops-task-category'
                  }
                />
                <datalist id="localops-category-list">
                  {categoryOptions.map((item) => (
                    <option value={item} key={item} />
                  ))}
                </datalist>
              </div>
            </div>
              <div className="localops-field">
                <label>Description</label>
                {stepTwoGuidance?.chips?.length ? (
                  <div
                    className="localops-guidance-chips"
                    aria-label="Writing checklist"
                  >
                    {stepTwoGuidance.chips.map((chip) => (
                      <span className="localops-guidance-chip" key={chip}>
                        {chip}
                      </span>
                    ))}
                  </div>
                ) : null}
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setDescriptionAuto(false);
                  }}
                  placeholder={
                    isTask
                      ? 'Add clear instructions'
                      : 'Add a short description'
                  }
                  autoComplete="on"
                  aria-invalid={taskDescriptionOverLimit ? 'true' : undefined}
                  aria-describedby={
                    isTask ? 'localops-task-description-counter' : undefined
                  }
                  name={
                    isOffering
                      ? 'localops-offering-description'
                      : 'localops-task-description'
                  }
                  onFocus={() => setFocusedField('description')}
                  onBlur={() => setFocusedField(null)}
                />
                {isTask ? (
                  <div
                    id="localops-task-description-counter"
                    className={`localops-character-count ${
                      taskDescriptionOverLimit ? 'is-over-limit' : ''
                    }`}
                    role={taskDescriptionOverLimit ? 'alert' : undefined}
                  >
                    {taskDescriptionLength.toLocaleString('en-US')} /{' '}
                    {LOCALOPS_TASK_DESCRIPTION_MAX_LENGTH.toLocaleString(
                      'en-US',
                    )}{' '}
                    characters
                  </div>
                ) : null}
              </div>
            {isOffering ? (
              <div className="localops-helper">
                People nearby can't buy what they can't see.
              </div>
            ) : null}
            {isTask ? (
              <div className="localops-helper">
                Clear details help the right person step in.
              </div>
            ) : null}
            {descriptionAuto === false && title.trim() && description.trim() !== title.trim() ? (
              <div className="localops-input-history">
                <button
                  type="button"
                  className="localops-input-history__chip"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    setDescription(title);
                    setDescriptionAuto(true);
                  }}
                >
                  Use title as description
                </button>
              </div>
            ) : null}
            {showDescriptionHistory ? (
              <div className="localops-input-history">
                <div className="localops-input-history__label">Recent</div>
                <div className="localops-input-history__list">
                  {descriptionSuggestions.map((item, index) => (
                    <button
                      key={`localops-desc-${index}`}
                      type="button"
                      className="localops-input-history__chip"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setDescription(item);
                        setDescriptionAuto(false);
                      }}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          ) : null}
          {!isRideHelpFlow && !isGuidedTaskFlow && !isGuidedOfferingFlow ? (
          <div className="localops-section">
            <h4>Location</h4>
            {isOffering ? (
              <>
                {originLocationConfirmed ? (
                  <div className="localops-earn-row" style={{ marginTop: 6 }}>
                    <CheckCircleOutlineIcon
                      className="localops-earn-icon"
                      fontSize="small"
                    />
                    <span>Location confirmed.</span>
                  </div>
                ) : pickupAddressValue &&
                  originResolution.message !==
                    LOCALOPS_ORIGIN_CONFIRMATION_REQUIRED_MESSAGE ? (
                  <div
                    className="localops-helper"
                    style={{ color: '#f97316', marginTop: 6 }}
                  >
                    {LOCALOPS_ORIGIN_CONFIRMATION_REQUIRED_MESSAGE}
                  </div>
                ) : null}
              </>
            ) : null}
            {isTask || isOffering ? (
              <div className="localops-field">
                <label>
                  {isOffering
                    ? 'Offering address (private, required)'
                    : 'Pickup address (private, required)'}
                </label>
                <input
                  value={pickupAddress}
                  onChange={(e) => handlePickupAddressChange(e.target.value)}
                  placeholder="Street, city, state"
                  list="localops-pickup-history"
                  autoComplete="street-address"
                  name="localops-pickup-address"
                />
                <datalist id="localops-pickup-history">
                  {pickupHistory.map((item, index) => (
                    <option
                      value={item}
                      key={`localops-pickup-${index}`}
                    />
                  ))}
                </datalist>
                {originResolution.candidates.length ? (
                  <div
                    className="localops-origin-candidates"
                    data-testid="localops-origin-candidates"
                  >
                    {originResolution.candidates.map((candidate, index) => {
                      const primary = candidateDisplayText(candidate);
                      const secondary = candidateSecondaryText(candidate);
                      return (
                        <button
                          type="button"
                          className="localops-origin-candidate"
                          key={candidate.id || `${primary}-${index}`}
                          onClick={() => handleSelectOriginCandidate(candidate)}
                        >
                          <span className="localops-origin-candidate__main">
                            {primary}
                          </span>
                          {secondary ? (
                            <span className="localops-origin-candidate__secondary">
                              {secondary}
                            </span>
                          ) : null}
                          <span className="localops-origin-candidate__action">
                            Select to confirm
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {originResolution.message ? (
                  <div className="localops-helper localops-origin-resolution">
                    {originResolution.message}
                  </div>
                ) : null}
                <div className="localops-helper">
                  {isOffering
                    ? 'Where buyers pick up your offering. Shared only after a buyer reserves.'
                    : isCollisionSpecialistRidePreset
                    ? 'Pickup point for the ride. Shared only after a helper accepts.'
                    : isDelivery
                    ? 'This is where the item will be picked up. Shared only after a helper accepts.'
                    : 'This is the place for the task. Shared only after a helper accepts.'}
                </div>
                <div className="localops-helper">
                  {isPickupGeocoding
                    ? 'Finding local address matches...'
                    : originLocationConfirmed
                    ? 'Location confirmed.'
                    : 'Select a local match to confirm location.'}
                </div>
                {originPinOutsideLocalArea ? (
                  <div className="localops-error">
                    {LOCALOPS_PIN_OUTSIDE_LOCAL_AREA_MESSAGE}
                  </div>
                ) : originLocationConfirmed ? (
                  <div className="localops-earn-row">
                    <CheckCircleOutlineIcon
                      className="localops-earn-icon"
                      fontSize="small"
                    />
                    <span>{pinNotice || 'Location confirmed.'}</span>
                  </div>
                ) : pinNotice ? (
                  <div className="localops-earn-row">
                    <CheckCircleOutlineIcon
                      className="localops-earn-icon"
                      fontSize="small"
                    />
                    <span>{pinNotice}</span>
                  </div>
                ) : null}
                {!pickupCoordsValid && pinError ? (
                  <div className="localops-error">{pinError}</div>
                ) : null}
              </div>
            ) : null}
            {isTask && isDelivery ? (
              <div className="localops-field">
                <label>
                  {isCollisionSpecialistRidePreset
                    ? 'Destination address (private, required)'
                    : 'Delivery address (private)'}
                </label>
                <input
                  value={destinationAddress}
                  onChange={(e) => handleDestinationAddressChange(e.target.value)}
                  placeholder="Street, city, state"
                  list="localops-delivery-history"
                  autoComplete="street-address"
                  name="localops-delivery-address"
                />
                <datalist id="localops-delivery-history">
                  {deliveryHistory.map((item, index) => (
                    <option
                      value={item}
                      key={`localops-delivery-${index}`}
                    />
                  ))}
                </datalist>
                {destinationResolution.candidates.length ? (
                  <div
                    className="localops-origin-candidates"
                    data-testid="localops-destination-candidates"
                  >
                    {destinationResolution.candidates.map((candidate, index) => {
                      const primary = candidateDisplayText(candidate);
                      const secondary = candidateSecondaryText(candidate);
                      return (
                        <button
                          type="button"
                          className="localops-origin-candidate"
                          key={candidate.id || `${primary}-${index}`}
                          onClick={() => handleSelectDestinationCandidate(candidate)}
                        >
                          <span className="localops-origin-candidate__main">
                            {primary}
                          </span>
                          {secondary ? (
                            <span className="localops-origin-candidate__secondary">
                              {secondary}
                            </span>
                          ) : null}
                          <span className="localops-origin-candidate__action">
                            {isCollisionSpecialistRidePreset
                              ? 'Select destination'
                              : 'Select drop-off'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {destinationResolution.message ? (
                  <div className="localops-helper localops-origin-resolution">
                    {destinationResolution.message}
                  </div>
                ) : null}
                <div className="localops-helper">
                  {isCollisionSpecialistRidePreset
                    ? 'Where do you need to go? Type a place or address, then select a local match. Shared only after a helper accepts.'
                    : 'Where should the item be delivered? Type a place or address, then select a local match. Shared only after a helper accepts.'}
                </div>
                <div className="localops-helper">
                  {isDestinationGeocoding
                    ? isCollisionSpecialistRidePreset
                      ? 'Finding local destination matches...'
                      : 'Finding local drop-off matches...'
                    : assistanceDestinationConfirmed
                      ? destinationConfirmedNotice
                      : isCollisionSpecialistRidePreset
                      ? 'Select a local destination match to confirm.'
                      : 'Select a local drop-off match to confirm.'}
                </div>
                {assistanceDestinationConfirmed ? (
                  <div className="localops-earn-row">
                    <CheckCircleOutlineIcon
                      className="localops-earn-icon"
                      fontSize="small"
                    />
                    <span>{destinationPinNotice || destinationConfirmedNotice}</span>
                  </div>
                ) : destinationPinNotice ? (
                  <div className="localops-earn-row">
                    <CheckCircleOutlineIcon
                      className="localops-earn-icon"
                      fontSize="small"
                    />
                    <span>{destinationPinNotice}</span>
                  </div>
                ) : null}
                {!assistanceDestinationConfirmed && destinationPinError ? (
                  <div className="localops-error">{destinationPinError}</div>
                ) : null}
              </div>
            ) : null}
            {isAssistance ? (
              <div className="localops-field">
                <label>
                  {isRideHelpStarterPreset
                    ? 'Destination required for Ride Help'
                    : 'Destination address (private, required)'}
                </label>
                <input
                  value={destinationAddress}
                  onChange={(e) => handleDestinationAddressChange(e.target.value)}
                  placeholder="Street, city, state"
                  list="localops-destination-history"
                  autoComplete="street-address"
                  name="localops-destination-address"
                />
                <datalist id="localops-destination-history">
                  {destinationHistory.map((item, index) => (
                    <option
                      value={item}
                      key={`localops-destination-${index}`}
                    />
                  ))}
                </datalist>
                {isAssistance && destinationResolution.candidates.length ? (
                  <div
                    className="localops-origin-candidates"
                    data-testid="localops-destination-candidates"
                  >
                    {destinationResolution.candidates.map((candidate, index) => {
                      const primary = candidateDisplayText(candidate);
                      const secondary = candidateSecondaryText(candidate);
                      return (
                        <button
                          type="button"
                          className="localops-origin-candidate"
                          key={candidate.id || `${primary}-${index}`}
                          onClick={() => handleSelectDestinationCandidate(candidate)}
                        >
                          <span className="localops-origin-candidate__main">
                            {primary}
                          </span>
                          {secondary ? (
                            <span className="localops-origin-candidate__secondary">
                              {secondary}
                            </span>
                          ) : null}
                          <span className="localops-origin-candidate__action">
                            Select destination
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {isAssistance && destinationResolution.message ? (
                  <div className="localops-helper localops-origin-resolution">
                    {destinationResolution.message}
                  </div>
                ) : null}
                <div className="localops-helper">
                  {isRideHelpStarterPreset
                    ? 'Where should you be dropped off? Type a place name or address, then select a local match. Shared only after a helper accepts.'
                    : 'Where should you be dropped off? Type a place name or address, then select a local match. Shared only after a helper accepts.'}
                </div>
                <div className="localops-helper">
                  {isDestinationGeocoding
                    ? 'Finding local destination matches...'
                    : assistanceDestinationConfirmed
                      ? 'Destination confirmed.'
                      : 'Select a local destination match before posting.'}
                </div>
                {assistanceDestinationConfirmed ? (
                  <div className="localops-earn-row">
                    <CheckCircleOutlineIcon className="localops-earn-icon" />
                    <div className="localops-earn-text">
                      {destinationPinNotice || 'Destination confirmed.'}
                    </div>
                  </div>
                ) : destinationPinNotice ? (
                  <div className="localops-earn-row">
                    <CheckCircleOutlineIcon className="localops-earn-icon" />
                    <div className="localops-earn-text">{destinationPinNotice}</div>
                  </div>
                ) : null}
                {!assistanceDestinationConfirmed && destinationPinError ? (
                  <div className="localops-error">{destinationPinError}</div>
                ) : null}
              </div>
            ) : null}
          </div>
          ) : null}
          {canShowCommunityTaskChoice ? (
            <div className="localops-section">
              <h4>Task mode</h4>
              <div className="localops-helper">
                Community Tasks are unpaid verified contribution tasks from
                approved LocalOps partners.
              </div>
              <div
                className="localops-visibility-grid localops-choice-grid"
                role="radiogroup"
                aria-label="Task payment mode"
              >
                <label
                  className={`localops-visibility-card ${
                    !communityTaskSelected ? 'is-active' : ''
                  }`}
                >
                  <input
                    className="localops-card-radio-input"
                    type="radio"
                    name="taskPaymentMode"
                    checked={!communityTaskSelected}
                    onChange={() =>
                      setTaskPaymentMode(LOCALOPS_TASK_PAYMENT_MODES.PAID)
                    }
                  />
                  <div className="localops-visibility-title">Paid Task</div>
                  <div className="localops-visibility-desc">
                    Fund with JGK. Helper payout is released after completion is
                    verified.
                  </div>
                </label>
                <label
                  className={`localops-visibility-card ${
                    communityTaskSelected ? 'is-active' : ''
                  } ${!canUseCommunityTaskMode ? 'is-disabled' : ''}`}
                >
                  <input
                    className="localops-card-radio-input"
                    type="radio"
                    name="taskPaymentMode"
                    checked={communityTaskSelected}
                    disabled={!canUseCommunityTaskMode}
                    onChange={() =>
                      setTaskPaymentMode(LOCALOPS_TASK_PAYMENT_MODES.COMMUNITY)
                    }
                  />
                  <div className="localops-visibility-title">
                    Community Task
                  </div>
                  <div className="localops-visibility-desc">
                    No cash payout. Verified completion strengthens Action
                    Credit.
                  </div>
                </label>
              </div>
              {!communityTaskTypeEligible ? (
                <div className="localops-helper">
                  Driving, deliveries, passenger rides, shopping, and supply
                  runs should be posted as paid LocalOps tasks.
                </div>
              ) : null}
            </div>
          ) : null}
          {!isRideHelpFlow && !communityTaskSelected && !isGuidedOfferingFlow ? (
          <div className="localops-section">
            <h4>{isGuidedTaskFlow ? 'Amount' : 'Price'}</h4>
            <div className="localops-row">
              <div className="localops-field">
                <label>
                  {isOffering
                    ? 'Unit price ($)'
                    : isCollisionSpecialistRidePreset ||
                      createFlowBlueprint.priceMode === 'route_suggested'
                    ? 'Suggested amount ($)'
                    : isGuidedTaskFlow
                    ? 'Task amount ($)'
                    : 'Amount ($)'}
                </label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => handlePriceInputChange(e.target.value)}
                  disabled={isOffering ? requestQuote : false}
                  min={isTask ? LOCALOPS_MIN_TASK_PRICE_USD : 0}
                  step="0.01"
                  aria-invalid={
                    isTask && price !== '' && !taskPriceIsValid
                      ? 'true'
                      : undefined
                  }
                  list="localops-price-history"
                  autoComplete="on"
                  name={
                    isOffering
                      ? 'localops-offering-price'
                      : 'localops-task-price'
                  }
                  onWheel={(e) => {
                    // Prevent mouse wheel from accidentally changing amount
                    e.target.blur();
                    e.preventDefault();
                  }}
                />
                <datalist id="localops-price-history">
                  {priceHistory.map((item, index) => (
                    <option
                      value={item}
                      key={`localops-price-${index}`}
                    />
                  ))}
                </datalist>
                {isTask &&
                !communityTaskSelected &&
                !isRideHelpStarterPreset &&
                !isCollisionSpecialistRidePreset &&
                createFlowBlueprint.priceMode !== 'route_suggested' ? (
                  <div className="localops-price-chips">
                    <div className="localops-helper">
                      Suggested starting amounts. You can adjust before posting.
                    </div>
                    <div className="localops-price-chip-row">
                      {TASK_PRICE_CHIPS.map((amount) => (
                        <button
                          type="button"
                          className="localops-price-chip"
                          key={amount}
                          onClick={() => handlePriceInputChange(amount)}
                        >
                          ${amount}
                        </button>
                      ))}
                      <span className="localops-price-chip localops-price-chip--muted">
                        Custom
                      </span>
                    </div>
                  </div>
                ) : null}
                {isCollisionSpecialistRidePreset ? (
                  <div className="localops-helper">
                    {rideSuggestedPrice && rideSuggestedDistanceLabel
                      ? `Suggested price based on estimated distance. You can adjust before posting. Estimated distance: ${rideSuggestedDistanceLabel}.`
                      : isRidePriceGeocoding
                      ? 'Estimating suggested price from the destination...'
                      : 'Suggested price based on estimated distance. You can adjust before posting.'}
                  </div>
                ) : null}
                {isRideHelpStarterPreset ? (
                  <div className="localops-helper">
                    {Number.isFinite(rideSuggestedPrice) && rideSuggestedDistanceLabel
                      ? `Suggested price based on estimated distance. You can adjust before posting. Estimated distance: ${rideSuggestedDistanceLabel}.`
                      : isRidePriceGeocoding
                      ? 'Estimating suggested price from pickup and destination...'
                      : 'Add a confirmed pickup and destination to estimate a suggested price.'}
                  </div>
                ) : null}
                {isGuidedRouteSuggestedFlow ? (
                  <div className="localops-helper">
                    {Number.isFinite(rideSuggestedPrice) && rideSuggestedDistanceLabel
                      ? `Suggested amount: ${rideReviewAmountLabel}. Based on about ${rideSuggestedDistanceLabel} at $2/mile. You can adjust before posting.`
                      : isRidePriceGeocoding
                      ? 'Estimating suggested amount from pickup and drop-off...'
                      : 'Add a confirmed pickup and drop-off to estimate a suggested amount.'}
                  </div>
                ) : null}
                {isTask && !isCollisionSpecialistRidePreset && !isRideHelpStarterPreset ? (
                  <div className="localops-helper">
                    {isGuidedTaskFlow
                      ? createFlowBlueprint.amountHelper
                      : 'Many simple starter tasks are posted around $5-$20 depending on distance, time, and effort. Offer more for driving, heavy work, tools, waiting, urgent timing, or longer distance.'}
                  </div>
                ) : null}
              </div>
            </div>
            {isOffering ? (
              <>
                <div className="localops-row">
                  <div className="localops-field">
                    <label>Quantity available</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={quantityAvailable}
                      onChange={(e) => setQuantityAvailable(e.target.value)}
                      name="localops-offering-quantity"
                    />
                  </div>
                </div>
                <div className="localops-helper">
                  Set how many units you can fulfill right now.
                </div>
              </>
            ) : null}
            {isTask ? (
              hasPaidTaskAmount ? (
                <LocalOpsCompletionFlowHint variant="paidRequester" compact />
              ) : (
                <div className="localops-helper">
                  {`Enter ${LOCALOPS_MIN_TASK_PRICE_LABEL} or more to fund this task before continuing.`}
                </div>
              )
            ) : null}
          </div>
          ) : !isRideHelpFlow && !isGuidedOfferingFlow ? (
            <div className="localops-section">
              <LocalOpsCompletionFlowHint variant="communityTask" compact />
              <div className="localops-helper">
                Use this for volunteer-style help only. Paid errands, shopping,
                driving, deliveries, and supply runs should stay paid LocalOps
                tasks.
              </div>
            </div>
          ) : null}
          {isGuidedTaskFlow ? (
            <div className="localops-section">
              <h4>
                {communityTaskSelected
                  ? 'Volunteer instructions'
                  : createFlowBlueprint.notesHeading}
              </h4>
              <div className="localops-field">
                <label>
                  {communityTaskSelected
                    ? 'Instructions for the helper'
                    : createFlowBlueprint.notesLabel}
                </label>
                {stepTwoGuidance?.chips?.length ? (
                  <div
                    className="localops-guidance-chips"
                    aria-label="Writing checklist"
                  >
                    {stepTwoGuidance.chips.map((chip) => (
                      <span className="localops-guidance-chip" key={chip}>
                        {chip}
                      </span>
                    ))}
                  </div>
                ) : null}
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setDescriptionAuto(false);
                  }}
                  placeholder={
                    communityTaskSelected
                      ? 'Add clear volunteer instructions'
                      : createFlowBlueprint.notesPlaceholder
                  }
                  autoComplete="on"
                  aria-invalid={taskDescriptionOverLimit ? 'true' : undefined}
                  aria-describedby="localops-task-description-counter"
                  name="localops-task-description"
                  onFocus={() => setFocusedField('description')}
                  onBlur={() => setFocusedField(null)}
                />
                <div
                  id="localops-task-description-counter"
                  className={`localops-character-count ${
                    taskDescriptionOverLimit ? 'is-over-limit' : ''
                  }`}
                  role={taskDescriptionOverLimit ? 'alert' : undefined}
                >
                  {taskDescriptionLength.toLocaleString('en-US')} /{' '}
                  {LOCALOPS_TASK_DESCRIPTION_MAX_LENGTH.toLocaleString(
                    'en-US',
                  )}{' '}
                  characters
                </div>
              </div>
              {showDescriptionHistory ? (
                <div className="localops-input-history">
                  <div className="localops-input-history__label">Recent</div>
                  <div className="localops-input-history__list">
                    {descriptionSuggestions.map((item, index) => (
                      <button
                        key={`localops-desc-${index}`}
                        type="button"
                        className="localops-input-history__chip"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          setDescription(item);
                          setDescriptionAuto(false);
                        }}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          {isOffering && !isGuidedOfferingFlow ? (
            <div className="localops-section">
              <h4>Delivery</h4>
              <label className="localops-pill-toggle">
                <input
                  type="checkbox"
                  checked={deliverySupported}
                  onChange={(e) => setDeliverySupported(e.target.checked)}
                />
                Delivery supported
              </label>
              <div className="localops-helper">
                Enable if you are willing to have this delivered by a local
                helper.
              </div>
            </div>
          ) : null}
          <div
            className="localops-step2-action"
            data-testid="localops-step2-action"
          >
            {canAdvanceFromDetails ? (
              <>
                <div>
                  <div className="localops-step2-action__title">
                    Ready for final review
                  </div>
                  <div className="localops-helper">
                    Check trust, visibility, and funding on the next step.
                  </div>
                </div>
                <button
                  type="button"
                  className="localops-btn"
                  onClick={handleAdvanceFromDetails}
                >
                  {detailsReviewLabel}
                </button>
              </>
            ) : (
              <>
                <div>
                  <div className="localops-step2-action__title">
                    Finish required details
                  </div>
                  <div className="localops-helper" aria-live="polite">
                    {stepTwoBlockerCopy}
                  </div>
                </div>
                <button type="button" className="localops-btn" disabled>
                  {isRideHelpFlow ? 'Review Ride Help task' : 'Next: Review'}
                </button>
              </>
            )}
          </div>
          {!isRideHelpFlow && !isGuidedTaskFlow && !isGuidedOfferingFlow ? (
            <LocalOpsMediaPicker
              mediaItems={mediaItems}
              coverIndex={coverIndex}
              onCoverChange={setCoverIndex}
              onRemove={removeMedia}
              onInvalidate={invalidateMedia}
              onAddFiles={addFiles}
              error={error}
              uploadPct={uploadPct}
              warning={mediaWarning}
            />
          ) : null}
          <div className="localops-section">
            <h4>
              {isRideHelpFlow
                ? 'Public preview (exact addresses hidden)'
                : isGuidedTaskFlow
                ? createFlowBlueprint.previewHeading
                : isGuidedOfferingFlow
                ? createFlowBlueprint.previewHeading
                : 'Public preview (what others will see)'}
            </h4>
            {isRideHelpFlow ? (
              <div className="localops-helper">
                Exact pickup and destination are shared only after a helper
                accepts and starts.
              </div>
            ) : isGuidedTaskFlow ? (
              <div className="localops-helper">
                {createFlowBlueprint.previewHelper}
              </div>
            ) : isGuidedOfferingFlow ? (
              <div className="localops-helper">
                {createFlowBlueprint.previewHelper}
              </div>
            ) : null}
            <div
              className="localops-preview-card"
              data-testid="localops-public-preview"
            >
              <div className="localops-preview-title">{previewData.title}</div>
              {previewData.description ? (
                <div className="localops-preview-desc">
                  {previewData.description}
                </div>
              ) : null}
              <div className="localops-preview-meta">
                <span className="localops-preview-badge">
                  {previewData.category}
                </span>
                <span className="localops-preview-price">
                  {previewData.priceLabel}
                </span>
                {previewData.quantityLabel ? (
                  <span className="localops-preview-badge">
                    {previewData.quantityLabel}
                  </span>
                ) : null}
                <span>{previewData.location}</span>
                {previewData.endMode ? (
                  <span className="localops-preview-badge">
                    {previewData.endMode}
                  </span>
                ) : null}
              </div>
              <div className="localops-preview-media">
                {previewData.media.length
                  ? previewData.media.map((item, idx) => (
                      <div className="localops-preview-thumb" key={idx}>
                        {item.placeholder ? (
                          <div className="localops-preview-thumb__placeholder">
                            {item.label || 'Draft preview expired.'}
                          </div>
                        ) : item.isVideo ? (
                          <video
                            src={item.preview}
                            muted
                            playsInline
                            preload="metadata"
                            onError={() => invalidateMedia(idx)}
                          />
                        ) : (
                          <img
                            src={item.preview}
                            alt=""
                            onError={() => invalidateMedia(idx)}
                          />
                        )}
                        {item.preview?.startsWith('blob:') ? (
                          <div className="localops-preview-thumb__badge">
                            Draft preview
                          </div>
                        ) : null}
                      </div>
                    ))
                  : previewData.showMediaPlaceholders
                  ? Array.from({ length: 3 }).map((_, idx) => (
                      <div
                        key={`placeholder-${idx}`}
                        className="localops-preview-thumb localops-preview-placeholder"
                      >
                        Add media
                      </div>
                    ))
                  : null}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="localops-adv-toggle"
            onClick={() => setShowAdvanced((prev) => !prev)}
          >
            {showAdvanced ? 'Hide advanced options' : 'Advanced options'}
          </button>
          {showAdvanced ? (
            <>
              {isRideHelpFlow ||
              (isGuidedTaskFlow &&
                createFlowBlueprint.mediaMode === 'advanced') ? (
                <LocalOpsMediaPicker
                  mediaItems={mediaItems}
                  coverIndex={coverIndex}
                  onCoverChange={setCoverIndex}
                  onRemove={removeMedia}
                  onInvalidate={invalidateMedia}
                  onAddFiles={addFiles}
                  error={error}
                  uploadPct={uploadPct}
                  warning={mediaWarning}
                  helperText={
                    isRideHelpFlow
                      ? 'Optional. Most Ride Help requests do not need photos or videos.'
                      : createFlowBlueprint.mediaHelper
                  }
                />
              ) : null}
              {!isRideHelpFlow && !isGuidedTaskFlow && !isGuidedOfferingFlow ? (
              <div className="localops-section">
                <h4>Availability</h4>
                <div className="localops-row">
                  <div className="localops-field">
                    <label>Start</label>
                    <input
                      type="datetime-local"
                      value={availabilityStart}
                      onChange={(e) => {
                        setAvailabilityStart(e.target.value);
                        setCustomStart(!!e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="localops-row">
                  <div className="localops-field">
                    <label>Ends</label>
                    <select
                      value={availabilityEndMode}
                      onChange={(e) => setAvailabilityEndMode(e.target.value)}
                    >
                      {END_MODE_OPTIONS.map((option) => (
                        <option value={option.id} key={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {availabilityEndMode === 'specific' ? (
                    <div className="localops-field">
                      <label>End</label>
                      <input
                        type="datetime-local"
                        value={availabilityEnd}
                        onChange={(e) => setAvailabilityEnd(e.target.value)}
                      />
                    </div>
                  ) : null}
                </div>
                {availabilityEndMode !== 'specific' ? (
                  <div className="localops-helper">
                    {labelForEndMode(availabilityEndMode)} keeps this live while
                    you plan. Update any time.
                  </div>
                ) : null}
              </div>
              ) : null}
              <div className="localops-section">
                <h4>Advanced coordinates</h4>
                <div className="localops-row localops-adv-fields">
                  <div className="localops-field">
                    <label>Latitude</label>
                    <input
                      type="number"
                      value={location?.lat ?? ''}
                      onChange={(e) =>
                        setLocation((prev) => {
                          setPinSource('manual');
                          originCandidateRequestRef.current += 1;
                          setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
                          setPinNotice('');
                          setPinError('');
                          return {
                            ...(prev || {}),
                            lat: Number(e.target.value),
                          };
                        })
                      }
                    />
                  </div>
                  <div className="localops-field">
                    <label>Longitude</label>
                    <input
                      type="number"
                      value={location?.lon ?? ''}
                      onChange={(e) =>
                        setLocation((prev) => {
                          setPinSource('manual');
                          originCandidateRequestRef.current += 1;
                          setOriginResolution(EMPTY_ORIGIN_RESOLUTION);
                          setPinNotice('');
                          setPinError('');
                          return {
                            ...(prev || {}),
                            lon: Number(e.target.value),
                          };
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {step === 2 && selectedMeta ? (
        <div className="localops-section">
          {isRideHelpFlow ? (
            <>
              <div
                className="localops-ride-review"
                data-testid="ride-help-review-summary"
              >
                <div className="localops-ride-review__title">
                  Review Ride Help task
                </div>
                <div className="localops-ride-review__row">
                  <span>Pickup</span>
                  <strong>{pickupAddressValue || 'Not set yet'}</strong>
                </div>
                <div className="localops-ride-review__row">
                  <span>Destination</span>
                  <strong>{destinationAddressValue || 'Not set yet'}</strong>
                </div>
                <div className="localops-ride-review__row">
                  <span>Time</span>
                  <strong>{rideHelpTimeLabel}</strong>
                </div>
                <div className="localops-ride-review__row">
                  <span>Amount reserved</span>
                  <strong>{rideReviewAmountLabel}</strong>
                </div>
              </div>
              {hasPaidTaskAmount ? (
                <div className="localops-section">
                  <h4>Publish this Ride Help task</h4>
                  <div className="localops-helper">
                    Nearby Ride Ready helpers can accept this task.
                  </div>
                  <div className="localops-helper">
                    You approve completion before the helper reward is released.
                  </div>
                  <div className="localops-helper">
                    Live tracking is required for Ride Help.
                  </div>
                  <div
                    className="localops-helper"
                    data-testid="localops-supply-proof"
                  >
                    {supplyProofLoading && !supplyProof
                      ? 'Checking nearby helper supply...'
                      : displayedSupplyProof.eligibleNearbyCopy ||
                        GENERIC_SUPPLY_PROOF.eligibleNearbyCopy}
                  </div>
                  <LocalOpsCompletionFlowHint variant="paidRequester" />
                  <LocalOpsPayWithBalance
                    required={escrowRequiredJGK}
                    balance={playerBalance}
                    onTopUp={onRequestTopUp}
                    title="Publish this Ride Help task"
                    label="Ride amount"
                    balanceLabel="Available task credit"
                    shortfallMode
                    shortfallRowLabel="Amount needed to publish"
                    readyStatusLabel="Ready to publish with your task credit"
                    shortfallStatusLabel="Add task credit to publish"
                    disclosure="JGK is JG LocalOps platform credit. $1 = 1,000 JGK. Any unused credit stays in your balance."
                    showPostBalanceHelper={false}
                    showTopUpButton={false}
                    amountDisplay="usd_first"
                  />
                </div>
              ) : null}
            </>
          ) : null}
          <h4>{isRideHelpFlow ? 'Advanced visibility' : 'Safety & visibility'}</h4>
          <div className="localops-helper">
            {isRideHelpFlow
              ? 'Nearby is the default so available Ride Ready helpers can see the request.'
              : isOffering
              ? 'Set offering discovery and reservation visibility.'
              : 'Set task discovery and accept eligibility.'}
          </div>
          <div className="localops-choice-label">
            {isOffering ? 'Who can see this offering?' : 'Who can see this task?'}
          </div>
          <div className="localops-visibility-grid localops-choice-grid">
            <button
              type="button"
              className={`localops-visibility-card ${
                visibility === 'nearby' ? 'is-active' : ''
              }`}
              onClick={() => {
                visibilityTouchedRef.current = true;
                setVisibility('nearby');
              }}
            >
              <div className="localops-visibility-title">Nearby</div>
              <div className="localops-visibility-desc">
                {isRideHelpFlow
                  ? 'Nearby Ride Ready helpers can see and accept when eligible.'
                  : 'Visible to LocalOps players nearby.'}
              </div>
            </button>
            <button
              type="button"
              className={`localops-visibility-card ${
                visibility === 'trusted' ? 'is-active' : ''
              } ${
                isRideHelpFlow && !friendOptions.length ? 'is-disabled' : ''
              }`}
              disabled={isRideHelpFlow && !friendOptions.length}
              onClick={() => {
                if (isRideHelpFlow && !friendOptions.length) return;
                visibilityTouchedRef.current = true;
                setVisibility('trusted');
              }}
            >
              <div className="localops-visibility-title">Trusted</div>
              <div className="localops-visibility-desc">
                {isRideHelpFlow && !friendOptions.length
                  ? 'Trusted is available after you add trusted players.'
                  : 'Only people you choose can see this.'}
              </div>
            </button>
          </div>
          {isOffering ? (
            <div className="localops-helper">
              <div>
                Publishing makes this offering visible nearby. Buyers can reserve it,
                then you coordinate fulfillment.
              </div>
              <div>
                Buyers reserve first. Funds are held until fulfillment is confirmed.
              </div>
              {deliverySupported ? (
                <div>
                  If delivery is selected, the buyer can create a delivery task for a
                  local helper.
                </div>
              ) : null}
            </div>
          ) : null}
          {visibility === 'trusted' ? (
            <div className="localops-section">
              <div className="localops-trusted-header">
                <span>Trusted players</span>
                <span className="localops-helper">
                  Select who can see this.
                </span>
              </div>
              {trustedPlayers.length ? (
                <div className="localops-chip-row">
                  {trustedPlayers.map((id) => {
                    const friend = friendOptions.find((f) => f.id === id);
                    const label =
                      friend?.playerId || friend?.label || 'JG Player';
                    return (
                      <button
                        key={id}
                        type="button"
                        className="localops-chip"
                        onClick={() => toggleTrusted(id)}
                      >
                        {label}
                        <span className="localops-chip__remove">x</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <label className="localops-search">
                <SearchOutlinedIcon fontSize="small" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search playerId or name"
                />
              </label>
              <div className="localops-trusted-list">
                {filteredFriends.map((friend) => {
                  const isSelected = trustedPlayers.includes(friend.id);
                  const label =
                    friend.displayName ||
                    friend.playerId ||
                    friend.label ||
                    'JG Player';
                  const showHandle =
                    friend.playerId &&
                    friend.displayName &&
                    friend.displayName !== friend.playerId;
                  return (
                    <button
                      key={friend.id}
                      type="button"
                      className={`localops-trusted-row ${
                        isSelected ? 'is-selected' : ''
                      }`}
                      onClick={() => toggleTrusted(friend.id)}
                    >
                      <span className="localops-avatar">
                        {friend.photo ? (
                          <img src={friend.photo} alt="" />
                        ) : (
                          <span className="localops-avatar__fallback">
                            {String(friend.label || friend.id || '?')
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        )}
                        {friend.presence ? (
                          <span
                            className={`localops-presence-dot ${
                              friend.presence === 'busy'
                                ? 'is-busy'
                                : friend.presence === 'offline'
                                ? 'is-offline'
                                : ''
                            }`}
                          />
                        ) : null}
                      </span>
                      <span className="localops-trusted-meta">
                        <span className="localops-trusted-label">{label}</span>
                        {showHandle ? (
                          <span className="localops-trusted-sub">
                            @{friend.playerId}
                          </span>
                        ) : null}
                      </span>
                      <span
                        className={`localops-toggle-switch ${
                          isSelected ? 'is-on' : ''
                        }`}
                      >
                        <span className="localops-toggle-switch__thumb" />
                      </span>
                    </button>
                  );
                })}
                {!filteredFriends.length ? (
                  <div className="localops-helper">
                    No matching players found.
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          {canShowRequireVerifiedLocal ? (
            <div className="localops-section">
              <div className="localops-choice-label">
                Who can accept this task?
              </div>
              <div
                className="localops-visibility-grid localops-choice-grid"
                role="radiogroup"
                aria-label="Acceptance requirement"
              >
                <label
                  className={`localops-visibility-card ${
                    !requireVerifiedLocal ? 'is-active' : ''
                  }`}
                >
                  <input
                    className="localops-card-radio-input"
                    type="radio"
                    name="taskAcceptanceRequirement"
                    checked={!requireVerifiedLocal}
                    onChange={() => setRequireVerifiedLocal(false)}
                  />
                  <div className="localops-visibility-title">
                    Anyone Nearby
                  </div>
                  <div className="localops-visibility-desc">
                    Recommended for faster help from available LocalOps helpers.
                  </div>
                </label>
                <label
                  className={`localops-visibility-card ${
                    requireVerifiedLocal ? 'is-active' : ''
                  }`}
                >
                  <input
                    className="localops-card-radio-input"
                    type="radio"
                    name="taskAcceptanceRequirement"
                    checked={requireVerifiedLocal}
                    onChange={() => setRequireVerifiedLocal(true)}
                  />
                  <div className="localops-visibility-title">
                    Verified Local only
                  </div>
                  <div className="localops-visibility-desc">
                    Only helpers with Action Credit 1+ can accept. This may
                    reduce the helper pool.
                  </div>
                </label>
              </div>
              <div className="localops-helper">
                Action Credit is verified proof from completed local work.
              </div>
            </div>
          ) : null}
          {canShowFirstCompletionAccess ? (
            <div className="localops-section">
              <div className="localops-choice-label">
                First Completion Access
              </div>
              <label
                className={`localops-visibility-card ${
                  firstCompletionAccessRequested ? 'is-active' : ''
                } ${!firstCompletionAccessSupported ? 'is-disabled' : ''}`}
              >
                <input
                  className="localops-card-radio-input"
                  type="checkbox"
                  checked={
                    firstCompletionAccessSupported &&
                    firstCompletionAccessRequested
                  }
                  disabled={!firstCompletionAccessSupported}
                  onChange={(event) =>
                    setFirstCompletionAccessRequested(event.target.checked)
                  }
                />
                <div className="localops-visibility-title">
                  First chance for new helpers
                </div>
                <div className="localops-visibility-desc">
                  Give zero-completion helpers the first 4 minutes before the
                  task opens to the full nearby helper pool.
                </div>
              </label>
              <div className="localops-helper">
                First Completion Access is available for open nearby paid tasks.
              </div>
            </div>
          ) : null}
          {communityTaskSelected ? (
            <div className="localops-section">
              <h4>Community verification</h4>
              <LocalOpsCompletionFlowHint variant="communityTask" />
            </div>
          ) : null}
          {hasPaidTaskAmount && !isRideHelpFlow ? (
            <div className="localops-section">
              {isCollisionSpecialistRidePreset ? (
                <div
                  className="localops-ride-review"
                  data-testid="collision-specialist-ride-review"
                >
                  <div className="localops-ride-review__title">Ride request</div>
                  <div className="localops-ride-review__row">
                    <span>Pickup</span>
                    <strong>
                      Collision Specialist
                      <br />
                      134 West Main Street, Camden, TN 38320
                    </strong>
                  </div>
                  <div className="localops-ride-review__row">
                    <span>Destination</span>
                    <strong>{deliveryAddressValue || 'Not set yet'}</strong>
                  </div>
                  <div className="localops-ride-review__row">
                    <span>Suggested amount</span>
                    <strong>{rideReviewAmountLabel}</strong>
                  </div>
                </div>
              ) : null}
              <h4>{isCollisionSpecialistRidePreset ? 'Ride funding' : 'Task funding'}</h4>
              <div className="localops-helper">
                {isCollisionSpecialistRidePreset
                  ? `Reserve ${escrowUsdFirstLabel} to request ride help.`
                  : `Reserve ${escrowUsdFirstLabel} to publish this task.`}
              </div>
              <div className="localops-helper" data-testid="localops-supply-proof">
                {supplyProofLoading && !supplyProof
                  ? 'Checking nearby helper supply...'
                  : displayedSupplyProof.eligibleNearbyCopy ||
                    GENERIC_SUPPLY_PROOF.eligibleNearbyCopy}
              </div>
              <div
                className="localops-helper"
                data-testid="localops-supply-priority-proof"
              >
                {displayedSupplyProof.verifiedLocalPriorityCopy ||
                  GENERIC_SUPPLY_PROOF.verifiedLocalPriorityCopy}
              </div>
              <LocalOpsCompletionFlowHint variant="paidRequester" />
              <div className="localops-helper">
                If no helper accepts, your task stays under your control.
              </div>
              {isRideHelpStarterPreset ? (
                <div className="localops-helper">
                  Ride Ready helpers can accept. Live tracking is required. You approve
                  completion only when satisfied.
                </div>
              ) : null}
              <div className="localops-helper">
                Platform fee: {feeLabel} (only after completion).
              </div>
              <div className="localops-helper">
                We take a small platform fee to keep LocalOps running.
              </div>
              <LocalOpsPayWithBalance
                required={escrowRequiredJGK}
                balance={playerBalance}
                onTopUp={onRequestTopUp}
                label="Amount reserved"
                showTopUpButton={false}
                usdApproximate={!isCollisionSpecialistRidePreset}
                amountDisplay="usd_first"
              />
            </div>
          ) : null}
          {isTask ? (
            <div className="localops-section">
              <label className="localops-pill-toggle">
                <input
                  type="checkbox"
                  checked={isAssistance ? true : liveTrackingEnabled}
                  disabled={isAssistance}
                  onChange={(e) => setLiveTrackingEnabled(e.target.checked)}
                />
                Live tracking {isAssistance ? '(required)' : ''}
              </label>
              <div className="localops-helper">
                Live tracking helps everyone feel safe.
              </div>
            </div>
          ) : null}
          {!presenceReady && isTask ? (
            <div className="localops-error">
              Turn on Live Location to publish. This helps nearby helpers find
              you and keeps it safe.
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? <div className="localops-error">{error}</div> : null}
      {!canPublish && publishBlockers.length ? (
        <div className="localops-helper localops-helper--warn">
          {publishBlockers.map((item) => (
            <div key={item}>• {item}</div>
          ))}
        </div>
      ) : null}
      {pendingIntent && pendingIntent.state === 'pending' && hasSufficientBalance ? (
        <div className="localops-section">
          <div className="localops-helper">
            We couldn't finish publishing. Tap retry to continue.
          </div>
          <button
            type="button"
            className="localops-btn"
            onClick={onRetryPendingIntent}
          >
            Retry publish
          </button>
        </div>
      ) : null}
      {step === 2 && hasPaidTaskAmount && !hasSufficientBalance ? (
        <div className="localops-helper">
          {isRideHelpFlow ? (
            <div>
              Add task credit securely with Stripe. LocalOps will return and
              continue publishing automatically.
            </div>
          ) : (
            <div>
              Top up securely with Stripe. After your JGK Balance is added, LocalOps
              will return and continue publishing automatically.
            </div>
          )}
          <div>
            If publishing cannot be confirmed, you can continue publishing without
            starting over.
          </div>
        </div>
      ) : null}

      <div className="localops-actions">
        {step > 0 ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={handleStepBack}
          >
            Back
          </button>
        ) : null}
        {step > 0 ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={handleResetSheet}
          >
            Reset
          </button>
        ) : null}
        {step < 2 && selectedMeta ? (
          <button
            type="button"
            className="localops-btn"
            disabled={!canAdvanceFromDetails}
            onClick={handleAdvanceFromDetails}
          >
            {isRideHelpFlow ? detailsReviewLabel : 'Next'}
          </button>
        ) : null}
        {step === 2 ? (
          !hasSufficientBalance ? (
            <button
              type="button"
              className="localops-btn"
              disabled={!canPublish || isSubmitting}
              onClick={handlePublish}
            >
              Top Up & Publish
            </button>
          ) : (
          <button
            type="button"
            className="localops-btn"
            disabled={!canPublish || isSubmitting}
            onClick={handlePublish}
          >
            {publishLabel}
          </button>
          )
        ) : null}
      </div>
    </LocalOpsSheet>
  );
}
